// ZB FIT BUILD 2026-06-09: P0 variant-resolution fix; Standard tab always offers Short/Regular/Long (made-to-order, requested length recorded on cart line); prominent "Find my size" estimator entry; fit-tool analytics (zbFitTrack); suits/full-tuxedos now capture trouser WAIST directly (selectable, defaults to drop-6) instead of forcing drop-6.
// ============================================
// ZANE BARLAS FIT ENGINE V2 - PART 1 OF 5
// Imports, Utilities, and Core Functions
// ============================================

import React, { useEffect, useMemo, useState } from "https://esm.sh/react@^19.2.3";
import { createRoot } from "https://esm.sh/react-dom@^19.2.3/client";
import htm from "https://esm.sh/htm";
const html = htm.bind(React.createElement);

(function bootstrapZBOpener() {
  window.__zbPendingOpen = window.__zbPendingOpen || false;
  window.openZaneSizeDrawer = function () {
    if (typeof window.__setZaneSizeDrawerOpen === "function") {
      window.__setZaneSizeDrawerOpen(true);
      return;
    }
    window.__zbPendingOpen = true;
    document.dispatchEvent(new CustomEvent("zane:open-size-drawer"));
  };
})();

// ============================================
// UTILITY FUNCTIONS
// ============================================
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const round1 = (n) => Math.round(n * 10) / 10;
const roundTo = (n, step) => Math.round(Number(n) / step) * step;

function safeJsonParse(text) { try { return JSON.parse(text); } catch { return null; } }
function getProductFromDom() { const el = document.getElementById("zb-product-json"); if (!el) return null; return safeJsonParse(el.textContent || ""); }
function normalizeValue(v) { return String(v ?? "").trim().toLowerCase().replace(/\s+/g, " "); }

// Lightweight analytics: dispatches a DOM event and pushes to dataLayer so fit-tool
// usage (open, recommend, add-to-cart) can be measured against the return rate.
function zbFitTrack(name, detail) {
  try {
    const payload = Object.assign({ event: "zb_fit_" + name }, detail || {});
    document.dispatchEvent(new CustomEvent("zb:fit-track", { detail: payload }));
    (window.dataLayer = window.dataLayer || []).push(payload);
    // GA4: the site loads gtag.js directly (no GTM container), so dataLayer pushes
    // alone never reach GA4. Mirror each event as zb_fit_* via gtag.
    if (typeof window.gtag === "function") window.gtag("event", "zb_fit_" + name, detail || {});
  } catch (e) {}
}

function optionNameLooksLikeSize(name) { 
  const n = normalizeValue(name); 
  const exactMatches = ["size", "jacket size", "coat size", "chest size", "collar size", "collar"]; 
  if (exactMatches.includes(n)) return true; 
  return n.includes("size") || n.includes("jacket") || n.includes("coat") || n.includes("chest") || n.includes("length") || n.includes("fit") || n.includes("collar"); 
}

function getSelectedVariantIdFromDom() { 
  const url = new URL(window.location.href); 
  const v = url.searchParams.get("variant"); 
  if (v && /^\d+$/.test(v)) return Number(v); 
  const input = document.querySelector('form[action^="/cart/add"] [name="id"]'); 
  if (input && input.value && /^\d+$/.test(input.value)) return Number(input.value); 
  const sel = document.querySelector('select[name="id"]'); 
  if (sel && sel.value && /^\d+$/.test(sel.value)) return Number(sel.value); 
  return null; 
}

function zbUserFacingErrorMessage(code) {
  if (code === "VARIANT_MATCH_FAIL") return "We couldn't match that selection to an in-stock option. Please try a different size or length.";
  if (code === "CART_ADD_FAIL") return "We couldn't add this to your cart. Please try again.";
  return "Something went wrong. Please try again.";
}

// ============================================
// PRODUCT CATEGORIZATION
// ============================================
function getProductCategory(product) { 
  if (!product) return 'sb_suit'; 
  const type = normalizeValue(product.product_type || ""); 
  const title = normalizeValue(product.title || ""); 
  const hay = [type, title, ...(product.tags || []).map(normalizeValue)].join(" "); 
  const isDouble = hay.includes("double breast") || hay.includes("double-breast") || hay.includes("db ");
  if (hay.includes("shirt")) return "shirt";
  if (hay.includes("overcoat")) return isDouble ? "db_overcoat" : "sb_overcoat";
  if (hay.includes("peacoat")) return "peacoat";
  if (hay.includes("waistcoat") || hay.includes("vest")) return "waistcoat";
  if (hay.includes("jacket") && !hay.includes("suit") && !hay.includes("tux")) return isDouble ? "db_jacket" : "sb_jacket";
  return isDouble ? "db_suit" : "sb_suit";
}

function isSuitOrTux(product) { const cat = getProductCategory(product); return cat === "sb_suit" || cat === "db_suit"; }

function getLengthKey(lengthLabel) { 
  if (lengthLabel === "Short") return "Short"; 
  if (lengthLabel === "Long") return "Long"; 
  return "Regular"; 
}

function extractJacketCodeFromSelections({ jacketSize, lengthKey }) {
  const char = lengthKey === "Short" ? "S" : lengthKey === "Long" ? "L" : "R";
  return `${jacketSize}${char}`;
}

function trouserFromJacket(jacketSize, lengthKey) {
  if (typeof jacketSize === 'string') return "—";
  const trouser = jacketSize - 6;
  const suffix = lengthKey === "Short" ? "S" : lengthKey === "Long" ? "L" : "R";
  return `${trouser}${suffix}`;
}

// ============================================
// ROBUST VARIANT RESOLUTION
// Handles every real ZB variant format: "40", "40L", "40S",
// "15 1/2", "15 3/4", "15 1/2L", "40 Long". Matches on a canonical
// {size, length} signature so decimals and fractions reconcile.
// ============================================
function fractionToDecimalStr(str) {
  return String(str)
    .replace(/(\d+)\s+(\d+)\s*\/\s*(\d+)/g, (_, w, n, d) => String(Number(w) + Number(n) / Number(d)))
    .replace(/(^|[^\d.])(\d+)\s*\/\s*(\d+)/g, (_, p, n, d) => p + String(Number(n) / Number(d)));
}
function canonSizeToken(raw) {
  const s = fractionToDecimalStr(normalizeValue(raw));
  const m = s.match(/\d+(?:\.\d+)?/);
  const size = m ? parseFloat(m[0]) : null;
  const rest = s.replace(/\d+(?:\.\d+)?/g, " ").replace(/[^a-z]/g, " ").trim();
  let len = "regular";
  if (rest.includes("long") || rest === "l") len = "long";
  else if (rest.includes("short") || rest === "s") len = "short";
  return { size, len };
}
function getSizeOptionIndexes(product) {
  const optionNames = (product && product.options) || [];
  return optionNames
    .map((name, idx) => ({ name, idx }))
    .filter(({ name }) => optionNameLooksLikeSize(name))
    .map(({ idx }) => idx);
}
function variantSizeSignature(product, variant, sizeIndexes) {
  const opts = (variant && variant.options) || [];
  let parts = sizeIndexes.map((i) => opts[i]).filter((x) => x != null && x !== "");
  if (!parts.length) parts = [variant && variant.title];
  return canonSizeToken(parts.join(" "));
}
function getProductSizeInfo(product) {
  const sizeIndexes = getSizeOptionIndexes(product);
  const lens = { regular: new Set(), short: new Set(), long: new Set() };
  ((product && product.variants) || []).forEach((v) => {
    const sig = variantSizeSignature(product, v, sizeIndexes);
    if (sig.size == null) return;
    (lens[sig.len] || lens.regular).add(sig.size);
  });
  const availableLengths = [
    { key: "Short", set: lens.short },
    { key: "Regular", set: lens.regular },
    { key: "Long", set: lens.long },
  ].filter((l) => l.set.size > 0).map((l) => l.key);
  return { sizeIndexes, lens, availableLengths };
}
// Resolve a desired {jacketSize, lengthKey} to a real Shopify variant.
// Returns { id, label, lengthKey } using the ACTUAL variant title, or null.
// Falls back to size-only when the requested length isn't stocked, so the
// recorded length always reflects what the customer actually receives.
function resolveVariant(product, { jacketSize, lengthKey }) {
  if (!product || !product.variants) return null;
  const sizeIndexes = getSizeOptionIndexes(product);
  const desiredSize = parseFloat(jacketSize);
  const desiredLen = getLengthKey(lengthKey).toLowerCase();
  const sizeEq = (a, b) => a != null && b != null && Math.abs(a - b) < 0.01;
  const sigs = (product.variants || []).map((v) => ({ v, sig: variantSizeSignature(product, v, sizeIndexes) }));
  const hit =
    sigs.find(({ sig }) => sizeEq(sig.size, desiredSize) && sig.len === desiredLen) ||
    sigs.find(({ sig }) => sizeEq(sig.size, desiredSize) && sig.len === "regular") ||
    sigs.find(({ sig }) => sizeEq(sig.size, desiredSize));
  if (!hit) return null;
  const cap = (x) => x.charAt(0).toUpperCase() + x.slice(1);
  return { id: hit.v.id, label: hit.v.title || String(jacketSize), lengthKey: cap(hit.sig.len) };
}

// ============================================
// UNIT CONVERSION FUNCTIONS
// ============================================
function toFtInFromCm(cm) { const totalIn = Math.round(Number(cm) / 2.54); const ft = Math.floor(totalIn / 12); const inch = totalIn - ft * 12; return { ft, inch }; }
function kgToLbs(kg) { return Math.round(Number(kg) * 2.20462); }
function lbsToKg(lbs) { return Math.round(Number(lbs) / 2.20462); }
function inToCm(inches) { return inches * 2.54; }
function cmToIn(cm) { return cm / 2.54; }

function formatMeasure(valIn, unit) {
  if (valIn == null || Number.isNaN(valIn)) return "—";
  if (unit === "in") return round1(valIn).toFixed(1).replace(/\.0$/, "");
  const cm = roundTo(inToCm(valIn), 0.5);
  return (Math.round(cm * 10) / 10).toFixed(1).replace(/\.0$/, "");
}

function adjustMeasure(valIn, deltaIn) { return round1(Number(valIn) + deltaIn); }

// ============================================
// END OF PART 1 - Continue to Part 2
// ============================================
// ============================================
// ZANE BARLAS FIT ENGINE V2 - PART 2 OF 5
// Overcoat, Peacoat, Waistcoat, Shirt Charts & Helpers
// COMPLETE MEASUREMENT FIELDS FROM CSVs
// ============================================

const LENGTH_OPTIONS = [
  { key: "Short", label: "Short", suffix: "S", height: '< 5.5"' },
  { key: "Regular", label: "Regular", suffix: "R", height: '5.5 - 6.1"' },
  { key: "Long", label: "Long", suffix: "L", height: '> 6.1"' },
];

const BODY_SHAPES = [
  { key: "Inverted", label: "V-Shape", desc: "Broader shoulders, narrower waist.", icon: "▽" },
  { key: "Rectangle", label: "Average", desc: "Balanced proportions.", icon: "▯" },
  { key: "Triangle", label: "Fuller", desc: "Waist is wider than shoulders.", icon: "△" },
];

const FIT_PREFS = [
  { key: "Slim", label: "Slim Fit", desc: "Closer cut. Defined shape." },
  { key: "Tailored", label: "Tailored Fit", desc: "Our standard cut. Clean line." },
  { key: "Classic", label: "Classic Fit", desc: "More room through the body." },
];

// ============================================
// SINGLE BREAST JACKET/BLAZER - VERIFIED INDUSTRY STANDARD
// Size 38 = 43.2" Chest (Drop-6 Standard)
// Fields: chest, waist, length, sleeve, hip, shoulder, back, vent, frontLength, bicep, sleeveOpening
// ============================================
const SB_JACKET_CHART_IN = {
  Regular: {
    34: { chest: 20.0, waist: 17.6, length: 28.7, sleeve: 24.5, hip: 19.0, shoulder: 17.0, back: 8.1, vent: 9.8, frontLength: 30.1, bicep: 7.7, sleeveOpening: 5.3 },
    36: { chest: 20.8, waist: 18.4, length: 29.1, sleeve: 25.0, hip: 19.8, shoulder: 17.5, back: 8.3, vent: 9.8, frontLength: 30.5, bicep: 7.9, sleeveOpening: 5.3 },
    38: { chest: 21.6, waist: 19.2, length: 29.5, sleeve: 25.5, hip: 20.6, shoulder: 18.0, back: 8.5, vent: 10.0, frontLength: 30.9, bicep: 8.1, sleeveOpening: 5.5 },
    40: { chest: 22.4, waist: 20.0, length: 29.9, sleeve: 26.0, hip: 21.4, shoulder: 18.5, back: 8.9, vent: 10.2, frontLength: 31.3, bicep: 8.3, sleeveOpening: 5.7 },
    42: { chest: 23.2, waist: 20.8, length: 30.3, sleeve: 26.5, hip: 22.2, shoulder: 19.0, back: 9.1, vent: 10.2, frontLength: 31.7, bicep: 8.5, sleeveOpening: 5.7 },
    44: { chest: 24.0, waist: 21.6, length: 30.7, sleeve: 27.0, hip: 23.0, shoulder: 19.5, back: 9.2, vent: 10.4, frontLength: 32.1, bicep: 8.7, sleeveOpening: 5.9 },
    46: { chest: 24.8, waist: 22.4, length: 31.1, sleeve: 27.5, hip: 23.8, shoulder: 20.0, back: 9.4, vent: 10.4, frontLength: 32.5, bicep: 8.9, sleeveOpening: 5.9 },
    48: { chest: 25.6, waist: 23.2, length: 31.5, sleeve: 28.0, hip: 24.6, shoulder: 20.5, back: 9.6, vent: 10.6, frontLength: 32.9, bicep: 9.1, sleeveOpening: 6.1 },
    50: { chest: 26.4, waist: 24.0, length: 31.9, sleeve: 28.5, hip: 25.4, shoulder: 21.0, back: 10.0, vent: 10.8, frontLength: 33.3, bicep: 9.3, sleeveOpening: 6.3 }
  },
  Long: {
    34: { chest: 20.0, waist: 17.6, length: 29.9, sleeve: 25.5, hip: 19.0, shoulder: 17.0, back: 8.1, vent: 10.2, frontLength: 31.3, bicep: 7.7, sleeveOpening: 5.3 },
    36: { chest: 20.8, waist: 18.4, length: 30.3, sleeve: 26.0, hip: 19.8, shoulder: 17.5, back: 8.3, vent: 10.2, frontLength: 31.7, bicep: 7.9, sleeveOpening: 5.3 },
    38: { chest: 21.6, waist: 19.2, length: 30.7, sleeve: 26.5, hip: 20.6, shoulder: 18.0, back: 8.5, vent: 10.4, frontLength: 32.1, bicep: 8.1, sleeveOpening: 5.5 },
    40: { chest: 22.4, waist: 20.0, length: 31.1, sleeve: 27.0, hip: 21.4, shoulder: 18.5, back: 8.9, vent: 10.6, frontLength: 32.5, bicep: 8.3, sleeveOpening: 5.7 },
    42: { chest: 23.2, waist: 20.8, length: 31.5, sleeve: 27.5, hip: 22.2, shoulder: 19.0, back: 9.1, vent: 10.6, frontLength: 32.9, bicep: 8.5, sleeveOpening: 5.7 },
    44: { chest: 24.0, waist: 21.6, length: 31.9, sleeve: 28.0, hip: 23.0, shoulder: 19.5, back: 9.2, vent: 10.8, frontLength: 33.3, bicep: 8.7, sleeveOpening: 5.9 },
    46: { chest: 24.8, waist: 22.4, length: 32.3, sleeve: 28.5, hip: 23.8, shoulder: 20.0, back: 9.4, vent: 10.8, frontLength: 33.7, bicep: 8.9, sleeveOpening: 5.9 },
    48: { chest: 25.6, waist: 23.2, length: 32.7, sleeve: 29.0, hip: 24.6, shoulder: 20.5, back: 9.6, vent: 11.0, frontLength: 34.1, bicep: 9.1, sleeveOpening: 6.1 },
    50: { chest: 26.4, waist: 24.0, length: 33.1, sleeve: 29.5, hip: 25.4, shoulder: 21.0, back: 10.0, vent: 11.2, frontLength: 34.4, bicep: 9.3, sleeveOpening: 6.3 }
  },
  Short: {
    34: { chest: 20.0, waist: 17.6, length: 27.6, sleeve: 23.5, hip: 19.0, shoulder: 17.0, back: 8.1, vent: 9.4, frontLength: 28.9, bicep: 7.7, sleeveOpening: 5.3 },
    36: { chest: 20.8, waist: 18.4, length: 28.0, sleeve: 24.0, hip: 19.8, shoulder: 17.5, back: 8.3, vent: 9.4, frontLength: 29.3, bicep: 7.9, sleeveOpening: 5.3 },
    38: { chest: 21.6, waist: 19.2, length: 28.4, sleeve: 24.5, hip: 20.6, shoulder: 18.0, back: 8.5, vent: 9.6, frontLength: 29.7, bicep: 8.1, sleeveOpening: 5.5 },
    40: { chest: 22.4, waist: 20.0, length: 28.7, sleeve: 25.0, hip: 21.4, shoulder: 18.5, back: 8.9, vent: 9.8, frontLength: 30.1, bicep: 8.3, sleeveOpening: 5.7 },
    42: { chest: 23.2, waist: 20.8, length: 29.1, sleeve: 25.5, hip: 22.2, shoulder: 19.0, back: 9.1, vent: 9.8, frontLength: 30.5, bicep: 8.5, sleeveOpening: 5.7 },
    44: { chest: 24.0, waist: 21.6, length: 29.5, sleeve: 26.0, hip: 23.0, shoulder: 19.5, back: 9.2, vent: 10.0, frontLength: 30.9, bicep: 8.7, sleeveOpening: 5.9 },
    46: { chest: 24.8, waist: 22.4, length: 29.9, sleeve: 26.5, hip: 23.8, shoulder: 20.0, back: 9.4, vent: 10.0, frontLength: 31.3, bicep: 8.9, sleeveOpening: 5.9 },
    48: { chest: 25.6, waist: 23.2, length: 30.3, sleeve: 27.0, hip: 24.6, shoulder: 20.5, back: 9.6, vent: 10.2, frontLength: 31.7, bicep: 9.1, sleeveOpening: 6.1 },
    50: { chest: 26.4, waist: 24.0, length: 30.7, sleeve: 27.5, hip: 25.4, shoulder: 21.0, back: 10.0, vent: 10.4, frontLength: 32.1, bicep: 9.3, sleeveOpening: 6.3 }
  }
};

// DB Jacket uses same structure - copy from SB for now (update if you have DB CSV)
const DB_JACKET_CHART_IN = SB_JACKET_CHART_IN;

// ============================================
// TROUSER - FULL MEASUREMENTS WITH HIP (26-44)
// Fields: waist, hip, thigh, knee, hem, inseam, rise, backRise
// ============================================
const SB_TROUSER_CHART_IN = { 
  Regular: {
    26: { waist: 14.6, hip: 17.6, thigh: 9.75, knee: 8.45, hem: 6.9, inseam: 32.1, rise: 9.35, backRise: 14.85 },
    28: { waist: 15.4, hip: 18.4, thigh: 10.25, knee: 8.65, hem: 7.1, inseam: 32.5, rise: 9.55, backRise: 15.05 },
    30: { waist: 16.2, hip: 19.1, thigh: 10.75, knee: 9.05, hem: 7.3, inseam: 32.9, rise: 9.55, backRise: 15.45 },
    32: { waist: 16.9, hip: 19.9, thigh: 11.25, knee: 9.25, hem: 7.5, inseam: 33.3, rise: 9.75, backRise: 15.65 },
    34: { waist: 17.7, hip: 20.7, thigh: 11.75, knee: 9.45, hem: 7.7, inseam: 33.7, rise: 9.95, backRise: 16.05 },
    36: { waist: 18.5, hip: 21.5, thigh: 12.25, knee: 9.55, hem: 7.9, inseam: 34.1, rise: 9.95, backRise: 16.45 },
    38: { waist: 19.3, hip: 22.3, thigh: 12.75, knee: 9.75, hem: 8.1, inseam: 34.4, rise: 10.05, backRise: 16.65 },
    40: { waist: 20.1, hip: 23.1, thigh: 13.25, knee: 9.95, hem: 8.3, inseam: 34.8, rise: 10.25, backRise: 16.75 },
    42: { waist: 20.9, hip: 23.9, thigh: 13.75, knee: 10.15, hem: 8.5, inseam: 35.2, rise: 10.25, backRise: 17.15 },
    44: { waist: 21.6, hip: 24.6, thigh: 14.25, knee: 10.35, hem: 8.7, inseam: 35.6, rise: 10.45, backRise: 17.35 }
  },
  Short: {
    26: { waist: 14.6, hip: 17.6, thigh: 9.75, knee: 8.45, hem: 6.9, inseam: 30.3, rise: 9.35, backRise: 14.85 },
    28: { waist: 15.4, hip: 18.4, thigh: 10.25, knee: 8.65, hem: 7.1, inseam: 30.9, rise: 9.55, backRise: 15.05 },
    30: { waist: 16.2, hip: 19.1, thigh: 10.75, knee: 9.05, hem: 7.3, inseam: 31.3, rise: 9.55, backRise: 15.45 },
    32: { waist: 17.0, hip: 19.9, thigh: 11.25, knee: 9.25, hem: 7.5, inseam: 31.5, rise: 9.75, backRise: 15.65 },
    34: { waist: 17.7, hip: 20.7, thigh: 11.75, knee: 9.45, hem: 7.7, inseam: 31.9, rise: 9.95, backRise: 16.05 },
    36: { waist: 18.5, hip: 21.5, thigh: 12.25, knee: 9.55, hem: 7.9, inseam: 32.3, rise: 9.95, backRise: 16.45 },
    38: { waist: 19.3, hip: 22.3, thigh: 12.75, knee: 9.75, hem: 8.1, inseam: 31.9, rise: 10.05, backRise: 16.65 },
    40: { waist: 20.1, hip: 23.1, thigh: 13.25, knee: 9.95, hem: 8.3, inseam: 32.5, rise: 10.25, backRise: 16.75 },
    42: { waist: 20.9, hip: 23.9, thigh: 13.75, knee: 10.15, hem: 8.5, inseam: 32.9, rise: 10.25, backRise: 17.15 },
    44: { waist: 21.6, hip: 24.6, thigh: 14.25, knee: 10.35, hem: 8.7, inseam: 33.1, rise: 10.45, backRise: 17.35 }
  },
  Long: {
    26: { waist: 14.6, hip: 17.6, thigh: 9.75, knee: 8.45, hem: 6.9, inseam: 33.7, rise: 10.1, backRise: 15.05 },
    28: { waist: 15.4, hip: 18.4, thigh: 10.25, knee: 8.65, hem: 7.1, inseam: 34.1, rise: 10.3, backRise: 15.25 },
    30: { waist: 15.8, hip: 19.1, thigh: 10.75, knee: 9.05, hem: 7.3, inseam: 34.4, rise: 10.3, backRise: 15.65 },
    32: { waist: 16.5, hip: 19.9, thigh: 11.25, knee: 9.25, hem: 7.5, inseam: 34.8, rise: 10.5, backRise: 15.85 },
    34: { waist: 17.3, hip: 20.7, thigh: 11.75, knee: 9.45, hem: 7.7, inseam: 35.2, rise: 10.6, backRise: 16.25 },
    36: { waist: 18.1, hip: 21.5, thigh: 12.25, knee: 9.55, hem: 7.9, inseam: 35.6, rise: 10.6, backRise: 16.65 },
    38: { waist: 18.9, hip: 22.3, thigh: 12.75, knee: 9.75, hem: 8.1, inseam: 36.0, rise: 10.8, backRise: 16.75 },
    40: { waist: 19.7, hip: 23.1, thigh: 13.25, knee: 9.95, hem: 8.3, inseam: 36.4, rise: 11.0, backRise: 16.95 },
    42: { waist: 20.5, hip: 23.9, thigh: 13.75, knee: 10.15, hem: 8.5, inseam: 36.8, rise: 11.0, backRise: 17.35 },
    44: { waist: 21.6, hip: 24.6, thigh: 14.25, knee: 10.35, hem: 8.7, inseam: 37.2, rise: 11.2, backRise: 17.55 }
  }
};

const DB_TROUSER_CHART_IN = SB_TROUSER_CHART_IN;

// ============================================
// SINGLE BREAST OVERCOAT (32-50, Regular/Long/Short)
// Fields: chest, waist, length, sleeve, hip, shoulder, back, vent, frontLength, bicep, sleeveOpening
// ============================================
const SB_OVERCOAT_CHART_IN = { 
  Regular: {
    32: { chest: 19.5, waist: 17.1, length: 43.3, sleeve: 32.3, hip: 18.5, shoulder: 16.9, back: 8.4, vent: 12.8, frontLength: 44.0, bicep: 8.2, sleeveOpening: 5.6 },
    34: { chest: 20.3, waist: 17.9, length: 43.7, sleeve: 32.7, hip: 19.3, shoulder: 17.3, back: 8.6, vent: 13.0, frontLength: 44.4, bicep: 8.4, sleeveOpening: 5.8 },
    36: { chest: 21.1, waist: 18.7, length: 44.1, sleeve: 33.1, hip: 20.1, shoulder: 17.7, back: 8.8, vent: 13.2, frontLength: 44.8, bicep: 8.6, sleeveOpening: 5.8 },
    38: { chest: 21.9, waist: 19.5, length: 44.5, sleeve: 33.5, hip: 20.9, shoulder: 18.1, back: 9.0, vent: 13.4, frontLength: 45.2, bicep: 8.6, sleeveOpening: 6.0 },
    40: { chest: 22.7, waist: 20.3, length: 44.9, sleeve: 33.9, hip: 21.7, shoulder: 18.5, back: 9.4, vent: 13.6, frontLength: 45.6, bicep: 8.85, sleeveOpening: 6.2 },
    42: { chest: 23.4, waist: 21.1, length: 45.3, sleeve: 34.3, hip: 22.5, shoulder: 18.9, back: 9.6, vent: 13.8, frontLength: 46.0, bicep: 9.1, sleeveOpening: 6.2 },
    44: { chest: 24.2, waist: 21.9, length: 45.7, sleeve: 34.6, hip: 23.2, shoulder: 19.3, back: 9.8, vent: 14.0, frontLength: 46.4, bicep: 9.35, sleeveOpening: 6.4 },
    46: { chest: 25.0, waist: 22.7, length: 46.1, sleeve: 35.0, hip: 24.0, shoulder: 19.7, back: 9.9, vent: 14.2, frontLength: 46.8, bicep: 9.6, sleeveOpening: 6.4 },
    48: { chest: 25.8, waist: 23.4, length: 46.5, sleeve: 35.4, hip: 24.8, shoulder: 20.1, back: 10.1, vent: 14.4, frontLength: 47.2, bicep: 9.85, sleeveOpening: 6.6 },
    50: { chest: 26.6, waist: 24.2, length: 46.9, sleeve: 35.8, hip: 25.6, shoulder: 20.5, back: 10.5, vent: 14.6, frontLength: 47.6, bicep: 10.1, sleeveOpening: 6.8 }
  },
  Long: {
    32: { chest: 19.3, waist: 16.7, length: 45.3, sleeve: 33.3, hip: 18.1, shoulder: 16.9, back: 8.4, vent: 13.2, frontLength: 46.0, bicep: 8.2, sleeveOpening: 5.6 },
    34: { chest: 20.1, waist: 17.5, length: 45.7, sleeve: 33.7, hip: 18.9, shoulder: 17.3, back: 8.6, vent: 13.4, frontLength: 46.4, bicep: 8.4, sleeveOpening: 5.8 },
    36: { chest: 20.9, waist: 18.3, length: 46.1, sleeve: 34.1, hip: 19.7, shoulder: 17.7, back: 8.8, vent: 13.6, frontLength: 46.8, bicep: 8.6, sleeveOpening: 5.8 },
    38: { chest: 21.7, waist: 19.1, length: 46.5, sleeve: 34.5, hip: 20.5, shoulder: 18.1, back: 9.0, vent: 13.8, frontLength: 47.2, bicep: 8.6, sleeveOpening: 6.0 },
    40: { chest: 22.5, waist: 19.9, length: 46.9, sleeve: 34.9, hip: 21.3, shoulder: 18.5, back: 9.4, vent: 14.0, frontLength: 47.6, bicep: 8.85, sleeveOpening: 6.2 },
    42: { chest: 23.2, waist: 20.7, length: 47.3, sleeve: 35.3, hip: 22.1, shoulder: 18.9, back: 9.6, vent: 14.2, frontLength: 48.0, bicep: 9.1, sleeveOpening: 6.2 },
    44: { chest: 24.0, waist: 21.5, length: 47.7, sleeve: 35.6, hip: 22.9, shoulder: 19.3, back: 9.8, vent: 14.4, frontLength: 48.4, bicep: 9.35, sleeveOpening: 6.4 },
    46: { chest: 24.8, waist: 22.3, length: 48.1, sleeve: 36.0, hip: 23.6, shoulder: 19.7, back: 9.9, vent: 14.6, frontLength: 48.8, bicep: 9.6, sleeveOpening: 6.4 },
    48: { chest: 25.6, waist: 23.0, length: 48.5, sleeve: 36.4, hip: 24.4, shoulder: 20.1, back: 10.1, vent: 14.8, frontLength: 49.2, bicep: 9.85, sleeveOpening: 6.6 },
    50: { chest: 26.4, waist: 23.8, length: 48.9, sleeve: 36.8, hip: 25.2, shoulder: 20.5, back: 10.5, vent: 15.0, frontLength: 49.6, bicep: 10.1, sleeveOpening: 6.8 }
  },
  Short: {
    32: { chest: 19.5, waist: 17.1, length: 41.3, sleeve: 31.3, hip: 18.5, shoulder: 16.9, back: 8.4, vent: 12.4, frontLength: 42.0, bicep: 8.2, sleeveOpening: 5.6 },
    34: { chest: 20.3, waist: 17.9, length: 41.7, sleeve: 31.7, hip: 19.3, shoulder: 17.3, back: 8.6, vent: 12.6, frontLength: 42.4, bicep: 8.4, sleeveOpening: 5.8 },
    36: { chest: 21.1, waist: 18.7, length: 42.1, sleeve: 32.1, hip: 20.1, shoulder: 17.7, back: 8.8, vent: 12.8, frontLength: 42.8, bicep: 8.6, sleeveOpening: 5.8 },
    38: { chest: 21.9, waist: 19.5, length: 42.5, sleeve: 32.5, hip: 20.9, shoulder: 18.1, back: 9.0, vent: 13.0, frontLength: 43.2, bicep: 8.6, sleeveOpening: 6.0 },
    40: { chest: 22.7, waist: 20.3, length: 42.9, sleeve: 32.9, hip: 21.7, shoulder: 18.5, back: 9.4, vent: 13.2, frontLength: 43.6, bicep: 8.85, sleeveOpening: 6.2 },
    42: { chest: 23.4, waist: 21.1, length: 43.3, sleeve: 33.3, hip: 22.5, shoulder: 18.9, back: 9.6, vent: 13.4, frontLength: 44.0, bicep: 9.1, sleeveOpening: 6.2 },
    44: { chest: 24.2, waist: 21.9, length: 43.7, sleeve: 33.6, hip: 23.2, shoulder: 19.3, back: 9.8, vent: 13.6, frontLength: 44.4, bicep: 9.35, sleeveOpening: 6.4 },
    46: { chest: 25.0, waist: 22.7, length: 44.1, sleeve: 34.0, hip: 24.0, shoulder: 19.7, back: 9.9, vent: 13.8, frontLength: 44.8, bicep: 9.6, sleeveOpening: 6.4 },
    48: { chest: 25.8, waist: 23.4, length: 44.5, sleeve: 34.4, hip: 24.8, shoulder: 20.1, back: 10.1, vent: 14.0, frontLength: 45.2, bicep: 9.85, sleeveOpening: 6.6 },
    50: { chest: 26.6, waist: 24.2, length: 44.9, sleeve: 34.8, hip: 25.6, shoulder: 20.5, back: 10.5, vent: 14.2, frontLength: 45.6, bicep: 10.1, sleeveOpening: 6.8 }
  }
};

const DB_OVERCOAT_CHART_IN = SB_OVERCOAT_CHART_IN;

// ============================================
// PEACOAT (32-50, Regular/Long/Short) - FROM CSV
// Fields: chest, waist, length, sleeve, hip, shoulder, back, vent, frontLength, bicep, sleeveOpening
// ============================================
const PEACOAT_CHART_IN = { 
  Regular: {
    32: { chest: 19.5, waist: 17.1, length: 30.3, sleeve: 24.9, hip: 18.5, shoulder: 17.0, back: 8.4, vent: 13.2, frontLength: 35.0, bicep: 8.2, sleeveOpening: 5.6 },
    34: { chest: 20.3, waist: 17.9, length: 30.7, sleeve: 25.3, hip: 19.3, shoulder: 17.4, back: 8.6, vent: 13.4, frontLength: 35.4, bicep: 8.4, sleeveOpening: 5.8 },
    36: { chest: 21.1, waist: 18.7, length: 31.1, sleeve: 25.7, hip: 20.1, shoulder: 17.8, back: 8.8, vent: 13.6, frontLength: 35.8, bicep: 8.6, sleeveOpening: 5.8 },
    38: { chest: 21.9, waist: 19.5, length: 31.5, sleeve: 26.1, hip: 20.9, shoulder: 18.2, back: 9.0, vent: 13.8, frontLength: 36.2, bicep: 8.6, sleeveOpening: 6.0 },
    40: { chest: 22.7, waist: 20.3, length: 31.9, sleeve: 26.5, hip: 21.7, shoulder: 18.6, back: 9.4, vent: 14.0, frontLength: 36.6, bicep: 8.85, sleeveOpening: 6.2 },
    42: { chest: 23.4, waist: 21.1, length: 32.3, sleeve: 26.9, hip: 22.5, shoulder: 19.0, back: 9.6, vent: 14.2, frontLength: 37.0, bicep: 9.1, sleeveOpening: 6.2 },
    44: { chest: 24.2, waist: 21.9, length: 32.5, sleeve: 27.3, hip: 23.2, shoulder: 19.4, back: 9.8, vent: 14.4, frontLength: 37.4, bicep: 9.35, sleeveOpening: 6.4 },
    46: { chest: 25.0, waist: 22.7, length: 32.9, sleeve: 27.7, hip: 24.0, shoulder: 19.8, back: 9.9, vent: 14.6, frontLength: 37.8, bicep: 9.6, sleeveOpening: 6.4 },
    48: { chest: 25.8, waist: 23.4, length: 33.3, sleeve: 28.1, hip: 24.8, shoulder: 20.2, back: 10.1, vent: 14.8, frontLength: 38.2, bicep: 9.85, sleeveOpening: 6.6 },
    50: { chest: 26.6, waist: 24.2, length: 33.7, sleeve: 28.5, hip: 25.6, shoulder: 20.6, back: 10.5, vent: 15.0, frontLength: 38.6, bicep: 10.1, sleeveOpening: 6.8 }
  },
  Long: {
    32: { chest: 19.3, waist: 16.7, length: 31.5, sleeve: 25.7, hip: 18.1, shoulder: 17.0, back: 8.4, vent: 13.6, frontLength: 36.2, bicep: 8.2, sleeveOpening: 5.6 },
    34: { chest: 20.1, waist: 17.5, length: 31.9, sleeve: 26.1, hip: 18.9, shoulder: 17.4, back: 8.6, vent: 13.8, frontLength: 36.6, bicep: 8.4, sleeveOpening: 5.8 },
    36: { chest: 20.9, waist: 18.3, length: 32.3, sleeve: 26.5, hip: 19.7, shoulder: 17.8, back: 8.8, vent: 14.0, frontLength: 37.0, bicep: 8.6, sleeveOpening: 5.8 },
    38: { chest: 21.7, waist: 19.1, length: 32.7, sleeve: 26.9, hip: 20.5, shoulder: 18.2, back: 9.0, vent: 14.2, frontLength: 37.4, bicep: 8.6, sleeveOpening: 6.0 },
    40: { chest: 22.5, waist: 19.9, length: 33.1, sleeve: 27.3, hip: 21.3, shoulder: 18.6, back: 9.4, vent: 14.4, frontLength: 37.8, bicep: 8.85, sleeveOpening: 6.2 },
    42: { chest: 23.2, waist: 20.7, length: 33.5, sleeve: 27.7, hip: 22.1, shoulder: 19.0, back: 9.6, vent: 14.6, frontLength: 38.2, bicep: 9.1, sleeveOpening: 6.2 },
    44: { chest: 24.0, waist: 21.5, length: 33.7, sleeve: 28.1, hip: 22.9, shoulder: 19.4, back: 9.8, vent: 14.8, frontLength: 38.6, bicep: 9.35, sleeveOpening: 6.4 },
    46: { chest: 24.8, waist: 22.3, length: 34.1, sleeve: 28.5, hip: 23.6, shoulder: 19.8, back: 9.9, vent: 15.0, frontLength: 39.0, bicep: 9.6, sleeveOpening: 6.4 },
    48: { chest: 25.6, waist: 23.0, length: 34.5, sleeve: 28.8, hip: 24.4, shoulder: 20.2, back: 10.1, vent: 15.2, frontLength: 39.4, bicep: 9.85, sleeveOpening: 6.6 },
    50: { chest: 26.4, waist: 23.8, length: 34.9, sleeve: 29.2, hip: 25.2, shoulder: 20.6, back: 10.5, vent: 15.4, frontLength: 39.8, bicep: 10.1, sleeveOpening: 6.8 }
  },
  Short: {
    32: { chest: 19.5, waist: 17.1, length: 29.1, sleeve: 24.1, hip: 18.5, shoulder: 17.0, back: 8.4, vent: 12.8, frontLength: 34.2, bicep: 8.2, sleeveOpening: 5.6 },
    34: { chest: 20.3, waist: 17.9, length: 29.5, sleeve: 24.5, hip: 19.3, shoulder: 17.4, back: 8.6, vent: 13.0, frontLength: 34.6, bicep: 8.4, sleeveOpening: 5.8 },
    36: { chest: 21.1, waist: 18.7, length: 29.9, sleeve: 24.9, hip: 20.1, shoulder: 17.8, back: 8.8, vent: 13.2, frontLength: 35.0, bicep: 8.6, sleeveOpening: 5.8 },
    38: { chest: 21.9, waist: 19.5, length: 30.3, sleeve: 25.3, hip: 20.9, shoulder: 18.2, back: 9.0, vent: 13.4, frontLength: 35.4, bicep: 8.6, sleeveOpening: 6.0 },
    40: { chest: 22.7, waist: 20.3, length: 30.7, sleeve: 25.7, hip: 21.7, shoulder: 18.6, back: 9.4, vent: 13.6, frontLength: 35.8, bicep: 8.85, sleeveOpening: 6.2 },
    42: { chest: 23.4, waist: 21.1, length: 31.1, sleeve: 26.1, hip: 22.5, shoulder: 19.0, back: 9.6, vent: 13.8, frontLength: 36.2, bicep: 9.1, sleeveOpening: 6.2 },
    44: { chest: 24.2, waist: 21.9, length: 31.3, sleeve: 26.5, hip: 23.2, shoulder: 19.4, back: 9.8, vent: 14.0, frontLength: 36.6, bicep: 9.35, sleeveOpening: 6.4 },
    46: { chest: 25.0, waist: 22.7, length: 31.7, sleeve: 26.9, hip: 24.0, shoulder: 19.8, back: 9.9, vent: 14.2, frontLength: 37.0, bicep: 9.6, sleeveOpening: 6.4 },
    48: { chest: 25.8, waist: 23.4, length: 32.1, sleeve: 27.3, hip: 24.8, shoulder: 20.2, back: 10.1, vent: 14.4, frontLength: 37.4, bicep: 9.85, sleeveOpening: 6.6 },
    50: { chest: 26.6, waist: 24.2, length: 32.5, sleeve: 27.7, hip: 25.6, shoulder: 20.6, back: 10.5, vent: 14.6, frontLength: 37.8, bicep: 10.1, sleeveOpening: 6.8 }
  }
};

// ============================================
// WAISTCOAT (34-46) - Flat structure (no length variants)
// ============================================
const WAISTCOAT_CHART_IN = {
  "Regular": {
    32: { chest: 17.6, waist: 16.2, hip: 17.2, length: 22.8 },
    34: { chest: 18.4, waist: 17.0, hip: 18.0, length: 23.0 },
    36: { chest: 19.0, waist: 17.8, hip: 18.8, length: 23.2 },
    38: { chest: 19.8, waist: 18.6, hip: 19.4, length: 23.4 },
    40: { chest: 20.6, waist: 19.2, hip: 20.2, length: 23.6 },
    42: { chest: 21.4, waist: 20.0, hip: 21.0, length: 23.8 },
    44: { chest: 22.2, waist: 20.8, hip: 21.8, length: 24.0 },
    46: { chest: 23.0, waist: 21.6, hip: 22.6, length: 24.2 },
    48: { chest: 23.8, waist: 22.4, hip: 23.4, length: 24.4 },
    50: { chest: 24.6, waist: 23.2, hip: 24.2, length: 24.6 }
  },
  "Short": {
    32: { chest: 17.6, waist: 16.2, hip: 17.2, length: 21.8 },
    34: { chest: 18.4, waist: 17.0, hip: 18.0, length: 22.0 },
    36: { chest: 19.0, waist: 17.8, hip: 18.8, length: 22.2 },
    38: { chest: 19.8, waist: 18.6, hip: 19.4, length: 22.4 },
    40: { chest: 20.6, waist: 19.2, hip: 20.2, length: 22.6 },
    42: { chest: 21.4, waist: 20.0, hip: 21.0, length: 22.8 },
    44: { chest: 22.2, waist: 20.8, hip: 21.8, length: 23.0 },
    46: { chest: 23.0, waist: 21.6, hip: 22.6, length: 23.2 },
    48: { chest: 23.8, waist: 22.4, hip: 23.4, length: 23.4 },
    50: { chest: 24.6, waist: 23.2, hip: 24.2, length: 23.6 }
  },
  "Long": {
    32: { chest: 17.6, waist: 16.2, hip: 17.2, length: 23.8 },
    34: { chest: 18.4, waist: 17.0, hip: 18.0, length: 24.0 },
    36: { chest: 18.8, waist: 17.4, hip: 18.4, length: 24.2 },
    38: { chest: 19.6, waist: 18.2, hip: 19.0, length: 24.4 },
    40: { chest: 20.4, waist: 18.8, hip: 19.8, length: 24.6 },
    42: { chest: 21.2, waist: 19.6, hip: 20.6, length: 24.8 },
    44: { chest: 22.0, waist: 20.4, hip: 21.4, length: 25.0 },
    46: { chest: 22.8, waist: 21.2, hip: 22.2, length: 25.2 },
    48: { chest: 23.6, waist: 22.0, hip: 23.0, length: 25.4 },
    50: { chest: 24.4, waist: 22.8, hip: 23.8, length: 25.6 }
  }
};

// ============================================
// SHIRT (Collar sizes 13.5-17.0, Regular/Long/Short) - FROM CSV
// Fields: collar, chest, waist, hip, length, sleeveRight, sleeveLeft, cuffRight, cuffLeft, bicep, forearm, shoulder
// ============================================
const SHIRT_CHART_IN = {
  "Regular": {
    "13.5": { collar: 14.0, chest: 20.1, waist: 17.5, hip: 19.3, length: 30.3, sleeveRight: 25.2, sleeveLeft: 25.2, cuffRight: 8.7, cuffLeft: 8.7, bicep: 13.8, forearm: 11.6, shoulder: 16.9 },
    "14.0": { collar: 14.4, chest: 20.7, waist: 18.1, hip: 19.9, length: 30.3, sleeveRight: 25.4, sleeveLeft: 25.4, cuffRight: 8.7, cuffLeft: 8.7, bicep: 14.2, forearm: 11.8, shoulder: 17.3 },
    "14.5": { collar: 14.8, chest: 21.3, waist: 18.7, hip: 20.5, length: 31.1, sleeveRight: 25.6, sleeveLeft: 25.6, cuffRight: 8.9, cuffLeft: 8.9, bicep: 14.6, forearm: 12.0, shoulder: 17.7 },
    "15.0": { collar: 15.2, chest: 21.9, waist: 19.3, hip: 21.1, length: 31.1, sleeveRight: 25.8, sleeveLeft: 25.8, cuffRight: 8.9, cuffLeft: 8.9, bicep: 15.0, forearm: 12.2, shoulder: 18.1 },
    "15.5": { collar: 15.6, chest: 22.4, waist: 19.9, hip: 21.7, length: 31.9, sleeveRight: 26.0, sleeveLeft: 26.0, cuffRight: 9.3, cuffLeft: 9.3, bicep: 15.4, forearm: 12.6, shoulder: 18.5 },
    "15.75": { collar: 15.9, chest: 23.0, waist: 20.5, hip: 22.2, length: 31.9, sleeveRight: 26.2, sleeveLeft: 26.2, cuffRight: 9.3, cuffLeft: 9.3, bicep: 15.7, forearm: 12.8, shoulder: 18.9 },
    "16.0": { collar: 16.3, chest: 23.6, waist: 21.1, hip: 22.8, length: 32.3, sleeveRight: 26.4, sleeveLeft: 26.4, cuffRight: 9.6, cuffLeft: 9.6, bicep: 16.1, forearm: 13.0, shoulder: 19.3 },
    "16.5": { collar: 16.7, chest: 24.2, waist: 21.7, hip: 23.4, length: 32.3, sleeveRight: 26.6, sleeveLeft: 26.6, cuffRight: 9.6, cuffLeft: 9.6, bicep: 16.5, forearm: 13.2, shoulder: 19.7 },
    "17.0": { collar: 17.1, chest: 24.8, waist: 22.2, hip: 24.0, length: 33.1, sleeveRight: 26.8, sleeveLeft: 26.8, cuffRight: 10.0, cuffLeft: 10.0, bicep: 16.9, forearm: 13.6, shoulder: 20.1 },
    "17.5": { collar: 17.6, chest: 25.4, waist: 22.7, hip: 24.6, length: 33.9, sleeveRight: 27.0, sleeveLeft: 27.0, cuffRight: 10.4, cuffLeft: 10.4, bicep: 17.3, forearm: 14.0, shoulder: 20.5 },
    "17.75": { collar: 17.9, chest: 25.7, waist: 23.0, hip: 24.9, length: 34.3, sleeveRight: 27.1, sleeveLeft: 27.1, cuffRight: 10.6, cuffLeft: 10.6, bicep: 17.5, forearm: 14.2, shoulder: 20.7 },
    "18.0": { collar: 18.1, chest: 26.0, waist: 23.2, hip: 25.2, length: 34.7, sleeveRight: 27.2, sleeveLeft: 27.2, cuffRight: 10.8, cuffLeft: 10.8, bicep: 17.7, forearm: 14.4, shoulder: 20.9 }
  },
  "Short": {
    "13.5": { collar: 14.0, chest: 20.1, waist: 17.5, hip: 19.3, length: 29.5, sleeveRight: 24.4, sleeveLeft: 24.4, cuffRight: 8.7, cuffLeft: 8.7, bicep: 13.8, forearm: 11.6, shoulder: 16.9 },
    "14.0": { collar: 14.4, chest: 20.7, waist: 18.1, hip: 19.9, length: 29.5, sleeveRight: 24.6, sleeveLeft: 24.6, cuffRight: 8.7, cuffLeft: 8.7, bicep: 14.2, forearm: 11.8, shoulder: 17.3 },
    "14.5": { collar: 14.8, chest: 21.3, waist: 18.7, hip: 20.5, length: 30.3, sleeveRight: 24.8, sleeveLeft: 24.8, cuffRight: 8.9, cuffLeft: 8.9, bicep: 14.6, forearm: 12.0, shoulder: 17.7 },
    "15.0": { collar: 15.2, chest: 21.9, waist: 19.3, hip: 21.1, length: 30.3, sleeveRight: 25.0, sleeveLeft: 25.0, cuffRight: 8.9, cuffLeft: 8.9, bicep: 15.0, forearm: 12.2, shoulder: 18.1 },
    "15.5": { collar: 15.6, chest: 22.4, waist: 19.9, hip: 21.7, length: 31.1, sleeveRight: 25.2, sleeveLeft: 25.2, cuffRight: 9.3, cuffLeft: 9.3, bicep: 15.4, forearm: 12.6, shoulder: 18.5 },
    "15.75": { collar: 15.9, chest: 23.0, waist: 20.5, hip: 22.2, length: 31.1, sleeveRight: 25.4, sleeveLeft: 25.4, cuffRight: 9.3, cuffLeft: 9.3, bicep: 15.7, forearm: 12.8, shoulder: 18.9 },
    "16.0": { collar: 16.3, chest: 23.6, waist: 21.1, hip: 22.8, length: 31.5, sleeveRight: 25.6, sleeveLeft: 25.6, cuffRight: 9.6, cuffLeft: 9.6, bicep: 16.1, forearm: 13.0, shoulder: 19.3 },
    "16.5": { collar: 16.7, chest: 24.2, waist: 21.7, hip: 23.4, length: 31.5, sleeveRight: 25.8, sleeveLeft: 25.8, cuffRight: 9.6, cuffLeft: 9.6, bicep: 16.5, forearm: 13.2, shoulder: 19.7 },
    "17.0": { collar: 17.1, chest: 24.8, waist: 22.2, hip: 24.0, length: 32.3, sleeveRight: 26.0, sleeveLeft: 26.0, cuffRight: 10.0, cuffLeft: 10.0, bicep: 16.9, forearm: 13.6, shoulder: 20.1 },
    "17.5": { collar: 17.6, chest: 25.4, waist: 22.7, hip: 24.6, length: 33.1, sleeveRight: 26.2, sleeveLeft: 26.2, cuffRight: 10.4, cuffLeft: 10.4, bicep: 17.3, forearm: 14.0, shoulder: 20.5 },
    "17.75": { collar: 17.9, chest: 25.7, waist: 23.0, hip: 24.9, length: 33.5, sleeveRight: 26.3, sleeveLeft: 26.3, cuffRight: 10.6, cuffLeft: 10.6, bicep: 17.5, forearm: 14.2, shoulder: 20.7 },
    "18.0": { collar: 18.1, chest: 26.0, waist: 23.2, hip: 25.2, length: 33.9, sleeveRight: 26.4, sleeveLeft: 26.4, cuffRight: 10.8, cuffLeft: 10.8, bicep: 17.7, forearm: 14.4, shoulder: 20.9 }
  },
  "Long": {
    "13.5": { collar: 14.0, chest: 20.1, waist: 17.5, hip: 19.3, length: 31.1, sleeveRight: 26.2, sleeveLeft: 26.2, cuffRight: 8.7, cuffLeft: 8.7, bicep: 13.8, forearm: 11.6, shoulder: 16.9 },
    "14.0": { collar: 14.4, chest: 20.7, waist: 18.1, hip: 19.9, length: 31.1, sleeveRight: 26.4, sleeveLeft: 26.4, cuffRight: 8.7, cuffLeft: 8.7, bicep: 14.2, forearm: 11.8, shoulder: 17.3 },
    "14.5": { collar: 14.8, chest: 21.3, waist: 18.7, hip: 20.5, length: 31.9, sleeveRight: 26.6, sleeveLeft: 26.6, cuffRight: 8.9, cuffLeft: 8.9, bicep: 14.6, forearm: 12.0, shoulder: 17.7 },
    "15.0": { collar: 15.2, chest: 21.9, waist: 19.3, hip: 21.1, length: 31.9, sleeveRight: 26.8, sleeveLeft: 26.8, cuffRight: 8.9, cuffLeft: 8.9, bicep: 15.0, forearm: 12.2, shoulder: 18.1 },
    "15.5": { collar: 15.6, chest: 22.4, waist: 19.9, hip: 21.7, length: 32.7, sleeveRight: 27.0, sleeveLeft: 27.0, cuffRight: 9.3, cuffLeft: 9.3, bicep: 15.4, forearm: 12.6, shoulder: 18.5 },
    "15.75": { collar: 15.9, chest: 23.0, waist: 20.5, hip: 22.2, length: 32.7, sleeveRight: 27.2, sleeveLeft: 27.2, cuffRight: 9.3, cuffLeft: 9.3, bicep: 15.7, forearm: 12.8, shoulder: 18.9 },
    "16.0": { collar: 16.3, chest: 23.6, waist: 21.1, hip: 22.8, length: 33.1, sleeveRight: 27.4, sleeveLeft: 27.4, cuffRight: 9.6, cuffLeft: 9.6, bicep: 16.1, forearm: 13.0, shoulder: 19.3 },
    "16.5": { collar: 16.7, chest: 24.2, waist: 21.7, hip: 23.4, length: 33.1, sleeveRight: 27.6, sleeveLeft: 27.6, cuffRight: 9.6, cuffLeft: 9.6, bicep: 16.5, forearm: 13.2, shoulder: 19.7 },
    "17.0": { collar: 17.1, chest: 24.8, waist: 22.2, hip: 24.0, length: 33.9, sleeveRight: 27.8, sleeveLeft: 27.8, cuffRight: 10.0, cuffLeft: 10.0, bicep: 16.9, forearm: 13.6, shoulder: 20.1 },
    "17.5": { collar: 17.6, chest: 25.4, waist: 22.7, hip: 24.6, length: 34.7, sleeveRight: 28.0, sleeveLeft: 28.0, cuffRight: 10.4, cuffLeft: 10.4, bicep: 17.3, forearm: 14.0, shoulder: 20.5 },
    "17.75": { collar: 17.9, chest: 25.7, waist: 23.0, hip: 24.9, length: 35.1, sleeveRight: 28.1, sleeveLeft: 28.1, cuffRight: 10.6, cuffLeft: 10.6, bicep: 17.5, forearm: 14.2, shoulder: 20.7 },
    "18.0": { collar: 18.1, chest: 26.0, waist: 23.2, hip: 25.2, length: 35.5, sleeveRight: 28.2, sleeveLeft: 28.2, cuffRight: 10.8, cuffLeft: 10.8, bicep: 17.7, forearm: 14.4, shoulder: 20.9 }
  }
};

// ============================================
// HELPER FUNCTIONS FOR CHART SELECTION
// ============================================
function getJacketChart(category) {
  if (category === 'db_suit' || category === 'db_jacket') return DB_JACKET_CHART_IN;
  return SB_JACKET_CHART_IN;
}

function getTrouserChart(category) {
  if (category === 'db_suit') return DB_TROUSER_CHART_IN;
  return SB_TROUSER_CHART_IN;
}

function getOvercoatChart(category) {
  if (category === 'db_overcoat') return DB_OVERCOAT_CHART_IN;
  return SB_OVERCOAT_CHART_IN;
}

// ============================================
// SHIRT SIZE RECOMMENDATION
// Maps body chest to collar size
// ============================================
function recommendShirtCollarFromChest(bodyChestIn, lengthKey) {
  const lk = getLengthKey(lengthKey);
  const collarSizes = ["13.5", "14.0", "14.5", "15.0", "15.5", "15.75", "16.0", "16.5", "17.0", "17.5", "17.75", "18.0"];
  
  // Find the collar size where shirt chest (doubled for full measurement) best matches body chest
  let bestCollar = 15.0;
  let bestDiff = Infinity;
  
  for (const collar of collarSizes) {
    const shirtData = SHIRT_CHART_IN[lk]?.[collar];
    if (!shirtData) continue;
    // Shirt chest is half-width, so double it for comparison with body chest
    const shirtFullChest = shirtData.chest * 2;
    const diff = Math.abs(shirtFullChest - bodyChestIn);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestCollar = parseFloat(collar);
    }
  }
  
  return bestCollar;
}

// ============================================
// VARIANT MATCHING FUNCTIONS
// ============================================
function getVariantById(product, id) { 
  if (!product || !product.variants) return null; 
  return product.variants.find((v) => Number(v.id) === Number(id)) || null; 
}

function findMatchingVariantId(product, { jacketCode, jacketSize, lengthKey }) {
  if (!product || !product.variants || !product.options) return null;
  const selectedVariantId = getSelectedVariantIdFromDom();
  const selectedVariant = selectedVariantId ? getVariantById(product, selectedVariantId) : null;
  const baseOptions = [];
  if (selectedVariant) { const opts = selectedVariant.options || []; for (let i = 0; i < opts.length; i++) baseOptions[i] = opts[i]; }
  else { const v0 = product.variants.find((v) => v.available) || product.variants[0]; if (v0 && v0.options) { for (let i = 0; i < v0.options.length; i++) baseOptions[i] = v0.options[i]; } }
  const optionNames = product.options || [];
  const sizeIndexes = optionNames.map((name, idx) => ({ name, idx })).filter(({ name }) => optionNameLooksLikeSize(name)).map(({ idx }) => idx);
  const attempts = [];
  
  // Length suffix variations
  const lengthSuffix = lengthKey === "Short" ? "S" : lengthKey === "Long" ? "L" : "R";
  const lengthFull = lengthKey || "Regular";
  
  // Try combined code like "50L"
  if (sizeIndexes.length >= 1) { const optA = baseOptions.slice(); optA[sizeIndexes[0]] = jacketCode; attempts.push(optA); }
  // Try "50 L" with space
  if (sizeIndexes.length >= 1) { const optA2 = baseOptions.slice(); optA2[sizeIndexes[0]] = `${jacketSize} ${lengthSuffix}`; attempts.push(optA2); }
  // Try "50 Long" full word
  if (sizeIndexes.length >= 1) { const optA3 = baseOptions.slice(); optA3[sizeIndexes[0]] = `${jacketSize} ${lengthFull}`; attempts.push(optA3); }
  // Try "50-L" with dash
  if (sizeIndexes.length >= 1) { const optA4 = baseOptions.slice(); optA4[sizeIndexes[0]] = `${jacketSize}-${lengthSuffix}`; attempts.push(optA4); }
  // Try separate size + length  
  if (sizeIndexes.length >= 1) { const optB = baseOptions.slice(); optB[sizeIndexes[0]] = String(jacketSize); if (sizeIndexes.length >= 2) optB[sizeIndexes[1]] = lengthKey; attempts.push(optB); }
  // Try separate with suffix
  if (sizeIndexes.length >= 2) { const optB2 = baseOptions.slice(); optB2[sizeIndexes[0]] = String(jacketSize); optB2[sizeIndexes[1]] = lengthSuffix; attempts.push(optB2); }
  // Try just the code in each size slot
  sizeIndexes.forEach((idx) => { const opt = baseOptions.slice(); opt[idx] = jacketCode; attempts.push(opt); });
  
  const variants = product.variants || [];
  const equalsOptions = (a, b) => { if (!a || !b || a.length !== b.length) return false; for (let i = 0; i < a.length; i++) { if (normalizeValue(a[i]) !== normalizeValue(b[i])) return false; } return true; };
  
  for (const opts of attempts) { const hit = variants.find((v) => equalsOptions(v.options || [], opts)); if (hit) return hit.id; }
  
  // Loose match: variant options contain the jacket code exactly
  const hitLoose = variants.find((v) => { const vopts = (v.options || []).map(normalizeValue); return vopts.includes(normalizeValue(jacketCode)); });
  if (hitLoose) return hitLoose.id;
  
  // Loose match: variant option contains size + length suffix anywhere
  const sizeStr = String(jacketSize);
  const hitLoose2 = variants.find((v) => { 
    const vopts = (v.options || []).map(normalizeValue); 
    return vopts.some(opt => opt.includes(sizeStr) && opt.includes(lengthSuffix.toLowerCase())); 
  });
  if (hitLoose2) return hitLoose2.id;
  
  // Variant title match: check if variant title contains "50L" or "50 L" etc.
  const hitTitle = variants.find((v) => {
    const title = normalizeValue(v.title || "");
    return title.includes(normalizeValue(jacketCode)) || 
           title.includes(normalizeValue(`${jacketSize} ${lengthSuffix}`)) ||
           title.includes(normalizeValue(`${jacketSize}${lengthSuffix}`));
  });
  if (hitTitle) return hitTitle.id;
  
  // Last resort: find any variant with matching size number and length indicator
  const hitNumeric = variants.find((v) => { 
    const joined = normalizeValue((v.options || []).join(" ") + " " + (v.title || ""));
    const hasSize = joined.includes(sizeStr);
    const hasLength = joined.includes(lengthSuffix.toLowerCase()) || joined.includes(lengthFull.toLowerCase());
    return hasSize && hasLength;
  });
  if (hitNumeric) return hitNumeric.id;
  
  // Debug log to help troubleshoot
  console.warn("[ZB FIT] Could not find variant for:", { jacketCode, jacketSize, lengthKey });
  console.warn("[ZB FIT] Available variants:", variants.map(v => ({ id: v.id, title: v.title, options: v.options })));
  
  return null;
}

// ============================================
// CART INTEGRATION
// ============================================
async function addToShopifyCart(variantId, quantity = 1, properties = {}) {
  const formData = { items: [{ id: variantId, quantity, properties }] };
  const res = await fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(formData) });
  if (!res.ok) throw new Error(`Cart add failed (${res.status})`);
  const cart = await res.json();
  document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail: { cart } }));
  document.dispatchEvent(new CustomEvent("theme:cart:changed"));
  return cart;
}

// ============================================
// DEFAULT MEASUREMENTS FROM CHART - FULL FIELDS
// ============================================
function makeDefaultMeasurementsFromChart({ jacketSize, lengthKey }, isSuit, category = 'sb_suit') {
  const lk = getLengthKey(lengthKey);
  const jacketChart = getJacketChart(category);
  const trouserChart = getTrouserChart(category);
  const jacket = jacketChart[lk]?.[jacketSize] || null;
  const trouser = trouserChart[lk]?.[jacketSize - 6] || null;
  
  return {
    unit: "in",
    jacket: { 
      chest: jacket?.chest ?? null, 
      waist: jacket?.waist ?? null, 
      length: jacket?.length ?? null, 
      sleeve: jacket?.sleeve ?? null,
      hip: jacket?.hip ?? null,
      shoulder: jacket?.shoulder ?? null,
      back: jacket?.back ?? null,
      vent: jacket?.vent ?? null,
      frontLength: jacket?.frontLength ?? null,
      bicep: jacket?.bicep ?? null,
      sleeveOpening: jacket?.sleeveOpening ?? null
    },
    trouser: isSuit ? { 
      waist: trouser?.waist ?? null, 
      hip: trouser?.hip ?? null,  // NEW
      thigh: trouser?.thigh ?? null,
      knee: trouser?.knee ?? null,
      hem: trouser?.hem ?? null,
      inseam: trouser?.inseam ?? null,
      rise: trouser?.rise ?? null,
      backRise: trouser?.backRise ?? null
    } : null,
  };
}

// ============================================
// BODY CHEST ESTIMATION + ANCHORED RECOMMENDER
// ============================================
// ============================================
// V3 MULTIVARIATE BODY METRICS ESTIMATOR (VERIFIED PHYSICS)
// Inputs: Height, Weight, Age, Shoe Size, Shape
// Returns: { bodyChest, bodyWaist, bodyHip, bodyNeck, bodyThigh } - BODY measurements in INCHES
// Calibrated to separate "Lean Athletic" (38R) from "Mass Athletic" (40R)
// ============================================
function estimateMetricsV3({ heightCm, weightKg, age, shoe, shape }) {
  const height = Number(heightCm);
  const weight = Number(weightKg);
  const userAge = Number(age) || 35;
  const userShoe = Number(shoe) || 10;
  const bodyShape = shape || "Rectangle";
  
  // 1. Frame Factor
  const expectedShoe = (height - 140) / 4.5;
  const frameMult = 1 + ((userShoe - expectedShoe) * 0.015);
  
  // 2. Base Physics
  // Chest: (0.7 * W) + (0.25 * H). 72kg/183cm → 96.15cm (37.8")
  let baseChestCm = (0.7 * weight) + (0.25 * height);
  
  // Waist: (0.95 * W) + 12. 72kg → 80.4cm (31.6")
  let baseWaistCm = (0.95 * weight) + 12;
  
  // Thigh: (0.28 * W) + 40. 72kg → 60.16cm (23.6")
  const baseThighCm = (0.28 * weight) + 40;
  
  // Neck: Skin-First. 72kg → 37cm (14.6")
  const baseNeckCm = 34 + ((weight - 60) * 0.25);
  
  // NEW: Hip/Seat Estimation (Waist + ~16cm/6.3")
  // Matches garment drop (e.g., Waist 34 → Hip ~41")
  const baseHipCm = baseWaistCm + 16;
  
  // 3. SHAPE MODIFIERS (Calibrated)
  // Inverted: +4cm Chest to ensure 72kg hits 38R (not 36R)
  if (bodyShape === "Inverted Triangle" || bodyShape === "Inverted") {
    baseChestCm += 4;    // +1.6" chest (V-shape/swimmer build)
    baseWaistCm -= 2;    // Narrower waist
  } else if (bodyShape === "Rectangle") {
    baseWaistCm += 2;    // Slightly wider waist
  } else if (bodyShape === "Triangle") {
    baseWaistCm += 4;    // Wider waist (pear shape)
  }
  // Oval shape: no modifier (default proportions)
  
  // 4. Age Adjustments (REDUCED from 0.5 to 0.3 to prevent oversizing)
  const chestAdj = Math.max(0, userAge - 35) * 0.3;
  const waistAdj = Math.max(0, userAge - 28) * 0.5;
  
  // 5. Apply adjustments
  const finalChestCm = (baseChestCm + chestAdj) * frameMult;
  const finalWaistCm = baseWaistCm + waistAdj;
  const finalHipCm = baseHipCm + waistAdj; // Hip grows with waist adjustment
  const finalThighCm = baseThighCm; // Thigh not affected by frame/age
  const finalNeckCm = baseNeckCm;   // Neck not affected by frame
  
  // 6. Convert to INCHES
  return {
    bodyChest: round1(finalChestCm / 2.54),
    bodyWaist: round1(finalWaistCm / 2.54),
    bodyHip: round1(finalHipCm / 2.54),   // NEW FIELD
    bodyThigh: round1(finalThighCm / 2.54),
    bodyNeck: round1(finalNeckCm / 2.54)
  };
}

// Legacy wrapper for backward compatibility
function estimateBodyChestIn({ heightCm, weightKg, age, shoe }) {
  const metrics = estimateMetricsV3({ heightCm, weightKg, age: age || 35, shoe: shoe || 10 });
  return metrics.bodyChest;
}

// Jacket ease (Standard Industry Mapping)
function getSuitEaseIn(fitPref) {
  if (fitPref === "Slim") return 2.0;
  if (fitPref === "Classic") return 4.0;
  return 3.0; // Tailored default
}

function getWaistcoatEaseIn(fitPref) {
  // Waistcoat ease is smaller (+1.5" for Tailored vs +3" for jackets)
  if (fitPref === "Slim") return 1.0;
  if (fitPref === "Classic") return 2.0;
  return 1.5; // Tailored default
}

// ============================================
// SIZE SELECTION - "TIMES 2" RULE
// Chart values are FLAT WIDTHS (half measurements)
// Body values are FULL CIRCUMFERENCE
// chartCircumference = chartValue * 2
// NO BODY SHAPE MODIFIERS - Pure Physics!
// ============================================
function chooseSizeFromChest({ bodyChestIn, fitPref, category }) {
  const isWaistcoat = category === "waistcoat";
  const ease = isWaistcoat ? getWaistcoatEaseIn(fitPref) : getSuitEaseIn(fitPref);
  
  // Target body chest (no shape modifiers - physics is physics)
  const targetBodyChest = round1(bodyChestIn);
  
  // Target garment circumference = body + ease
  const targetGarmentChest = targetBodyChest + ease;
  
  const sizes = [32, 34, 36, 38, 40, 42, 44, 46, 48, 50];
  const chart = isWaistcoat ? WAISTCOAT_CHART_IN.Regular : SB_JACKET_CHART_IN.Regular;
  
  // Chart values are FLAT WIDTH (half), so multiply by 2 for circumference
  const minChartHalf = chart[32]?.chest ?? 17.6;
  const maxChartHalf = chart[50]?.chest ?? 24.6;
  const minSupported = round1(minChartHalf * 2 - ease); // Min body chest we can fit
  const maxSupported = round1(maxChartHalf * 2 - ease); // Max body chest we can fit

  if (targetBodyChest < minSupported) {
    return { jacketSize: 32, easeIn: ease, bodyChestIn: targetBodyChest, outOfRange: true, outOfRangeDir: "low", supportedMinBodyChestIn: minSupported, supportedMaxBodyChestIn: maxSupported };
  }
  if (targetBodyChest > maxSupported) {
    return { jacketSize: 50, easeIn: ease, bodyChestIn: targetBodyChest, outOfRange: true, outOfRangeDir: "high", supportedMinBodyChestIn: minSupported, supportedMaxBodyChestIn: maxSupported };
  }

  let bestSize = 38;
  let bestDiff = Infinity;

  for (const s of sizes) {
    const chartHalfWidth = chart[s]?.chest;
    if (chartHalfWidth == null) continue;
    
    // "TIMES 2" RULE: Convert chart flat width to full circumference
    const chartCircumference = chartHalfWidth * 2;
    
    // Find size where garment circumference best matches target
    const diff = Math.abs(chartCircumference - targetGarmentChest);
    
    // Prefer smaller size for Slim/Tailored, larger for Classic
    const preferSmaller = fitPref !== "Classic";
    const isTie = diff === bestDiff;
    const winsTie = preferSmaller ? (s < bestSize) : (s > bestSize);
    
    if (diff < bestDiff || (isTie && winsTie)) { 
      bestDiff = diff; 
      bestSize = s; 
    }
  }

  // Slim fit preference: Consider sizing down if within tolerance
  if (fitPref === "Slim") {
    const idx = sizes.indexOf(bestSize);
    const down = idx > 0 ? sizes[idx - 1] : null;
    if (down != null) {
      const downCircumference = (chart[down]?.chest ?? 0) * 2;
      const slimTolerance = 1.0; // Allow 1" tighter fit for slim
      if (downCircumference >= targetGarmentChest - slimTolerance) { 
        bestSize = down; 
      }
    }
  }

  return { jacketSize: bestSize, easeIn: ease, bodyChestIn: targetBodyChest, outOfRange: false, outOfRangeDir: null, supportedMinBodyChestIn: minSupported, supportedMaxBodyChestIn: maxSupported };
}

// ============================================
// SHIRT SIZING - Tiered Ease with Safety Floor
// Neck is already circumference (NO "Times 2")
// ============================================
function getShirtEaseIn(fitPref) {
  // Tiered ease values for shirt collar
  if (fitPref === "Slim") return 0.25;    // Aggressive/Model fit
  if (fitPref === "Classic") return 1.0;  // Comfort
  return 0.75; // Tailored (balanced standard)
}

function chooseShirtSizeFromNeck({ bodyNeckIn, lengthKey, fitPref }) {
  const lk = getLengthKey(lengthKey);
  const collarSizes = ["13.5", "14.0", "14.5", "15.0", "15.5", "15.75", "16.0", "16.5", "17.0", "17.5", "17.75", "18.0"];
  
  // Step A: Get ease based on fit preference
  const ease = getShirtEaseIn(fitPref);
  
  // Step B: Calculate target neck (body neck + ease)
  const targetNeck = bodyNeckIn + ease;
  
  // Step C: Snap to closest collar size
  let bestCollar = "15.0";
  let bestDiff = Infinity;
  
  for (const collar of collarSizes) {
    const collarNum = parseFloat(collar);
    const diff = Math.abs(collarNum - targetNeck);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestCollar = collar;
    }
  }
  
  // Step D: Safety Floor - Prevent choking
  // If selected size minus body neck < 0.25", force size up
  const selectedNum = parseFloat(bestCollar);
  if (selectedNum - bodyNeckIn < 0.25) {
    const idx = collarSizes.indexOf(bestCollar);
    if (idx < collarSizes.length - 1) {
      bestCollar = collarSizes[idx + 1]; // Force size up
    }
  }
  
  return parseFloat(bestCollar);
}

// Thigh ease based on fit preference (for trousers)
function getThighEaseIn(fitPref) {
  if (fitPref === "Slim") return 1.5;
  if (fitPref === "Classic") return 2.5;
  return 2.0; // Tailored default
}

// Waist ease for trousers
function getTrouserWaistEaseIn(fitPref) {
  if (fitPref === "Slim") return 0.0;
  if (fitPref === "Classic") return 1.0;
  return 0.5; // Tailored default
}

// Validate trouser fit against Drop-6 implied size (TRIPLE CHECK: Waist, Thigh, Hip)
function validateTrouserFit({ impliedWaistSize, bodyWaistIn, bodyThighIn, bodyHipIn, fitPref, lengthKey }) {
  const lk = getLengthKey(lengthKey);
  const chart = SB_TROUSER_CHART_IN[lk];
  if (!chart) return { fits: true, fitNote: null, idealCustomSpecs: null };
  
  // Fixed ease values for trouser validation
  const waistEase = 0.5;  // Standard waist ease
  const thighEase = 2.0;  // Standard thigh ease (Tailored)
  const hipEase = 2.0;    // Standard seat/hip ease (Tailored)
  
  // Required CIRCUMFERENCE measurements (body + ease)
  const requiredWaistCirc = bodyWaistIn + waistEase;
  const requiredThighCirc = bodyThighIn + thighEase;
  const requiredHipCirc = bodyHipIn + hipEase;  // NEW
  
  // Get chart values for implied size
  const chartData = chart[impliedWaistSize];
  if (!chartData) return { fits: true, fitNote: null, idealCustomSpecs: null };
  
  // Convert chart flat width to circumference ("Times 2" rule)
  const chartWaistCirc = chartData.waist * 2;
  const chartThighCirc = chartData.thigh * 2;
  const chartHipCirc = chartData.hip * 2;  // NEW
  
  // THE TRIPLE CHECK
  const waistFits = chartWaistCirc >= requiredWaistCirc;
  const thighFits = chartThighCirc >= requiredThighCirc;
  const hipFits = chartHipCirc >= requiredHipCirc;  // NEW
  
  if (waistFits && thighFits && hipFits) {
    return { fits: true, fitNote: null, idealCustomSpecs: null };
  }
  
  // Calculate GARMENT FLAT WIDTHS for custom specs (Body + Ease) / 2
  // These match the CSV column format
  const idealCustomSpecs = {
    waist: round1(requiredWaistCirc / 2),  // Garment flat width
    hip: round1(requiredHipCirc / 2),      // NEW
    thigh: round1(requiredThighCirc / 2),  // Garment flat width
    knee: chartData.knee,                   // Keep standard
    hem: chartData.hem,
    inseam: chartData.inseam,
    rise: chartData.rise,
    backRise: chartData.backRise
  };
  
  const fitNote = `Standard size may feel tight in the seat or thigh. Made to Measure resolves this while keeping your base size.`;
  
  return { fits: false, fitNote, idealCustomSpecs };
}

function recommendFromBody({ heightCm, weightKg, shoe, age }, { bodyShape, fitPref, category }) {
  const userAge = age || 35;
  // Pass bodyShape to estimateMetricsV3 for shape modifiers
  const metrics = estimateMetricsV3({ heightCm, weightKg, age: userAge, shoe, shape: bodyShape });
  
  // Determine length key based on height
  let lengthKey = "Regular";
  if (heightCm < 168) lengthKey = "Short";
  if (heightCm > 185) lengthKey = "Long";
  
  const isShirt = category === "shirt";
  const isWaistcoat = category === "waistcoat";
  const isSuit = category === "sb_suit" || category === "db_suit";
  
  // For shirts, use neck-based sizing with tiered ease
  if (isShirt) {
    // Shirts are only cut in Regular and Long; never recommend Short.
    if (lengthKey === "Short") lengthKey = "Regular";
    const ease = getShirtEaseIn(fitPref);
    const collarSize = chooseShirtSizeFromNeck({ bodyNeckIn: metrics.bodyNeck, lengthKey, fitPref });
    return { 
      jacketSize: collarSize, 
      trouserSize: null,
      lengthKey, 
      bodyChestIn: metrics.bodyChest, 
      bodyWaistIn: metrics.bodyWaist,
      bodyNeckIn: metrics.bodyNeck,
      bodyThighIn: metrics.bodyThigh,
      easeIn: ease, 
      outOfRange: false, 
      outOfRangeDir: null,
      supportedMinBodyChestIn: null,
      supportedMaxBodyChestIn: null,
      fitNote: null,
      idealCustomSpecs: null
    };
  }
  
  // For all other categories, use chest-based sizing
  const bmi = weightKg / Math.pow(heightCm / 100, 2);
  let bmiAdj = 0;
  if (bmi < 18.5) bmiAdj -= 0.6;
  else if (bmi > 30) bmiAdj += 0.8;
  else if (bmi > 26.5) bmiAdj += 0.4;

  // Step A: Calculate Jacket Size (The Anchor) - NEVER changes based on trousers
  const chosen = chooseSizeFromChest({ 
    bodyChestIn: round1(metrics.bodyChest + bmiAdj), 
    fitPref, 
    category: isWaistcoat ? "waistcoat" : "suit"
  });

  // Step B & C: For suits, validate trouser fit using Drop-6 assumption
  let trouserResult = { fits: true, fitNote: null, idealCustomSpecs: null };
  let impliedTrouserSize = null;
  
  if (isSuit) {
    // Drop-6: Trouser size = Jacket size - 6
    impliedTrouserSize = chosen.jacketSize - 6;
    
    // Validate if the implied trouser size fits (TRIPLE CHECK: Waist, Thigh, Hip)
    trouserResult = validateTrouserFit({
      impliedWaistSize: impliedTrouserSize,
      bodyWaistIn: metrics.bodyWaist,
      bodyThighIn: metrics.bodyThigh,
      bodyHipIn: metrics.bodyHip,  // NEW: Pass hip for seat validation
      fitPref,
      lengthKey
    });
  }

  return { 
    jacketSize: chosen.jacketSize, 
    trouserSize: impliedTrouserSize, // Drop-6 implied size
    lengthKey, 
    bodyChestIn: chosen.bodyChestIn, 
    bodyWaistIn: metrics.bodyWaist,
    bodyHipIn: metrics.bodyHip,  // NEW: Include in return
    bodyNeckIn: metrics.bodyNeck,
    bodyThighIn: metrics.bodyThigh,
    easeIn: chosen.easeIn, 
    outOfRange: chosen.outOfRange, 
    outOfRangeDir: chosen.outOfRangeDir, 
    supportedMinBodyChestIn: chosen.supportedMinBodyChestIn, 
    supportedMaxBodyChestIn: chosen.supportedMaxBodyChestIn,
    fitNote: trouserResult.fitNote,
    idealCustomSpecs: trouserResult.idealCustomSpecs,
    trouserFits: trouserResult.fits
  };
}

// ============================================
// END OF PART 2 - Continue to Part 3
// ============================================
// ============================================
// ZANE BARLAS FIT ENGINE V2 - PART 3 OF 5
// UI Components: InlineInfo, DetailsModal, SizeGuideModal
// COMPLETE MEASUREMENT FIELDS PER CATEGORY
// ============================================

const InlineInfo = ({ title, text }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => { if (!e.target.closest('.zb-info-popup') && !e.target.closest('.zb-info-btn')) setOpen(false); };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);
  return html`
    <span style=${ { position: "relative", display: "inline-flex", alignItems: "center" } }>
      <button type="button" className="zb-info-btn" onClick=${(e) => { e.stopPropagation(); setOpen(!open); }} style=${ { width: "18px", height: "18px", borderRadius: "999px", border: "1px solid rgba(0,0,0,.12)", background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "600", cursor: "pointer", marginLeft: "6px", color: "rgba(0,0,0,0.4)" } }>i</button>
      ${open && html`
        <div className="zb-info-popup" onClick=${(e) => e.stopPropagation()} style=${ { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "280px", maxWidth: "90vw", background: "#111", color: "#fff", padding: "16px", borderRadius: "10px", zIndex: 99999999, boxShadow: "0 12px 40px rgba(0,0,0,.4)" } }>
          <div style=${ { fontSize: "11px", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700, opacity: 0.85 } }>${title}</div>
          <div style=${ { marginTop: "8px", fontSize: "13px", lineHeight: "1.45", opacity: 0.9 } }>${text}</div>
          <button type="button" onClick=${() => setOpen(false)} style=${ { marginTop: "12px", width: "100%", border: 0, background: "#fff", color: "#111", padding: "10px", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "11px" } }>Close</button>
        </div>
      `}
    </span>
  `;
};

// ============================================
// DETAILS MODAL - Full Specs View
// ============================================
const DetailsModal = ({ open, onClose, title, product, selected, originalUnit }) => {
  if (!open) return null;
  const [unit, setUnit] = useState(originalUnit || "in");
  const category = getProductCategory(product);
  const lk = getLengthKey(selected.lengthKey);
  const isSuit = category === "sb_suit" || category === "db_suit";
  const isJacketOnly = category === "sb_jacket" || category === "db_jacket";
  const isShirt = category === "shirt";
  const isPeacoat = category === "peacoat";
  const isOvercoat = category === "sb_overcoat" || category === "db_overcoat";
  const isWaistcoat = category === "waistcoat";
  
  // Get specs based on category
  const specs = useMemo(() => {
    if (!selected.jacketSize) return null;
    if (isOvercoat) return getOvercoatChart(category)[lk]?.[selected.jacketSize];
    if (isPeacoat) return PEACOAT_CHART_IN[lk]?.[selected.jacketSize];
    if (isWaistcoat) return WAISTCOAT_CHART_IN[lk]?.[selected.jacketSize];
    if (isShirt) return SHIRT_CHART_IN[lk]?.[selected.jacketSize];
    return getJacketChart(category)[lk]?.[selected.jacketSize];
  }, [selected, category, lk]);

  // Get trouser specs for suits ONLY (not jackets)
  const trouserSpecs = useMemo(() => {
    if (!selected.jacketSize || !isSuit) return null;
    const trouserSize = selected.jacketSize - 6;
    const trouserChart = getTrouserChart(category);
    return trouserChart[lk]?.[trouserSize];
  }, [selected, category, lk, isSuit]);

  // Build rows
  const rows = useMemo(() => {
    if (!specs) return [];
    if (isShirt) {
      return [
        { label: "Collar Around", val: specs.collar },
        { label: "Chest Width", val: specs.chest },
        { label: "Shoulder Width", val: specs.shoulder },
        { label: "Waist Width", val: specs.waist },
        { label: "Back Length", val: specs.length },
        { label: "Sleeve Length", val: specs.sleeveRight }
      ];
    }
    if (isWaistcoat) {
      return [
        { label: "Chest Width", val: specs.chest },
        { label: "Waist Width", val: specs.waist },
        { label: "Hip Width", val: specs.hip },
        { label: "Waistcoat Length", val: specs.length }
      ];
    }
    if (isOvercoat || isPeacoat) {
      return [
        { label: "Chest Width", val: specs.chest },
        { label: "Waist Width", val: specs.waist },
        { label: "Shoulder Width", val: specs.shoulder },
        { label: "Sleeve Length", val: specs.sleeve },
        { label: "Coat Length", val: specs.length }
      ];
    }
    // JACKET/SUIT
    return [
      { label: "Chest Width", val: specs.chest },
      { label: "Waist Width", val: specs.waist },
      { label: "Shoulder Width", val: specs.shoulder },
      { label: "Sleeve Length", val: specs.sleeve },
      { label: "Jacket Length", val: specs.length }
    ];
  }, [specs, category, isShirt, isWaistcoat, isOvercoat, isPeacoat]);

  // Trouser rows
  const trouserRows = useMemo(() => {
    if (!trouserSpecs || !isSuit) return [];
    return [
      { label: "Waist Width", val: trouserSpecs.waist },
      { label: "Hip Width", val: trouserSpecs.hip },
      { label: "Inside-leg Length", val: trouserSpecs.inseam },
      { label: "Thigh Width", val: trouserSpecs.thigh },
      { label: "Hem Width", val: trouserSpecs.hem }
    ];
  }, [trouserSpecs, isSuit]);

  const getItemLabel = () => {
    if (isShirt) return 'SHIRT';
    if (isWaistcoat) return 'WAISTCOAT';
    if (isPeacoat) return 'PEACOAT';
    if (isOvercoat) return 'OVERCOAT';
    if (isJacketOnly) return 'JACKET';
    return 'JACKET';
  };
  
  // Helper to render a safe row that handles long text
  const renderRow = (label, val, isLast) => html`
    <div style=${{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderBottom: isLast ? "none" : "1px solid rgba(0,0,0,.08)" }}>
      <div style=${{ fontSize: "11px", letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 900, color: "rgba(0,0,0,.55)", flex: 1, paddingRight: "12px", lineHeight: "1.3" }}>
        ${label}
      </div>
      <div style=${{ fontSize: "13px", fontWeight: 900, flexShrink: 0, textAlign: "right" }}>
        ${formatMeasure(val, unit)} ${unit}
      </div>
    </div>
  `;
  
  return html`
    <div style=${ { position: "fixed", inset: 0, zIndex: 9999999 } }>
      <div onClick=${onClose} style=${ { position: "absolute", inset: 0, background: "rgba(0,0,0,.28)" } }></div>
      <div style=${ { position: "absolute", right: 0, top: 0, height: "100%", width: "min(520px, 100%)", background: "#fff", boxShadow: "-24px 0 60px rgba(0,0,0,.25)", display: "flex", flexDirection: "column" } }>
        <div style=${ { padding: "18px 22px", borderBottom: "1px solid rgba(0,0,0,.08)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 } }>
          <div style=${ { fontSize: "12px", letterSpacing: ".18em", textTransform: "uppercase", fontWeight: 900 } }>${title}</div>
          <div style=${{ display: "flex", alignItems: "center", gap: "12px" }}>
            <${SegToggle} left=${ { label: "IN", value: "in" } } right=${ { label: "CM", value: "cm" } } value=${unit} onChange=${setUnit} />
            <button type="button" onClick=${onClose} style=${ { border: 0, background: "transparent", cursor: "pointer", fontSize: "18px" } }>✕</button>
          </div>
        </div>
        <div style=${ { padding: "18px 22px 40px 22px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column" } }>
          ${rows.length > 0 ? html`
            <div style=${ { fontSize: "11px", fontWeight: 900, marginBottom: "10px", opacity: 0.5, letterSpacing: ".12em", textTransform: "uppercase" } }>${getItemLabel()}</div>
            <div style=${ { border: "1px solid rgba(0,0,0,.10)", borderRadius: "10px", marginBottom: isSuit ? "20px" : "0" } }>
              ${rows.map((r, idx) => html`<div key=${idx}>${renderRow(r.label, r.val, idx === rows.length - 1)}</div>`)}
            </div>
            ${isSuit && trouserRows.length > 0 && html`
              <div style=${ { fontSize: "11px", fontWeight: 900, marginBottom: "10px", opacity: 0.5, letterSpacing: ".12em", textTransform: "uppercase" } }>TROUSER</div>
              <div style=${ { border: "1px solid rgba(0,0,0,.10)", borderRadius: "10px" } }>
                ${trouserRows.map((r, idx) => html`<div key=${idx}>${renderRow(r.label, r.val, idx === trouserRows.length - 1)}</div>`)}
              </div>
            `}
          ` : html`
            <div style=${ { background: "#000", color: "#fff", padding: "10px 20px", borderRadius: "20px", fontWeight: 900, fontSize: "12px", letterSpacing: ".05em", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" } }>
              Please select a size.
            </div>
          `}
        </div>
        <div className="zane-fit-footer-glass" style=${ { padding: "18px 22px", flexShrink: 0 } }><button type="button" className="zane-fit-back" onClick=${onClose} style=${{ width: "100%" }}>Done</button></div>
      </div>
    </div>
  `;
};

// ============================================
// SIZE GUIDE MODAL - Full Size Charts with Interactive Highlighting
// ============================================
const SizeGuideModal = ({ open, onClose, product }) => {
  if (!open) return null;
  const [unit, setUnit] = useState("in");
  const [lengthFilter, setLengthFilter] = useState("Regular");
  const [hoverCell, setHoverCell] = useState({ row: null, col: null });
  const category = getProductCategory(product);
  const jacketSizes = [32, 34, 36, 38, 40, 42, 44, 46, 48, 50];
  const trouserSizes = [26, 28, 30, 32, 34, 36, 38, 40, 42, 44];
  const shirtSizes = ["13.5", "14.0", "14.5", "15.0", "15.5", "15.75", "16.0", "16.5", "17.0", "17.5", "17.75", "18.0"];
  const waistcoatSizes = [32, 34, 36, 38, 40, 42, 44, 46, 48, 50];
  
  const isSuit = category === "sb_suit" || category === "db_suit";
  const isJacketOnly = category === "sb_jacket" || category === "db_jacket";
  const isShirt = category === "shirt";
  const isPeacoat = category === "peacoat";
  const isOvercoat = category === "sb_overcoat" || category === "db_overcoat";
  const isWaistcoat = category === "waistcoat";
  
  const jacketChart = getJacketChart(category);
  const trouserChart = getTrouserChart(category);
  const overcoatChart = getOvercoatChart(category);

  const getLengthSuffix = (len) => len === "Short" ? "S" : len === "Long" ? "L" : "R";
  const showLengthSelector = true;

  // Interactive cell styles
  const getCellStyle = (rowIdx, colIdx, isFirst = false) => {
    const isRowHighlight = hoverCell.row === rowIdx;
    const isColHighlight = hoverCell.col === colIdx;
    const isActive = isRowHighlight && isColHighlight;
    
    return {
      padding: "10px 6px",
      textAlign: isFirst ? "left" : "center",
      fontSize: "11px",
      fontWeight: isFirst ? 700 : 400,
      paddingLeft: isFirst ? "12px" : "6px",
      background: isActive ? "rgba(0,0,0,0.08)" : isRowHighlight || isColHighlight ? "rgba(0,0,0,0.03)" : "transparent",
      transition: "background 0.15s ease",
      cursor: "default"
    };
  };

  const getHeaderStyle = (colIdx) => ({
    padding: "12px 6px",
    textAlign: "center",
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    background: hoverCell.col === colIdx ? "rgba(0,0,0,0.06)" : "#f8f8f8",
    transition: "background 0.15s ease",
    position: "sticky",
    top: 0
  });

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "11px",
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid rgba(0,0,0,0.08)"
  };

  const renderTable = (headers, rows, tableId) => html`
    <div style=${{ overflowX: "auto", marginBottom: "24px" }}>
      <table style=${tableStyle} onMouseLeave=${() => setHoverCell({ row: null, col: null })}>
        <thead>
          <tr>
            ${headers.map((h, colIdx) => html`
              <th key=${colIdx} style=${getHeaderStyle(colIdx)}>${h}</th>
            `)}
          </tr>
        </thead>
        <tbody>
          ${rows.map((row, rowIdx) => html`
            <tr key=${rowIdx} style=${{ background: rowIdx % 2 === 0 ? "#fff" : "#fafafa" }}>
              ${row.map((cell, colIdx) => html`
                <td 
                  key=${colIdx}
                  style=${getCellStyle(rowIdx, colIdx, colIdx === 0)}
                  onMouseEnter=${() => setHoverCell({ row: rowIdx, col: colIdx })}
                  onTouchStart=${() => setHoverCell({ row: rowIdx, col: colIdx })}
                >
                  ${cell}
                </td>
              `)}
            </tr>
          `)}
        </tbody>
      </table>
    </div>
  `;
  
  return html`
    <div style=${ { position: "fixed", inset: 0, zIndex: 999999999 } }>
      <div onClick=${onClose} style=${ { position: "absolute", inset: 0, background: "rgba(0,0,0,.35)" } }></div>
      <div style=${ { position: "absolute", right: 0, top: 0, height: "100%", width: "min(700px, 100%)", background: "#fff", boxShadow: "-24px 0 60px rgba(0,0,0,.25)", display: "flex", flexDirection: "column" } }>
        <div style=${ { padding: "18px 22px", borderBottom: "1px solid rgba(0,0,0,.08)", display: "flex", alignItems: "center", justifyContent: "space-between" } }>
          <div style=${ { fontSize: "15px", letterSpacing: ".12em", textTransform: "uppercase", fontWeight: 700, fontFamily: "var(--zb-font-heading)" } }>Size Guide</div>
          <div style=${{ display: "flex", alignItems: "center", gap: "12px" }}>
            <${SegToggle} left=${ { label: "IN", value: "in" } } right=${ { label: "CM", value: "cm" } } value=${unit} onChange=${setUnit} />
            <button type="button" onClick=${onClose} style=${ { border: 0, background: "transparent", cursor: "pointer", fontSize: "18px" } }>✕</button>
          </div>
        </div>
        <div style=${ { flex: 1, overflow: "auto", padding: "18px 22px" } }>
          ${showLengthSelector && html`
            <div style=${ { marginBottom: "20px" } }>
              <div style=${ { fontSize: "13px", fontWeight: 700, marginBottom: "12px" } }>Length</div>
              <div className="zane-fit-seg" style=${{ width: "100%", background: "#f1f1f1", borderRadius: "18px", padding: "4px", display: "flex", gap: "4px" }}>
                <button type="button" style=${{ flex: 1 }} onClick=${() => setLengthFilter("Short")} className=${lengthFilter === "Short" ? "is-active" : ""}>
                  <div style=${{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}><span style=${{ fontWeight: 700 }}>Short</span><span style=${{ fontSize: "11px", opacity: 0.6 }}>${"<"} 5'5"</span></div>
                </button>
                <button type="button" style=${{ flex: 1 }} onClick=${() => setLengthFilter("Regular")} className=${lengthFilter === "Regular" ? "is-active" : ""}>
                  <div style=${{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}><span style=${{ fontWeight: 700 }}>Regular</span><span style=${{ fontSize: "11px", opacity: 0.6 }}>5'5" – 6'1"</span></div>
                </button>
                <button type="button" style=${{ flex: 1 }} onClick=${() => setLengthFilter("Long")} className=${lengthFilter === "Long" ? "is-active" : ""}>
                  <div style=${{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}><span style=${{ fontWeight: 700 }}>Long</span><span style=${{ fontSize: "11px", opacity: 0.6 }}>${">"} 6'1"</span></div>
                </button>
              </div>
            </div>
          `}
          
          ${isShirt && html`
            <h3 style=${ { fontSize: "13px", fontWeight: 700, marginBottom: "12px", fontFamily: "var(--zb-font-heading)" } }>Shirt Measurements (${unit})</h3>
            ${renderTable(
              ["Collar", "Chest", "Shoulder", "Waist", "Back Length", "Sleeve"],
              shirtSizes.map(size => {
                const d = SHIRT_CHART_IN[lengthFilter]?.[size];
                if (!d) return null;
                return [
                  size + getLengthSuffix(lengthFilter),
                  formatMeasure(d.chest, unit),
                  formatMeasure(d.shoulder, unit),
                  formatMeasure(d.waist, unit),
                  formatMeasure(d.length, unit),
                  formatMeasure(d.sleeveRight, unit)
                ];
              }).filter(Boolean),
              "shirt"
            )}
          `}
          
          ${isWaistcoat && html`
            <h3 style=${ { fontSize: "13px", fontWeight: 700, marginBottom: "12px", fontFamily: "var(--zb-font-heading)" } }>Waistcoat Measurements (${unit})</h3>
            ${renderTable(
              ["Size", "Chest", "Waist", "Hip", "Length"],
              waistcoatSizes.map(size => {
                const d = WAISTCOAT_CHART_IN[lengthFilter]?.[size];
                if (!d) return null;
                return [
                  size + getLengthSuffix(lengthFilter),
                  formatMeasure(d.chest, unit),
                  formatMeasure(d.waist, unit),
                  formatMeasure(d.hip, unit),
                  formatMeasure(d.length, unit)
                ];
              }).filter(Boolean),
              "waistcoat"
            )}
          `}
          
          ${(isSuit || isJacketOnly) && html`
            <h3 style=${ { fontSize: "13px", fontWeight: 700, marginBottom: "12px", fontFamily: "var(--zb-font-heading)" } }>${category.includes('db') ? 'Double Breast' : 'Single Breast'} Jacket (${unit})</h3>
            ${renderTable(
              ["Size", "Chest", "Waist", "Shoulder", "Sleeve", "Length"],
              jacketSizes.map(size => {
                const d = jacketChart[lengthFilter]?.[size];
                if (!d) return null;
                return [
                  size + getLengthSuffix(lengthFilter),
                  formatMeasure(d.chest, unit),
                  formatMeasure(d.waist, unit),
                  formatMeasure(d.shoulder, unit),
                  formatMeasure(d.sleeve, unit),
                  formatMeasure(d.length, unit)
                ];
              }).filter(Boolean),
              "jacket"
            )}
            
            ${isSuit && html`
              <h3 style=${ { fontSize: "13px", fontWeight: 700, marginBottom: "12px", fontFamily: "var(--zb-font-heading)" } }>Trouser Measurements (${unit})</h3>
              ${renderTable(
                ["Size", "Waist", "Inside Leg", "Upper Leg", "Hem"],
                trouserSizes.map(size => {
                  const d = trouserChart[lengthFilter]?.[size];
                  if (!d) return null;
                  return [
                    size + getLengthSuffix(lengthFilter),
                    formatMeasure(d.waist, unit),
                    formatMeasure(d.inseam, unit),
                    formatMeasure(d.thigh, unit),
                    formatMeasure(d.hem, unit)
                  ];
                }).filter(Boolean),
                "trouser"
              )}
            `}
          `}
          
          ${isOvercoat && html`
            <h3 style=${ { fontSize: "13px", fontWeight: 700, marginBottom: "12px", fontFamily: "var(--zb-font-heading)" } }>${category === 'db_overcoat' ? 'Double Breast' : 'Single Breast'} Overcoat (${unit})</h3>
            ${renderTable(
              ["Size", "Chest", "Waist", "Shoulder", "Sleeve", "Length"],
              jacketSizes.map(size => {
                const d = overcoatChart[lengthFilter]?.[size];
                if (!d) return null;
                return [
                  size + getLengthSuffix(lengthFilter),
                  formatMeasure(d.chest, unit),
                  formatMeasure(d.waist, unit),
                  formatMeasure(d.shoulder, unit),
                  formatMeasure(d.sleeve, unit),
                  formatMeasure(d.length, unit)
                ];
              }).filter(Boolean),
              "overcoat"
            )}
          `}
          
          ${isPeacoat && html`
            <h3 style=${ { fontSize: "13px", fontWeight: 700, marginBottom: "12px", fontFamily: "var(--zb-font-heading)" } }>Peacoat Measurements (${unit})</h3>
            ${renderTable(
              ["Size", "Chest", "Waist", "Shoulder", "Sleeve", "Length"],
              jacketSizes.map(size => {
                const d = PEACOAT_CHART_IN[lengthFilter]?.[size];
                if (!d) return null;
                return [
                  size + getLengthSuffix(lengthFilter),
                  formatMeasure(d.chest, unit),
                  formatMeasure(d.waist, unit),
                  formatMeasure(d.shoulder, unit),
                  formatMeasure(d.sleeve, unit),
                  formatMeasure(d.length, unit)
                ];
              }).filter(Boolean),
              "peacoat"
            )}
          `}
        </div>
        <div className="zane-fit-footer-glass" style=${ { padding: "14px 22px", borderTop: "1px solid rgba(0,0,0,.06)" } }>
          <button type="button" className="zane-fit-back" onClick=${onClose} style=${{ width: "100%" }}>Close</button>
        </div>
      </div>
    </div>
  `;
};

// ============================================
// LOGIN MODAL (Native Form Submission)
// ============================================
const LoginModal = ({ open, onClose }) => {
  const [mode, setMode] = useState("login");
  const [csrf, setCsrf] = useState("");

  // Scrape the Authenticity Token from the product page (Required for Shopify forms)
  useEffect(() => {
    const token = document.querySelector('input[name="authenticity_token"]')?.value;
    if (token) setCsrf(token);
  }, [open]);

  if (!open) return null;

  const isLogin = mode === "login";
  const returnUrl = window.location.pathname;

  return html`
    <div style=${ { position: "fixed", inset: 0, zIndex: 999999999 } }>
      <div onClick=${onClose} style=${ { position: "absolute", inset: 0, background: "rgba(0,0,0,.35)" } }></div>
      <div style=${ { position: "absolute", right: 0, top: 0, height: "100%", width: "min(520px, 100%)", background: "#fff", boxShadow: "-24px 0 60px rgba(0,0,0,.25)", display: "flex", flexDirection: "column" } }>
        
        <div style=${ { padding: "18px 22px", borderBottom: "1px solid rgba(0,0,0,.08)", display: "flex", alignItems: "center", justifyContent: "space-between" } }>
          <div style=${ { fontSize: "12px", letterSpacing: ".18em", textTransform: "uppercase", fontWeight: 900 } }>${isLogin ? "Log in" : "Create Account"}</div>
          <button type="button" onClick=${onClose} style=${ { border: 0, background: "transparent", cursor: "pointer", fontSize: "18px" } }>✕</button>
        </div>

        <div style=${ { flex: 1, padding: "24px 22px", overflow: "auto" } }>
          
          <form 
            method="post" 
            action=${isLogin ? "/account/login" : "/account"} 
            acceptCharset="UTF-8"
            onSubmit=${() => { sessionStorage.setItem("zane_reopen_drawer", "true"); }}
          >
            <input type="hidden" name="form_type" value=${isLogin ? "customer_login" : "create_customer"} />
            <input type="hidden" name="utf8" value="✓" />
            <input type="hidden" name="authenticity_token" value=${csrf} />
            <input type="hidden" name="return_to" value=${returnUrl} /> 

            ${!isLogin && html`
              <div style=${ { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" } }>
                <div>
                  <label style=${ { display: "block", fontSize: "11px", fontWeight: 900, marginBottom: "8px" } }>First Name</label>
                  <input type="text" name="customer[first_name]" required style=${ { width: "100%", padding: "14px", fontSize: "16px", border: "1px solid rgba(0,0,0,.15)", borderRadius: "10px", boxSizing: "border-box" } } />
                </div>
                <div>
                  <label style=${ { display: "block", fontSize: "11px", fontWeight: 900, marginBottom: "8px" } }>Last Name</label>
                  <input type="text" name="customer[last_name]" required style=${ { width: "100%", padding: "14px", fontSize: "16px", border: "1px solid rgba(0,0,0,.15)", borderRadius: "10px", boxSizing: "border-box" } } />
                </div>
              </div>
            `}
            
            <div style=${ { marginBottom: "16px" } }>
              <label style=${ { display: "block", fontSize: "11px", fontWeight: 900, marginBottom: "8px" } }>Email</label>
              <input type="email" name="customer[email]" required style=${ { width: "100%", padding: "14px", fontSize: "16px", border: "1px solid rgba(0,0,0,.15)", borderRadius: "10px", boxSizing: "border-box" } } />
            </div>
            
            <div style=${ { marginBottom: "16px" } }>
              <label style=${ { display: "block", fontSize: "11px", fontWeight: 900, marginBottom: "8px" } }>Password</label>
              <input type="password" name="customer[password]" required style=${ { width: "100%", padding: "14px", fontSize: "16px", border: "1px solid rgba(0,0,0,.15)", borderRadius: "10px", boxSizing: "border-box" } } />
            </div>
            
            ${isLogin && html`<a href="/account/recover" style=${ { fontSize: "13px", color: "#000", display: "block", marginBottom: "20px" } }>Forgot password?</a>`}
            
            <button type="submit" className="zane-fit-cta" style=${{ width: "100%" }}>
              ${isLogin ? "Log In" : "Create Account"}
            </button>
          </form>
          
          <div style=${ { marginTop: "24px", textAlign: "center", fontSize: "14px" } }>
            ${isLogin 
              ? html`Don't have an account? <button type="button" onClick=${() => setMode("register")} style=${ { border: 0, background: "transparent", fontWeight: 900, textDecoration: "underline", cursor: "pointer", padding: 0 } }>Create one</button>` 
              : html`Already have an account? <button type="button" onClick=${() => setMode("login")} style=${ { border: 0, background: "transparent", fontWeight: 900, textDecoration: "underline", cursor: "pointer", padding: 0 } }>Log in</button>`
            }
          </div>

        </div>
      </div>
    </div>
  `;
};

// ============================================
// SMALL UI COMPONENTS
// ============================================
const RoundedPillButton = ({ label, onClick, icon }) => html`
  <button type="button" onClick=${onClick} className="zane-link-btn">
    ${icon}
    ${label}
  </button>
`;

const SegToggle = ({ left, right, value, onChange }) => html`
  <div className="zane-fit-seg">
    <button type="button" onClick=${() => onChange(left.value)} className=${value === left.value ? "is-active" : ""}>${left.label}</button>
    <button type="button" onClick=${() => onChange(right.value)} className=${value === right.value ? "is-active" : ""}>${right.label}</button>
  </div>
`;

const StepTabs = ({ steps, active }) => html`<div style=${ { display: "flex", gap: "8px", padding: "14px 22px", borderBottom: "1px solid rgba(0,0,0,.08)", flexWrap: "wrap" } }>${steps.map((s, i) => html`<span key=${i} style=${ { fontSize: "11px", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: active === i ? 700 : 500, opacity: active === i ? 1 : 0.4, fontFamily: "var(--zb-font-heading)" } }>${s}</span>${i < steps.length - 1 ? html`<span style=${ { opacity: 0.3 } }>|</span>` : null}`)}</div>`;

// ZB-DIAGRAMS: per-measurement explainer diagrams (minimalist line art, no tape measure).
// Each opens a popover that shows WHAT the measurement is and that we set it for the customer.
const ZB_MEASURE_DIAGRAMS = {
  jacketChest: {
    title: "Chest",
    desc: "The room across the fullest part of the chest, where the jacket has to close cleanly and move with you. We set it from your height, weight and build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;">' +
      '<path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/>' +
      '<path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/>' +
      '<path d="M76 172 L85 118 L94 172"/>' +
      '<path d="M58 42 L66 47 M112 42 L104 47"/>' +
      '<path d="M66 47 L85 62 L104 47"/>' +
      '<path d="M66 47 L60 72 L85 118"/>' +
      '<path d="M104 47 L110 72 L85 118"/>' +
      '<path d="M60 72 L69 74 M110 72 L101 74"/>' +
      '<circle cx="85" cy="120" r="2.1" fill="#1a1a1a" stroke="none"/>' +
      '<circle cx="85" cy="138" r="2.1" fill="#1a1a1a" stroke="none"/>' +
      '<path d="M56 138 L71 140 M114 138 L99 140"/>' +
      '<line x1="35" y1="94" x2="135" y2="94" stroke="#5C1A1F" stroke-width="2"/>' +
      '<path d="M35 94 l9 -5 v10 z" fill="#5C1A1F" stroke="none"/><path d="M135 94 l-9 -5 v10 z" fill="#5C1A1F" stroke="none"/>' +
      '</svg>'
  },
  trouserWaist: {
    title: "Trouser Waist",
    desc: "Where the trousers sit at your waist, set to fasten comfortably and sit clean without the waistband straining. We work it out from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 150 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:140px;height:174px;">' +
      '<path d="M34 50 L116 50 L116 66 L34 66 Z"/>' +
      '<path d="M34 66 L28 180 L60 180 L75 86 L90 180 L122 180 L116 66"/>' +
      '<path d="M50 66 L46 180 M100 66 L104 180" stroke-width="1.1" opacity="0.45"/>' +
      '<path d="M75 50 L75 86" stroke-width="1.4"/>' +
      '<circle cx="75" cy="56" r="1.8" fill="#1a1a1a" stroke="none"/>' +
      '<path d="M44 50 L44 44 M75 50 L75 44 M106 50 L106 44" stroke-width="1.6"/>' +
      '<line x1="34" y1="36" x2="116" y2="36" stroke="#5C1A1F" stroke-width="2"/>' +
      '<path d="M34 36 l9 -5 v10 z" fill="#5C1A1F" stroke="none"/><path d="M116 36 l-9 -5 v10 z" fill="#5C1A1F" stroke="none"/>' +
      '</svg>'
  },
  jacketWaist: {
    title: "Jacket Waist",
    desc: "How much the jacket draws in through the middle. It shapes the silhouette without pulling at the button. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L85 118 L94 172"/><path d="M58 42 L66 47 M112 42 L104 47"/><path d="M66 47 L85 62 L104 47"/><path d="M66 47 L60 72 L85 118"/><path d="M104 47 L110 72 L85 118"/><path d="M60 72 L69 74 M110 72 L101 74"/><circle cx="85" cy="120" r="2.1" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="138" r="2.1" fill="#1a1a1a" stroke="none"/><path d="M56 138 L71 140 M114 138 L99 140"/><line x1="42" y1="128" x2="128" y2="128" stroke="#5C1A1F" stroke-width="2"/><path d="M42 128 L51.0 123.0 L51.0 133.0 Z" fill="#5C1A1F" stroke="none"/><path d="M128 128 L119.0 133.0 L119.0 123.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  jacketLength: {
    title: "Jacket Length",
    desc: "From the base of the collar to the hem, cut to cover the seat and keep the proportions balanced. We set it from your height, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L85 118 L94 172"/><path d="M58 42 L66 47 M112 42 L104 47"/><path d="M66 47 L85 62 L104 47"/><path d="M66 47 L60 72 L85 118"/><path d="M104 47 L110 72 L85 118"/><path d="M60 72 L69 74 M110 72 L101 74"/><circle cx="85" cy="120" r="2.1" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="138" r="2.1" fill="#1a1a1a" stroke="none"/><path d="M56 138 L71 140 M114 138 L99 140"/><line x1="14" y1="42" x2="14" y2="172" stroke="#5C1A1F" stroke-width="2"/><path d="M14 42 L19.0 51.0 L9.0 51.0 Z" fill="#5C1A1F" stroke="none"/><path d="M14 172 L9.0 163.0 L19.0 163.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  jacketSleeve: {
    title: "Sleeve Length",
    desc: "From the shoulder seam to the cuff, finishing at the wrist bone so a hint of shirt cuff shows. We set it from your height and build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L85 118 L94 172"/><path d="M58 42 L66 47 M112 42 L104 47"/><path d="M66 47 L85 62 L104 47"/><path d="M66 47 L60 72 L85 118"/><path d="M104 47 L110 72 L85 118"/><path d="M60 72 L69 74 M110 72 L101 74"/><circle cx="85" cy="120" r="2.1" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="138" r="2.1" fill="#1a1a1a" stroke="none"/><path d="M56 138 L71 140 M114 138 L99 140"/><line x1="30.3" y1="45.5" x2="15.8" y2="81.2" stroke="#5C1A1F" stroke-width="2"/><path d="M30.3 45.5 L31.3 53.4 L24.1 50.5 Z" fill="#5C1A1F" stroke="none"/><path d="M15.8 81.2 L14.8 73.3 L22.0 76.2 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  jacketHip: {
    title: "Jacket Hip",
    desc: "The room through the bottom of the jacket, where it skims the seat and stays smooth when buttoned. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L85 118 L94 172"/><path d="M58 42 L66 47 M112 42 L104 47"/><path d="M66 47 L85 62 L104 47"/><path d="M66 47 L60 72 L85 118"/><path d="M104 47 L110 72 L85 118"/><path d="M60 72 L69 74 M110 72 L101 74"/><circle cx="85" cy="120" r="2.1" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="138" r="2.1" fill="#1a1a1a" stroke="none"/><path d="M56 138 L71 140 M114 138 L99 140"/><line x1="44" y1="160" x2="126" y2="160" stroke="#5C1A1F" stroke-width="2"/><path d="M44 160 L53.0 155.0 L53.0 165.0 Z" fill="#5C1A1F" stroke="none"/><path d="M126 160 L117.0 165.0 L117.0 155.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  jacketShoulder: {
    title: "Shoulder Width",
    desc: "From shoulder point to shoulder point across the back. It is the line the whole jacket hangs from. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L85 118 L94 172"/><path d="M58 42 L66 47 M112 42 L104 47"/><path d="M66 47 L85 62 L104 47"/><path d="M66 47 L60 72 L85 118"/><path d="M104 47 L110 72 L85 118"/><path d="M60 72 L69 74 M110 72 L101 74"/><circle cx="85" cy="120" r="2.1" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="138" r="2.1" fill="#1a1a1a" stroke="none"/><path d="M56 138 L71 140 M114 138 L99 140"/><path d="M58 42 L58 33" stroke-width="1.6"/><path d="M112 42 L112 33" stroke-width="1.6"/><line x1="58" y1="35" x2="112" y2="35" stroke="#5C1A1F" stroke-width="2"/><path d="M58 35 L67.0 30.0 L67.0 40.0 Z" fill="#5C1A1F" stroke="none"/><path d="M112 35 L103.0 40.0 L103.0 30.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  jacketBack: {
    title: "Back Width",
    desc: "The room across the upper back, between the armholes, so the jacket moves with you when you reach forward. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L94 172"/><path d="M58 42 L66 44 M112 42 L104 44"/><path d="M66 44 Q85 52 104 44"/><path d="M85 51 L85 148"/><path d="M85 148 L80 172 M85 148 L90 172"/><path d="M66 44 L62 64 M104 44 L108 64" stroke-width="1.1" opacity="0.45"/><line x1="50" y1="72" x2="120" y2="72" stroke="#5C1A1F" stroke-width="2"/><path d="M50 72 L59.0 67.0 L59.0 77.0 Z" fill="#5C1A1F" stroke="none"/><path d="M120 72 L111.0 77.0 L111.0 67.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  jacketVent: {
    title: "Vent Length",
    desc: "The length of the opening at the center back hem. It lets the jacket sit clean when you move or use your pockets. We set it in proportion to the jacket, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L94 172"/><path d="M58 42 L66 44 M112 42 L104 44"/><path d="M66 44 Q85 52 104 44"/><path d="M85 51 L85 148"/><path d="M85 148 L80 172 M85 148 L90 172"/><path d="M66 44 L62 64 M104 44 L108 64" stroke-width="1.1" opacity="0.45"/><path d="M85 148 L97 148" stroke-width="1.6"/><line x1="99" y1="148" x2="99" y2="172" stroke="#5C1A1F" stroke-width="2"/><path d="M99 148 L101.8 153.0 L96.2 153.0 Z" fill="#5C1A1F" stroke="none"/><path d="M99 172 L96.2 167.0 L101.8 167.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  jacketFrontLength: {
    title: "Front Length",
    desc: "From the shoulder seam at the neck down to the front hem. It keeps the front edge hanging straight. We set it from your height and build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L85 118 L94 172"/><path d="M58 42 L66 47 M112 42 L104 47"/><path d="M66 47 L85 62 L104 47"/><path d="M66 47 L60 72 L85 118"/><path d="M104 47 L110 72 L85 118"/><path d="M60 72 L69 74 M110 72 L101 74"/><circle cx="85" cy="120" r="2.1" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="138" r="2.1" fill="#1a1a1a" stroke="none"/><path d="M56 138 L71 140 M114 138 L99 140"/><line x1="56" y1="46" x2="56" y2="168" stroke="#5C1A1F" stroke-width="2"/><path d="M56 46 L61.0 55.0 L51.0 55.0 Z" fill="#5C1A1F" stroke="none"/><path d="M56 168 L51.0 159.0 L61.0 159.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  jacketBicep: {
    title: "Bicep",
    desc: "The room around the upper arm, cut so the sleeve drapes clean without gripping. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L85 118 L94 172"/><path d="M58 42 L66 47 M112 42 L104 47"/><path d="M66 47 L85 62 L104 47"/><path d="M66 47 L60 72 L85 118"/><path d="M104 47 L110 72 L85 118"/><path d="M60 72 L69 74 M110 72 L101 74"/><circle cx="85" cy="120" r="2.1" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="138" r="2.1" fill="#1a1a1a" stroke="none"/><path d="M56 138 L71 140 M114 138 L99 140"/><line x1="52.7" y1="64.4" x2="29.1" y2="53.3" stroke="#5C1A1F" stroke-width="2"/><path d="M52.7 64.4 L45.9 64.9 L48.7 58.8 Z" fill="#5C1A1F" stroke="none"/><path d="M29.1 53.3 L35.9 52.8 L33.1 58.9 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  jacketSleeveOpening: {
    title: "Sleeve Opening",
    desc: "The width of the sleeve at the cuff, finished so it sits neatly over the shirt cuff. We set it in proportion to the sleeve, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M58 42 L36 50 L23 82 L34 87 L48 64 L50 166 L76 172"/><path d="M112 42 L134 50 L147 82 L136 87 L122 64 L120 166 L94 172"/><path d="M76 172 L85 118 L94 172"/><path d="M58 42 L66 47 M112 42 L104 47"/><path d="M66 47 L85 62 L104 47"/><path d="M66 47 L60 72 L85 118"/><path d="M104 47 L110 72 L85 118"/><path d="M60 72 L69 74 M110 72 L101 74"/><circle cx="85" cy="120" r="2.1" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="138" r="2.1" fill="#1a1a1a" stroke="none"/><path d="M56 138 L71 140 M114 138 L99 140"/><line x1="18.3" y1="87.5" x2="32.9" y2="94.2" stroke="#5C1A1F" stroke-width="2"/><path d="M18.3 87.5 L25.1 87.0 L22.4 93.0 Z" fill="#5C1A1F" stroke="none"/><path d="M32.9 94.2 L26.1 94.7 L28.8 88.7 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  trouserHip: {
    title: "Hip and Seat",
    desc: "The room through the seat and hips, set so the trousers drape from the waistband without pulling across the pockets. We work it out from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 150 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:140px;height:174px;"><path d="M34 50 L116 50 L116 66 L34 66 Z"/><path d="M34 66 L28 180 L60 180 L75 86 L90 180 L122 180 L116 66"/><path d="M50 66 L46 180 M100 66 L104 180" stroke-width="1.1" opacity="0.45"/><path d="M75 50 L75 86" stroke-width="1.4"/><circle cx="75" cy="56" r="1.8" fill="#1a1a1a" stroke="none"/><path d="M44 50 L44 44 M75 50 L75 44 M106 50 L106 44" stroke-width="1.6"/><line x1="26" y1="80" x2="124" y2="80" stroke="#5C1A1F" stroke-width="2"/><path d="M26 80 L35.0 75.0 L35.0 85.0 Z" fill="#5C1A1F" stroke="none"/><path d="M124 80 L115.0 85.0 L115.0 75.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  trouserThigh: {
    title: "Thigh",
    desc: "The room through the upper leg, with enough ease to sit and move freely while keeping a clean line. We work it out from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 150 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:140px;height:174px;"><path d="M34 50 L116 50 L116 66 L34 66 Z"/><path d="M34 66 L28 180 L60 180 L75 86 L90 180 L122 180 L116 66"/><path d="M50 66 L46 180 M100 66 L104 180" stroke-width="1.1" opacity="0.45"/><path d="M75 50 L75 86" stroke-width="1.4"/><circle cx="75" cy="56" r="1.8" fill="#1a1a1a" stroke="none"/><path d="M44 50 L44 44 M75 50 L75 44 M106 50 L106 44" stroke-width="1.6"/><line x1="75" y1="106" x2="121" y2="106" stroke="#5C1A1F" stroke-width="2"/><path d="M75 106 L83.0 101.6 L83.0 110.4 Z" fill="#5C1A1F" stroke="none"/><path d="M121 106 L113.0 110.4 L113.0 101.6 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  trouserKnee: {
    title: "Knee",
    desc: "The width of the leg at the knee, shaping the taper from thigh to hem. We set it in proportion to your leg, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 150 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:140px;height:174px;"><path d="M34 50 L116 50 L116 66 L34 66 Z"/><path d="M34 66 L28 180 L60 180 L75 86 L90 180 L122 180 L116 66"/><path d="M50 66 L46 180 M100 66 L104 180" stroke-width="1.1" opacity="0.45"/><path d="M75 50 L75 86" stroke-width="1.4"/><circle cx="75" cy="56" r="1.8" fill="#1a1a1a" stroke="none"/><path d="M44 50 L44 44 M75 50 L75 44 M106 50 L106 44" stroke-width="1.6"/><line x1="80" y1="140" x2="123" y2="140" stroke="#5C1A1F" stroke-width="2"/><path d="M80 140 L88.0 135.6 L88.0 144.4 Z" fill="#5C1A1F" stroke="none"/><path d="M123 140 L115.0 144.4 L115.0 135.6 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  trouserHem: {
    title: "Hem Opening",
    desc: "The width of the leg opening at the ankle. It finishes the taper and frames the shoe. We set it in proportion to the leg, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 150 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:140px;height:174px;"><path d="M34 50 L116 50 L116 66 L34 66 Z"/><path d="M34 66 L28 180 L60 180 L75 86 L90 180 L122 180 L116 66"/><path d="M50 66 L46 180 M100 66 L104 180" stroke-width="1.1" opacity="0.45"/><path d="M75 50 L75 86" stroke-width="1.4"/><circle cx="75" cy="56" r="1.8" fill="#1a1a1a" stroke="none"/><path d="M44 50 L44 44 M75 50 L75 44 M106 50 L106 44" stroke-width="1.6"/><path d="M90 180 L90 186" stroke-width="1.6"/><path d="M122 180 L122 186" stroke-width="1.6"/><line x1="90" y1="188" x2="122" y2="188" stroke="#5C1A1F" stroke-width="2"/><path d="M90 188 L99.0 183.0 L99.0 193.0 Z" fill="#5C1A1F" stroke="none"/><path d="M122 188 L113.0 193.0 L113.0 183.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  trouserInseam: {
    title: "Inside Leg",
    desc: "From the crotch seam down the inside of the leg to the hem. It decides where the trousers break on the shoe. We set it from your height, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 150 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:140px;height:174px;"><path d="M34 50 L116 50 L116 66 L34 66 Z"/><path d="M34 66 L28 180 L60 180 L75 86 L90 180 L122 180 L116 66"/><path d="M50 66 L46 180 M100 66 L104 180" stroke-width="1.1" opacity="0.45"/><path d="M75 50 L75 86" stroke-width="1.4"/><circle cx="75" cy="56" r="1.8" fill="#1a1a1a" stroke="none"/><path d="M44 50 L44 44 M75 50 L75 44 M106 50 L106 44" stroke-width="1.6"/><line x1="75" y1="92" x2="75" y2="182" stroke="#5C1A1F" stroke-width="2"/><path d="M75 92 L80.0 101.0 L70.0 101.0 Z" fill="#5C1A1F" stroke="none"/><path d="M75 182 L70.0 173.0 L80.0 173.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  trouserRise: {
    title: "Front Rise",
    desc: "From the crotch seam up to the front of the waistband. It decides where the trousers sit on your waist. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 150 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:140px;height:174px;"><path d="M34 50 L116 50 L116 66 L34 66 Z"/><path d="M34 66 L28 180 L60 180 L75 86 L90 180 L122 180 L116 66"/><path d="M50 66 L46 180 M100 66 L104 180" stroke-width="1.1" opacity="0.45"/><path d="M75 50 L75 86" stroke-width="1.4"/><circle cx="75" cy="56" r="1.8" fill="#1a1a1a" stroke="none"/><path d="M44 50 L44 44 M75 50 L75 44 M106 50 L106 44" stroke-width="1.6"/><line x1="67" y1="50" x2="67" y2="86" stroke="#5C1A1F" stroke-width="2"/><path d="M67 50 L70.3 56.0 L63.7 56.0 Z" fill="#5C1A1F" stroke="none"/><path d="M67 86 L63.7 80.0 L70.3 80.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  trouserBackRise: {
    title: "Back Rise",
    desc: "From the crotch seam up to the back of the waistband, cut higher than the front so the trousers stay put when you sit. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 150 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:140px;height:174px;"><path d="M34 50 L116 50 L116 66 L34 66 Z"/><path d="M34 66 L28 180 L60 180 L75 86 L90 180 L122 180 L116 66"/><path d="M50 66 L46 180 M100 66 L104 180" stroke-width="1.1" opacity="0.45"/><path d="M75 50 L75 86" stroke-width="1.4"/><path d="M56 66 L58 76 M94 66 L92 76" stroke-width="1.1" opacity="0.6"/><path d="M44 82 L62 84 M106 82 L88 84" stroke-width="1.4"/><line x1="67" y1="50" x2="67" y2="86" stroke="#5C1A1F" stroke-width="2"/><path d="M67 50 L70.3 56.0 L63.7 56.0 Z" fill="#5C1A1F" stroke="none"/><path d="M67 86 L63.7 80.0 L70.3 80.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtCollar: {
    title: "Collar",
    desc: "The room around the neck at the collar band, fastened with space to breathe. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><path d="M68 38 L68 28" stroke-width="1.6"/><path d="M102 38 L102 28" stroke-width="1.6"/><line x1="68" y1="26" x2="102" y2="26" stroke="#5C1A1F" stroke-width="2"/><path d="M68 26 L77.0 21.0 L77.0 31.0 Z" fill="#5C1A1F" stroke="none"/><path d="M102 26 L93.0 31.0 L93.0 21.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtChest: {
    title: "Shirt Chest",
    desc: "The room across the fullest part of the chest, so the shirt drapes clean without pulling at the buttons. We set it from your height, weight and build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><line x1="46" y1="92" x2="124" y2="92" stroke="#5C1A1F" stroke-width="2"/><path d="M46 92 L55.0 87.0 L55.0 97.0 Z" fill="#5C1A1F" stroke="none"/><path d="M124 92 L115.0 97.0 L115.0 87.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtWaist: {
    title: "Shirt Waist",
    desc: "How much the shirt follows your middle, shaping the body without straining when you sit. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><line x1="52" y1="126" x2="118" y2="126" stroke="#5C1A1F" stroke-width="2"/><path d="M52 126 L61.0 121.0 L61.0 131.0 Z" fill="#5C1A1F" stroke="none"/><path d="M118 126 L109.0 131.0 L109.0 121.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtHip: {
    title: "Shirt Hip",
    desc: "The room through the bottom of the shirt, where it tucks clean or sits over the trouser. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><line x1="52" y1="168" x2="118" y2="168" stroke="#5C1A1F" stroke-width="2"/><path d="M52 168 L61.0 163.0 L61.0 173.0 Z" fill="#5C1A1F" stroke="none"/><path d="M118 168 L109.0 173.0 L109.0 163.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtLength: {
    title: "Shirt Length",
    desc: "From the base of the collar to the hem, long enough to stay tucked when you move. We set it from your height, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><line x1="155" y1="46" x2="155" y2="184" stroke="#5C1A1F" stroke-width="2"/><path d="M155 46 L160.0 55.0 L150.0 55.0 Z" fill="#5C1A1F" stroke="none"/><path d="M155 184 L150.0 175.0 L160.0 175.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtSleeve: {
    title: "Sleeve Length",
    desc: "From the shoulder seam to the end of the cuff, finishing at the base of the thumb. We set it from your height and build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><line x1="37.3" y1="51" x2="23.8" y2="157" stroke="#5C1A1F" stroke-width="2"/><path d="M37.3 51 L40.3 58.4 L32.6 57.5 Z" fill="#5C1A1F" stroke="none"/><path d="M23.8 157 L20.8 149.6 L28.5 150.5 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtCuff: {
    title: "Cuff",
    desc: "The room around the wrist at the cuff, snug enough to sit at the wrist bone without sliding over the hand. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><line x1="29" y1="162.6" x2="47" y2="165.2" stroke="#5C1A1F" stroke-width="2"/><path d="M29 162.6 L35.4 160.2 L34.5 166.8 Z" fill="#5C1A1F" stroke="none"/><path d="M47 165.2 L40.6 167.6 L41.5 161.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtBicep: {
    title: "Bicep",
    desc: "The room around the upper arm, cut for movement without excess cloth. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><line x1="34" y1="88" x2="59" y2="88" stroke="#5C1A1F" stroke-width="2"/><path d="M34 88 L40.0 84.7 L40.0 91.3 Z" fill="#5C1A1F" stroke="none"/><path d="M59 88 L53.0 91.3 L53.0 84.7 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtForearm: {
    title: "Forearm",
    desc: "The room around the lower arm, tapering the sleeve toward the cuff. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><line x1="31" y1="125" x2="54" y2="125" stroke="#5C1A1F" stroke-width="2"/><path d="M31 125 L37.0 121.7 L37.0 128.3 Z" fill="#5C1A1F" stroke="none"/><path d="M54 125 L48.0 128.3 L48.0 121.7 Z" fill="#5C1A1F" stroke="none"/></svg>'
  },
  shirtShoulder: {
    title: "Shoulder Width",
    desc: "From shoulder point to shoulder point across the back, where the sleeve seam sits right at the bone. We set it from your build, so there is nothing for you to measure.",
    svg: '<svg viewBox="0 0 170 198" fill="none" stroke="#1a1a1a" stroke-width="2.3" stroke-linejoin="round" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" style="width:150px;height:174px;"><path d="M68 46 Q85 36 102 46"/><path d="M68 46 L74 54 L85 50 L96 54 L102 46"/><path d="M68 46 L45 54 M102 46 L125 54"/><path d="M74 54 L82 57 M96 54 L88 57"/><path d="M45 54 L36 102 L34 140"/><path d="M56 82 L49 142"/><path d="M34 140 L48 142 M32 156 L46 158 M34 140 L32 156 M48 142 L46 158"/><path d="M125 54 L134 102 L136 140"/><path d="M114 82 L121 142"/><path d="M136 140 L122 142 M138 156 L124 158 M136 140 L138 156 M122 142 L124 158"/><path d="M56 82 L58 178 M114 82 L112 178"/><path d="M58 178 Q85 188 112 178"/><path d="M82 57 L82 182 M88 57 L88 182" stroke-width="1.1" opacity="0.45"/><circle cx="85" cy="72" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="96" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="120" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="144" r="1.8" fill="#1a1a1a" stroke="none"/><circle cx="85" cy="166" r="1.8" fill="#1a1a1a" stroke="none"/><path d="M45 52 L45 24" stroke-width="1.6"/><path d="M125 52 L125 24" stroke-width="1.6"/><line x1="45" y1="22" x2="125" y2="22" stroke="#5C1A1F" stroke-width="2"/><path d="M45 22 L54.0 17.0 L54.0 27.0 Z" fill="#5C1A1F" stroke="none"/><path d="M125 22 L116.0 27.0 L116.0 17.0 Z" fill="#5C1A1F" stroke="none"/></svg>'
  }
};

const MeasureRow = ({ label, valueIn, unit, onMinus, onPlus, autoUpdated, diagramKey, onInfo }) => {
  const [showTip, setShowTip] = useState(false);

  return html`
    <div style=${{ borderBottom: "1px solid rgba(0,0,0,.06)", padding: "12px 0" }}>
      <div style=${{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style=${{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style=${{ fontSize: "14px", fontWeight: 600 }}>${label}</span>

          ${diagramKey && onInfo && html`
            <button type="button" onClick=${() => onInfo(diagramKey)} aria-label=${"What is " + label + "?"}
              style=${{ width: "17px", height: "17px", borderRadius: "50%", border: "1px solid rgba(0,0,0,0.28)", background: "none", color: "rgba(0,0,0,0.55)", fontSize: "11px", fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, padding: 0, flexShrink: 0 }}>i</button>
          `}

          ${autoUpdated && html`
            <button 
              type="button" 
              onClick=${() => setShowTip(!showTip)}
              style=${{ 
                fontSize: "9px", fontWeight: 700, color: "#059669", background: "#d1fae5", 
                border: "none", padding: "3px 8px", borderRadius: "12px", 
                textTransform: "uppercase", letterSpacing: "0.05em", cursor: "pointer",
                display: "flex", alignItems: "center", gap: "3px"
              }}
            >
              Optimized ${showTip ? "▴" : "▾"}
            </button>
          `}
        </div>

        <div style=${{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button type="button" onClick=${onMinus} style=${{ width: "36px", height: "36px", borderRadius: "8px", border: "1px solid rgba(0,0,0,.12)", background: "#fff", cursor: "pointer", fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
          <div style=${{ width: "60px", textAlign: "center", fontWeight: 900, fontSize: "16px" }}>${formatMeasure(valueIn, unit)}</div>
          <button type="button" onClick=${onPlus} style=${{ width: "36px", height: "36px", borderRadius: "8px", border: "1px solid rgba(0,0,0,.12)", background: "#fff", cursor: "pointer", fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
        </div>
      </div>
      
      ${showTip && html`
        <div style=${{ fontSize: "11px", color: "#059669", marginTop: "6px", fontWeight: 500, lineHeight: "1.4" }}>
          We optimized this measurement for your build. Feel free to adjust it.
        </div>
      `}
    </div>
  `;
};

// ============================================
// END OF PART 3 - Continue to Part 4
// ============================================
// ============================================
// ZANE BARLAS FIT ENGINE V2 - PART 4 OF 5
// StandardTab, FindYourSizeModal, CustomSizesTab
// SHIRT COLLAR RECOMMENDATION + FULL MEASUREMENT FIELDS
// ============================================

// Trust Feature Row - 3-column benefit grid
const TrustFeatureRow = () => html`
  <div style=${{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px", marginTop: "12px", marginBottom: "12px" }}>
    ${[
      { text: "No Added Cost" },
      { text: "~3 Weeks Delivery" },
      { text: "Fit Guaranteed" }
    ].map(item => html`
      <div key=${item.text} style=${{ 
        background: "transparent", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "6px", padding: "8px 4px", 
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "3px", textAlign: "center" 
      }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(0,0,0,0.4)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        <span style=${{ fontSize: "9px", fontWeight: 600, letterSpacing: "0.01em", color: "rgba(0,0,0,0.6)", lineHeight: "1.2" }}>${item.text}</span>
      </div>
    `)}
  </div>
`;

const StandardTab = ({ product, selected, setSelected, openSizeGuide, onClose, onFindMySize }) => {
  const [showDetails, setShowDetails] = useState(false);
  const category = getProductCategory(product);
  const isSuit = category === "sb_suit" || category === "db_suit";
  const isShirt = category === "shirt";
  
  const allSizes = useMemo(() => {
    if (category === 'shirt') return ["13.5", "14.0", "14.5", "15.0", "15.5", "15.75", "16.0", "16.5", "17.0", "17.5", "17.75", "18.0"];
    return [32, 34, 36, 38, 40, 42, 44, 46, 48, 50];
  }, [category]);

  // Gate sizes and lengths to what THIS product actually stocks, so a customer
  // can never select a chest size or length that has no matching variant.
  const sizeInfo = useMemo(() => getProductSizeInfo(product), [product]);
  // ZB is made-to-order: always offer Short / Regular / Long so the customer can
  // specify garment length even when only the Regular SKU is stocked. The chosen
  // length is recorded on the cart line; resolveVariant() maps it to a real
  // purchasable variant (falling back to the size-only SKU when a length isn't stocked).
  const showLengthSelector = true;
  const activeLen = getLengthKey(selected?.lengthKey || "Regular");
  const sizes = useMemo(() => {
    const set = sizeInfo.lens[activeLen.toLowerCase()] || sizeInfo.lens.regular;
    if (!set || !set.size) return allSizes; // fallback if variant data is unavailable
    return allSizes.filter((s) => set.has(parseFloat(s)));
  }, [allSizes, sizeInfo, activeLen]);

  const getSizeLabel = () => {
    if (isShirt) return 'COLLAR SIZE';
    if (isSuit) return 'CHEST SIZE';
    return 'SIZE';
  };

  const displayCode = useMemo(() => {
    if (!selected?.jacketSize) return "—";
    if (!showLengthSelector) return String(selected.jacketSize);
    const suffix = selected.lengthKey === "Short" ? "S" : selected.lengthKey === "Long" ? "L" : "R";
    return `${selected.jacketSize}${suffix}`;
  }, [selected, showLengthSelector]);

  // Trouser waist: default to the conventional drop-6, but let the customer override it.
  // Real chest-to-waist drop is usually < 6, so a forced drop-6 runs trousers tight for
  // many builds; the chosen waist is recorded on the cart line for the tailor.
  const trouserSuffix = selected.lengthKey === "Short" ? "S" : selected.lengthKey === "Long" ? "L" : "R";
  const derivedWaist = (isSuit && selected?.jacketSize) ? selected.jacketSize - 6 : null;
  const effectiveWaist = (selected?.trouserWaist != null) ? selected.trouserWaist : derivedWaist;
  const waistOptions = useMemo(() => {
    if (derivedWaist == null) return [];
    return [-4, -2, 0, 2, 4].map((d) => derivedWaist + d).filter((w) => w >= 26 && w <= 44);
  }, [derivedWaist]);
  const trouserCode = (isSuit && effectiveWaist != null) ? `${effectiveWaist}${trouserSuffix}` : "";

  return html`
    <div style=${ { padding: "12px 24px" } }>
      <section style=${{ marginBottom: "24px" }}>
        <div style=${ { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" } }>
          <span className="zane-fit-label">${getSizeLabel()}</span>
          <${RoundedPillButton} label="Size Guide" onClick=${openSizeGuide} icon=${html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="6" width="22" height="12" rx="2" ry="2"/><line x1="5" y1="6" x2="5" y2="10"/><line x1="9" y1="6" x2="9" y2="12"/><line x1="13" y1="6" x2="13" y2="10"/><line x1="17" y1="6" x2="17" y2="12"/><line x1="21" y1="6" x2="21" y2="10"/></svg>`} />
        </div>
        <div className="zane-fit-grid">
          ${sizes.map((s) => html`<button key=${s} type="button" title=${isShirt ? `Collar ${s}` : `Size ${s}`} className=${`zane-fit-btn is-centered ${selected?.jacketSize === s ? "is-active" : ""}`} onClick=${() => setSelected((prev) => ({ ...(prev || {}), jacketSize: s, trouserWaist: null }))}>${s}</button>`)}
        </div>
      </section>
      
      ${showLengthSelector && html`
        <section style=${{ marginBottom: "24px" }}>
          <div style=${ { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" } }>
            <span className="zane-fit-label">Length</span>
            <${InlineInfo} title="Length" text="Determines garment length based on height." />
          </div>
          <div style=${ { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" } }>
            ${LENGTH_OPTIONS.map((len) => html`<button key=${len.key} type="button" title=${`Select ${len.label} length`} className=${`zane-fit-btn is-centered ${selected?.lengthKey === len.key ? "is-active" : ""}`} onClick=${() => setSelected((prev) => ({ ...(prev || {}), lengthKey: len.key }))} style=${ { padding: "12px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", borderRadius: "12px", minHeight: "56px" } }><span style=${ { fontWeight: 900, fontSize: "13px" } }>${len.label}</span><span style=${ { fontSize: "10px", opacity: 0.6 } }>${len.height}</span></button>`)}
          </div>
        </section>
      `}

      ${isSuit && waistOptions.length > 0 && html`
        <section style=${{ marginBottom: "24px" }}>
          <div style=${ { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" } }>
            <span className="zane-fit-label">Trouser Waist</span>
            <${InlineInfo} title="Trouser Waist" text="Your suit trousers, cut to this waist. We pre-select the standard drop for your jacket; adjust it if you carry more or less at the waist." />
          </div>
          <div className="zane-fit-grid">
            ${waistOptions.map((w) => html`<button key=${w} type="button" title=${`Trouser waist ${w}`} className=${`zane-fit-btn is-centered ${effectiveWaist === w ? "is-active" : ""}`} onClick=${() => setSelected((prev) => ({ ...(prev || {}), trouserWaist: w }))}>${w}</button>`)}
          </div>
          <div style=${ { fontSize: "11px", color: "#8C8678", marginTop: "8px" } }>Standard for a ${selected.jacketSize} jacket is ${derivedWaist}. Choose your true waist for the best fit.</div>
        </section>
      `}

      <div className="zane-selection-container" style=${{ marginTop: "16px" }}>
        <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" } }>
          <span className="zane-selection-header">Selection</span>
          <button type="button" onClick=${() => setShowDetails(true)} className="zane-full-specs-btn">View Full Specs</button>
        </div>
        <div style=${ { display: "grid", gridTemplateColumns: isSuit ? "1fr 1fr" : "1fr", gap: "12px" } }>
          <div className="zane-selection-card">
            <span className="zane-card-label">${isShirt ? 'Collar Size' : 'Selected Size'}</span>
            <span className="zane-card-value">${displayCode}</span>
          </div>
          ${isSuit && html`
            <div className="zane-selection-card">
              <span className="zane-card-label">Trouser Size</span>
              <span className="zane-card-value">${trouserCode}</span>
            </div>
          `}
        </div>
      </div>
      <${DetailsModal} open=${showDetails} onClose=${() => setShowDetails(false)} title="Size Details" product=${product} selected=${selected} originalUnit="in" />
    </div>
  `;
};

const FindYourSizeModal = ({ open, onClose, onApply, onCustomize, product }) => {
  const category = getProductCategory(product);
  const isShirt = category === "shirt";
  
  const [step, setStep] = useState(0);
  const [heightMode, setHeightMode] = useState("ft");
  const [heightCm, setHeightCm] = useState(180);
  const [weightMode, setWeightMode] = useState("kg");
  const [weightKg, setWeightKg] = useState(85);
  const [shoe, setShoe] = useState(11);
  const [age, setAge] = useState(35);
  const [bodyShape, setBodyShape] = useState(null);
  const [fitPref, setFitPref] = useState(null);
  const [error, setError] = useState("");
  
  // SMART OPEN: Load saved profile data when modal opens
  useEffect(() => { 
    if (open) { 
      setError(""); 
      
      // 1. Try to find saved data (Server first, then Local)
      let loaded = null;
      if (window.ZanePassport?.savedProfile) {
        loaded = window.ZanePassport.savedProfile;
      } else {
        try { loaded = JSON.parse(localStorage.getItem("zane_mtm_profile")); } catch(e) {}
      }

      // 2. If data exists, pre-fill the sliders
      if (loaded) {
        if (typeof loaded.heightCm === "number") setHeightCm(loaded.heightCm);
        if (typeof loaded.weightKg === "number") setWeightKg(loaded.weightKg);
        if (typeof loaded.shoe === "number") setShoe(loaded.shoe);
        if (typeof loaded.age === "number") setAge(loaded.age);
        
        // 3. Decide where to start
        if (loaded.bodyShape && loaded.fitPref) {
          // COMPLETE PROFILE: Jump straight to Result
          setBodyShape(loaded.bodyShape);
          setFitPref(loaded.fitPref);
          setStep(3); 
        } else {
          // PARTIAL PROFILE: Start at Step 0, but sliders are already moved
          if (loaded.bodyShape) setBodyShape(loaded.bodyShape);
          setStep(0);
        }
      } else {
        // 4. NO DATA: Reset to defaults
        setStep(0);
        setBodyShape(null);
        setFitPref(null);
      }
    } 
  }, [open]);
  
  const heightLabel = useMemo(() => heightMode === "cm" ? `${Math.round(heightCm)} cm` : `${toFtInFromCm(heightCm).ft}ft ${toFtInFromCm(heightCm).inch}in`, [heightMode, heightCm]);
  const weightLabel = useMemo(() => weightMode === "kg" ? `${Math.round(weightKg)} kg` : `${kgToLbs(weightKg)} lb`, [weightMode, weightKg]);
  const weightSliderValue = useMemo(() => weightMode === "kg" ? Math.round(weightKg) : kgToLbs(weightKg), [weightMode, weightKg]);
  const setWeightFromSlider = (val) => weightMode === "kg" ? setWeightKg(Number(val)) : setWeightKg(lbsToKg(Number(val)));
  const stepsCount = 4;
  const metricCardStyle = { borderRadius: "14px", background: "rgba(0,0,0,0.03)", padding: "12px 14px", position: "relative", overflow: "visible", marginBottom: "10px" };
  
  const recommended = useMemo(() => {
    return recommendFromBody({ heightCm, weightKg, shoe, age }, { bodyShape, fitPref, category });
  }, [heightCm, weightKg, shoe, age, bodyShape, fitPref, category]);

  const next = () => { setError(""); if (step === 1 && !bodyShape) return setError("Please select a body shape."); if (step === 2 && !fitPref) return setError("Please select a fit preference."); setStep(step + 1); };
  const back = () => setStep(Math.max(0, step - 1));
  
  // Apply standard size
  const applyStandard = () => { 
    onApply(recommended); 
    onClose(); 
  };
  
  // Switch to custom tab with pre-filled specs
  const goCustomize = () => {
    if (onCustomize) {
      onCustomize({
        heightCm,
        weightKg,
        shoe,
        age,
        bodyShape,
        fitPref,
        idealCustomSpecs: recommended?.idealCustomSpecs,
        recommended
      });
    }
    onClose();
  };
  
  if (!open) return null;

  const displayCode = recommended ? `${recommended.jacketSize}${recommended.lengthKey[0]}` : "—";
  const sizeLabel = isShirt ? "Recommended Collar" : "Recommended Size";

  return html`
    <div style=${ { position: "fixed", inset: 0, zIndex: 99999999 } }>
      <div onClick=${onClose} style=${ { position: "absolute", inset: 0, background: "rgba(0,0,0,.35)" } }></div>
      <div style=${ { position: "absolute", right: 0, top: 0, height: "100%", width: "min(520px, 100%)", background: "#fff", boxShadow: "-24px 0 60px rgba(0,0,0,.25)", display: "flex", flexDirection: "column" } }>
        <div style=${ { padding: "12px 22px", borderBottom: "1px solid rgba(0,0,0,.08)", display: "flex", alignItems: "center", justifyContent: "space-between" } }>
          ${step === 3 ? html`
            <button type="button" onClick=${() => setStep(0)} style=${ { border: 0, background: "transparent", fontSize: "10px", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".12em", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", color: "#111", padding: 0 } }>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
              Edit Profile
            </button>
          ` : html`
            <div style=${ { fontSize: "10px", letterSpacing: ".18em", textTransform: "uppercase", fontWeight: 700, color: "rgba(0,0,0,0.4)" } }>Step ${step + 1} of ${stepsCount}</div>
          `}
          <button type="button" onClick=${onClose} style=${ { border: 0, background: "transparent", cursor: "pointer", fontSize: "18px" } }>✕</button>
        </div>
        <div style=${ { height: "4px", background: "#f5f5f5", width: "100%" } }><div style=${ { height: "100%", background: "#000", width: `${((step + 1) / stepsCount) * 100}%`, transition: "width 0.4s ease" } }></div></div>
        <div style=${ { flex: 1, overflow: "auto", padding: "16px 22px", display: "flex", flexDirection: "column" } }>
          ${step === 0 && html`
            <div style=${ { fontSize: "32px", fontWeight: 700, marginBottom: "16px", letterSpacing: "-0.04em", fontFamily: "var(--zb-font-heading)" } }>Profile</div>
            <div style=${metricCardStyle}>
              <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center" } }>
                <div style=${{ display: "flex", alignItems: "center", gap: "6px" }}><span style=${ { fontSize: "15px", fontWeight: 600 } }>Height</span><${InlineInfo} title="Height" text="Ensures correct sleeve and jacket length." /></div>
                <span style=${ { fontSize: "15px", fontWeight: 600 } }>${heightLabel}</span>
              </div>
              <div style=${ { marginTop: "4px", display: "flex", justifyContent: "flex-end" } }><${SegToggle} left=${ { label: "FT", value: "ft" } } right=${ { label: "CM", value: "cm" } } value=${heightMode} onChange=${setHeightMode} /></div>
              <input type="range" min=${140} max=${220} value=${heightCm} onChange=${(e) => setHeightCm(Number(e.target.value))} style=${ { width: "100%", marginTop: "8px" } } />
            </div>
            <div style=${metricCardStyle}>
              <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center" } }>
                <div style=${{ display: "flex", alignItems: "center", gap: "6px" }}><span style=${ { fontSize: "15px", fontWeight: 600 } }>Weight</span><${InlineInfo} title="Weight" text="Calibrates chest and shoulder width." /></div>
                <span style=${ { fontSize: "15px", fontWeight: 600 } }>${weightLabel}</span>
              </div>
              <div style=${ { marginTop: "4px", display: "flex", justifyContent: "flex-end" } }><${SegToggle} left=${ { label: "KG", value: "kg" } } right=${ { label: "LBS", value: "lbs" } } value=${weightMode} onChange=${setWeightMode} /></div>
              <input type="range" min=${weightMode === "kg" ? 45 : 100} max=${weightMode === "kg" ? 140 : 320} value=${weightSliderValue} onChange=${(e) => setWeightFromSlider(e.target.value)} style=${ { width: "100%", marginTop: "8px" } } />
            </div>
            <div style=${metricCardStyle}>
              <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center" } }>
                <div style=${{ display: "flex", alignItems: "center", gap: "6px" }}><span style=${ { fontSize: "15px", fontWeight: 600 } }>Age</span><${InlineInfo} title="Age" text="Fine-tunes silhouette for natural posture." /></div>
                <span style=${ { fontSize: "15px", fontWeight: 600 } }>${age} years</span>
              </div>
              <input type="range" min=${18} max=${90} value=${age} onChange=${(e) => setAge(Number(e.target.value))} style=${ { width: "100%", marginTop: "12px" } } />
            </div>
            <div style=${{ ...metricCardStyle, marginBottom: "0" }}>
              <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center" } }>
                <div style=${{ display: "flex", alignItems: "center", gap: "6px" }}><span style=${ { fontSize: "15px", fontWeight: 600 } }>Shoe Size</span><${InlineInfo} title="Shoe" text="Predicts frame size." /></div>
                <span style=${ { fontSize: "15px", fontWeight: 600 } }>${shoe} US</span>
              </div>
              <input type="range" min=${6} max=${14} step=${0.5} value=${shoe} onChange=${(e) => setShoe(Number(e.target.value))} style=${ { width: "100%", marginTop: "12px" } } />
            </div>
          `}
          ${step === 1 && html`
            <div style=${ { fontSize: "32px", fontWeight: 700, marginBottom: "8px", letterSpacing: "-0.04em", fontFamily: "var(--zb-font-heading)" } }>Build</div>
            <div style=${ { opacity: 0.6, marginBottom: "20px", fontSize: "14px" } }>Select the closest match.</div>
            <div style=${ { display: "flex", flexDirection: "column", gap: "10px" } }>
              ${BODY_SHAPES.map((o) => html`
                <button key=${o.key} type="button" className=${`zane-fit-btn ${bodyShape === o.key ? "is-active" : ""}`} onClick=${() => setBodyShape(o.key)} style=${ { display: "flex", alignItems: "center", gap: "14px", padding: "14px 16px", width: "100%", textAlign: "left" } }>
                  <div style=${ { width: "44px", height: "44px", borderRadius: "10px", border: "1px solid rgba(0,0,0,.08)", background: "#fff", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 } }>${o.icon}</div>
                  <div style=${ { flex: 1 } }><div style=${ { fontWeight: 700, fontSize: "13px", marginBottom: "2px" } }>${o.label}</div><div style=${ { fontSize: "12px", opacity: 0.6, lineHeight: "1.3" } }>${o.desc}</div></div>
                </button>
              `)}
            </div>
            ${error && html`<div style=${ { marginTop: "12px", color: "#c00", fontWeight: 600 } }>${error}</div>`}
          `}
          ${step === 2 && html`
            <div style=${ { fontSize: "32px", fontWeight: 700, marginBottom: "8px", letterSpacing: "-0.04em", fontFamily: "var(--zb-font-heading)" } }>Fit Preference</div>
            <div style=${ { opacity: 0.6, marginBottom: "20px", fontSize: "14px" } }>Select your silhouette.</div>
            <div style=${ { display: "flex", flexDirection: "column", gap: "10px" } }>
              ${FIT_PREFS.map((o) => html`
                <button key=${o.key} type="button" className=${`zane-fit-btn ${fitPref === o.key ? "is-active" : ""}`} onClick=${() => setFitPref(o.key)} style=${ { display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", padding: "18px 16px", width: "100%", textAlign: "center" } }>
                  <div style=${ { fontWeight: 700, fontSize: "13px" } }>${o.label}</div>
                  <div style=${ { fontSize: "12px", opacity: 0.6, lineHeight: "1.3" } }>${o.desc}</div>
                </button>
              `)}
            </div>
            ${error && html`<div style=${ { marginTop: "12px", color: "#c00", fontWeight: 600 } }>${error}</div>`}
          `}
          ${step === 3 && html`
            <div style=${ { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", flex: 1, padding: "0 10px" } }>
              <div className="zane-success-icon" style=${{ marginBottom: "10px" }}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg></div>
              
              <div style=${{ marginBottom: "12px" }}>
                <h2 style=${{ fontSize: "26px", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: "1.1", color: "#111", margin: "0 0 6px 0", fontFamily: "var(--zb-font-heading)" }}>
                  ${recommended?.outOfRange || recommended?.fitNote ? "Fit Optimization" : "Your Size"}
                </h2>
                <p style=${{ color: "rgba(0,0,0,0.5)", fontSize: "13px", fontWeight: 500, maxWidth: "300px", margin: "0 auto", lineHeight: "1.4" }}>
                  ${recommended?.fitNote ? "Standard size may restrict movement in key areas." : "Based on your profile."}
                </p>
              </div>

              <div style=${{ marginBottom: "12px", width: "100%", background: "#fff", border: "1px solid rgba(0,0,0,.08)", borderRadius: "10px", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style=${{ fontSize: "11px", letterSpacing: ".05em", fontWeight: 600, color: "rgba(0,0,0,0.5)" }}>
                  ${isShirt ? "Collar Size" : "Standard Size"}
                </span>
                <span style=${{ fontSize: "20px", fontWeight: 700, letterSpacing: "-0.02em", fontFamily: "var(--zb-font-heading)" }}>${displayCode}</span>
              </div>

              ${recommended?.fitNote && html`
                <div style=${{ marginBottom: "12px", padding: "12px 14px", background: "#fefce8", borderRadius: "8px", border: "1px solid rgba(202,138,4,0.3)", textAlign: "left", width: "100%", boxSizing: "border-box" }}>
                  <div style=${{ marginBottom: "6px", fontSize: "11px", fontWeight: 700, color: "#a16207", letterSpacing: "0.02em" }}>Optimization Recommended</div>
                  <p style=${{ fontSize: "12px", lineHeight: "1.45", color: "#854d0e", margin: 0 }}>${recommended.fitNote}</p>
                </div>
              `}

              <div style=${{ marginTop: "4px", marginBottom: "8px", width: "100%" }}>
                <${TrustFeatureRow} />
              </div>

              <p className="zb-ethical-statement" style=${{ marginBottom: "16px", textAlign: "left", maxWidth: "100%", paddingLeft: "0", paddingRight: "0" }}>
                ZB Fit Intelligence™ uses biometric inputs to recommend size with precision and discretion. No data is stored. No profiles are sold. Fit is treated as a responsibility.
              </p>

              <div style=${{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                <button type="button" className="zane-fit-cta" onClick=${goCustomize} style=${{ padding: "16px", fontSize: "12px", background: "#000", color: "#fff", border: "none" }}>
                  Choose Made to Measure
                </button>
                
                <button type="button" className="zane-fit-back" onClick=${applyStandard} style=${{ padding: "14px", fontSize: "12px", background: "#fff", color: "#111", border: "1px solid rgba(0,0,0,0.12)" }}>
                  Keep Standard ${displayCode}
                </button>
              </div>
            </div>
          `}
        </div>
        <div className="zane-fit-footer-glass" style=${ { padding: "16px 22px" } }>
          ${step < 3 ? html`
            <div style=${ { display: "flex", gap: "12px" } }>
              ${step > 0 && html`<button type="button" className="zane-fit-back" onClick=${back} style=${{ flex: 1 }}>Back</button>`}
              <button type="button" className="zane-fit-cta" onClick=${next} style=${{ flex: step > 0 ? 2.5 : 1 }}>NEXT</button>
            </div>
          ` : html`
            <div style=${{ textAlign: "center", fontSize: "11px", color: "#6b7280" }}>
              Your recommendation is based on your profile inputs.
            </div>
          `}
        </div>
      </div>
    </div>
  `;
};

const CustomSizesTab = ({ product, onAddCustomToCart, customSpecs }) => {
  const category = getProductCategory(product);
  const isSuit = category === "sb_suit" || category === "db_suit";
  const isJacketOnly = category === "sb_jacket" || category === "db_jacket";
  const isShirt = category === "shirt";
  const isPeacoat = category === "peacoat";
  const isOvercoat = category === "sb_overcoat" || category === "db_overcoat";
  const isWaistcoat = category === "waistcoat";
  
  const [screen, setScreen] = useState("intro");
  const [wizStep, setWizStep] = useState(0);
  const [error, setError] = useState("");
  const [heightMode, setHeightMode] = useState("ft");
  const [heightCm, setHeightCm] = useState(180);
  const [weightMode, setWeightMode] = useState("kg");
  const [weightKg, setWeightKg] = useState(85);
  const [shoe, setShoe] = useState(11);
  const [age, setAge] = useState(35);
  const [bodyShape, setBodyShape] = useState(null);
  const [fitPref, setFitPref] = useState(null);
  const [unit, setUnit] = useState("in");
  const [baseMeasurements, setBaseMeasurements] = useState(null);
  const [measurements, setMeasurements] = useState(null);
  const [cartError, setCartError] = useState("");
  const [autoUpdatedFields, setAutoUpdatedFields] = useState({}); // Track auto-populated fields
  const [loadedFromPassport, setLoadedFromPassport] = useState(false); // Track if we loaded saved data
  const [infoKey, setInfoKey] = useState(null); // ZB-DIAGRAMS: which measurement diagram popover is open

  // AUTO-LOAD: Priority 1 = Order History (ZanePassport), Priority 2 = Local Storage
  useEffect(() => {
    let loadedProfile = null;

    // A. Check Server History (Passport from Liquid)
    if (window.ZanePassport?.savedProfile) {
      console.log("Size Passport: Loaded from Order History");
      loadedProfile = window.ZanePassport.savedProfile;
      setLoadedFromPassport(true);
    } 
    // B. Check Local Device (Drafts)
    else {
      const localDraft = localStorage.getItem("zane_mtm_profile");
      if (localDraft) {
        try { 
          loadedProfile = JSON.parse(localDraft); 
          console.log("Size Passport: Loaded from Local Draft");
        } catch(e) {}
      }
    }

    // Apply profile data if found
    if (loadedProfile) {
      if (typeof loadedProfile.heightCm === "number") setHeightCm(loadedProfile.heightCm);
      if (typeof loadedProfile.weightKg === "number") setWeightKg(loadedProfile.weightKg);
      if (typeof loadedProfile.shoe === "number") setShoe(loadedProfile.shoe);
      if (typeof loadedProfile.age === "number") setAge(loadedProfile.age);
      if (loadedProfile.bodyShape) setBodyShape(loadedProfile.bodyShape);
      if (loadedProfile.fitPref) setFitPref(loadedProfile.fitPref);

      // FIX: Auto-Navigate to the Result Screen if we have enough data
      if (loadedProfile.bodyShape && loadedProfile.fitPref) {
        setScreen("wizard");
        setWizStep(3); // Jump straight to "Your Fit"
        setError("");  // Clear any errors
      } else {
        // If we have partial data (height/weight) but no shape, jump to start of wizard
        setScreen("wizard");
        setWizStep(0);
      }
    }
  }, []);

  // AUTO-SAVE: Save profile to Local Storage whenever user changes inputs
  useEffect(() => {
    // Only save if user has started the wizard
    if (screen !== "intro") {
      const profile = { heightCm, weightKg, shoe, age, bodyShape, fitPref };
      localStorage.setItem("zane_mtm_profile", JSON.stringify(profile));
    }
  }, [heightCm, weightKg, shoe, age, bodyShape, fitPref, screen]);

  // Handle customSpecs from FindYourSizeModal
  useEffect(() => {
    if (customSpecs && customSpecs.idealCustomSpecs) {
      // Pre-fill form data
      if (typeof customSpecs.heightCm === "number") setHeightCm(customSpecs.heightCm);
      if (typeof customSpecs.weightKg === "number") setWeightKg(customSpecs.weightKg);
      if (typeof customSpecs.shoe === "number") setShoe(customSpecs.shoe);
      if (typeof customSpecs.age === "number") setAge(customSpecs.age);
      if (customSpecs.bodyShape) setBodyShape(customSpecs.bodyShape);
      if (customSpecs.fitPref) setFitPref(customSpecs.fitPref);
      setScreen("wizard");
      setWizStep(3);
      setError("");
      
      // Mark which fields will be auto-updated (includes hip now)
      setAutoUpdatedFields({ waist: true, hip: true, thigh: true });
    }
  }, [customSpecs]);

  useEffect(() => {
    const handler = (e) => {
      const d = (e && e.detail) || null;
      if (!d) return;
      if (typeof d.heightCm === "number") setHeightCm(d.heightCm);
      if (typeof d.weightKg === "number") setWeightKg(d.weightKg);
      if (typeof d.shoe === "number") setShoe(d.shoe);
      if (typeof d.age === "number") setAge(d.age);
      if (d.bodyShape) setBodyShape(d.bodyShape);
      if (d.fitPref) setFitPref(d.fitPref);
      if (d.idealCustomSpecs) {
        setAutoUpdatedFields({ waist: true, hip: true, thigh: true });
      }
      setScreen("wizard");
      setWizStep(3);
      setError("");
    };
    window.addEventListener("zane:prefill-custom", handler);
    return () => window.removeEventListener("zane:prefill-custom", handler);
  }, []);

  const bodyRec = useMemo(() => {
    if (screen === "intro") return null;
    return recommendFromBody({ heightCm, weightKg, shoe, age }, { bodyShape, fitPref, category });
  }, [screen, heightCm, weightKg, shoe, age, bodyShape, fitPref, category]);

  const recommended = useMemo(() => {
    if (!bodyRec) return null;
    // recommendFromBody already returns the correct jacketSize for all categories
    // including shirts (using neck-based sizing)
    if (isShirt) {
      return { ...bodyRec, displaySize: bodyRec.jacketSize, isCollarSize: true };
    }
    return { ...bodyRec, displaySize: bodyRec.jacketSize, isCollarSize: false };
  }, [bodyRec, isShirt]);

  const heightLabel = useMemo(() => heightMode === "cm" ? `${Math.round(heightCm)} cm` : `${toFtInFromCm(heightCm).ft}ft ${toFtInFromCm(heightCm).inch}in`, [heightMode, heightCm]);
  const weightLabel = useMemo(() => weightMode === "kg" ? `${Math.round(weightKg)} kg` : `${kgToLbs(weightKg)} lb`, [weightMode, weightKg]);
  const weightSliderValue = useMemo(() => weightMode === "kg" ? Math.round(weightKg) : kgToLbs(weightKg), [weightMode, weightKg]);
  const setWeightFromSlider = (val) => weightMode === "kg" ? setWeightKg(Number(val)) : setWeightKg(lbsToKg(Number(val)));
  const deltaIn = useMemo(() => unit === "in" ? 0.2 : cmToIn(0.5), [unit]);
  const goCreate = () => { setScreen("wizard"); setWizStep(0); setError(""); setBodyShape(null); setFitPref(null); };
  const next = () => { setError(""); if (wizStep === 1 && !bodyShape) return setError("Please select a body shape."); if (wizStep === 2 && !fitPref) return setError("Please select a fit preference."); setWizStep(wizStep + 1); };
  const back = () => { if (screen === "measures") { setScreen("wizard"); setWizStep(3); return; } if (wizStep === 0) { setScreen("intro"); return; } setWizStep(wizStep - 1); };

  const finishWizard = () => {
    if (!bodyShape || !fitPref) return setError("Complete all selections.");
    
    // 1. Re-Calculate Recommendation
    const rec = recommendFromBody({ heightCm, weightKg, shoe, age }, { bodyShape, fitPref, category });
    const lk = getLengthKey(rec.lengthKey);
    let base = null;
    
    // 2. Build Base Measurements (Standard)
    if (isShirt) {
      // rec.jacketSize already has the correct collar size from recommendFromBody (neck-based)
      const collarSize = rec.jacketSize;
      const collarKey = String(collarSize);
      const specs = SHIRT_CHART_IN[lk]?.[collarKey];
      base = {
        unit: "in",
        shirt: {
          collar: specs?.collar ?? collarSize,
          chest: specs?.chest ?? null,
          waist: specs?.waist ?? null,
          hip: specs?.hip ?? null,
          length: specs?.length ?? null,
          sleeveRight: specs?.sleeveRight ?? null,
          sleeveLeft: specs?.sleeveLeft ?? null,
          cuffRight: specs?.cuffRight ?? null,
          cuffLeft: specs?.cuffLeft ?? null,
          bicep: specs?.bicep ?? null,
          forearm: specs?.forearm ?? null,
          shoulder: specs?.shoulder ?? null
        },
        trouser: null
      };
    } else if (isPeacoat) {
      const specs = PEACOAT_CHART_IN[lk]?.[rec.jacketSize];
      base = {
        unit: "in",
        jacket: {
          chest: specs?.chest ?? null, waist: specs?.waist ?? null, length: specs?.length ?? null, sleeve: specs?.sleeve ?? null,
          hip: specs?.hip ?? null, shoulder: specs?.shoulder ?? null, back: specs?.back ?? null, vent: specs?.vent ?? null,
          frontLength: specs?.frontLength ?? null, bicep: specs?.bicep ?? null, sleeveOpening: specs?.sleeveOpening ?? null
        },
        trouser: null
      };
    } else if (isOvercoat) {
      const chart = getOvercoatChart(category);
      const specs = chart[lk]?.[rec.jacketSize];
      base = {
        unit: "in",
        jacket: {
          chest: specs?.chest ?? null, waist: specs?.waist ?? null, length: specs?.length ?? null, sleeve: specs?.sleeve ?? null,
          hip: specs?.hip ?? null, shoulder: specs?.shoulder ?? null, back: specs?.back ?? null, vent: specs?.vent ?? null,
          frontLength: specs?.frontLength ?? null, bicep: specs?.bicep ?? null, sleeveOpening: specs?.sleeveOpening ?? null
        },
        trouser: null
      };
    } else {
      // SUIT / JACKET LOGIC
      const jacketChart = getJacketChart(category);
      const trouserChart = getTrouserChart(category);
      const jacket = jacketChart[lk]?.[rec.jacketSize];
      
      // Drop-6 or Recommendation override
      const trouserSize = isSuit && rec.trouserSize ? rec.trouserSize : (rec.jacketSize - 6);
      const trouser = isSuit ? trouserChart[lk]?.[trouserSize] : null;
      
      // 3. APPLY OPTIMIZATION LOGIC (The Fix)
      let trouserMeasures = null;
      let newAutoFields = {}; // Track which fields are optimized
      
      if (isSuit && trouser) {
        trouserMeasures = {
          waist: trouser?.waist ?? null,
          hip: trouser?.hip ?? null,
          thigh: trouser?.thigh ?? null,
          knee: trouser?.knee ?? null,
          hem: trouser?.hem ?? null,
          inseam: trouser?.inseam ?? null,
          rise: trouser?.rise ?? null,
          backRise: trouser?.backRise ?? null
        };
        
        // Check for Custom Specs (passed via props OR calculated live)
        const specsToApply = customSpecs?.idealCustomSpecs || rec.idealCustomSpecs;
        
        if (specsToApply) {
          // Apply Waist
          if (specsToApply.waist) {
            trouserMeasures.waist = specsToApply.waist;
            newAutoFields.waist = true; // Turn on Badge
          }
          // Apply Hip
          if (specsToApply.hip) {
            trouserMeasures.hip = specsToApply.hip;
            newAutoFields.hip = true; // Turn on Badge
          }
          // Apply Thigh
          if (specsToApply.thigh) {
            trouserMeasures.thigh = specsToApply.thigh;
            newAutoFields.thigh = true; // Turn on Badge
          }
          // Apply others if present
          if (specsToApply.knee) trouserMeasures.knee = specsToApply.knee;
          if (specsToApply.hem) trouserMeasures.hem = specsToApply.hem;
          if (specsToApply.inseam) trouserMeasures.inseam = specsToApply.inseam;
          if (specsToApply.rise) trouserMeasures.rise = specsToApply.rise;
          if (specsToApply.backRise) trouserMeasures.backRise = specsToApply.backRise;
        }
      }
      
      base = {
        unit: "in",
        jacket: {
          chest: jacket?.chest ?? null, waist: jacket?.waist ?? null, length: jacket?.length ?? null, sleeve: jacket?.sleeve ?? null,
          hip: jacket?.hip ?? null, shoulder: jacket?.shoulder ?? null, back: jacket?.back ?? null, vent: jacket?.vent ?? null,
          frontLength: jacket?.frontLength ?? null, bicep: jacket?.bicep ?? null, sleeveOpening: jacket?.sleeveOpening ?? null
        },
        trouser: trouserMeasures,
        fitNote: rec.fitNote // Pass fitNote to measurements for display
      };
      
      // TRIGGER THE BADGES (The Fix)
      setAutoUpdatedFields(newAutoFields);
    }

    setBaseMeasurements(base);
    setMeasurements(JSON.parse(JSON.stringify(base)));
    // ZB-PROFILE: if the customer confirmed (locked) saved measurements on their Fit Profile,
    // start from those so this order carries their corrected fit. Only overrides fields the
    // derived base already has, and only when explicitly locked. Sizing math is untouched.
    try {
      var __zbsp = window.ZBProfileStore && window.ZBProfileStore.read();
      if (__zbsp && __zbsp.measurementsLocked && __zbsp.measurements) {
        var __merged = JSON.parse(JSON.stringify(base));
        ["jacket", "trouser", "shirt"].forEach(function (g) {
          if (__merged[g] && __zbsp.measurements[g]) {
            Object.keys(__zbsp.measurements[g]).forEach(function (f) {
              if (typeof __zbsp.measurements[g][f] === "number" && __merged[g][f] != null) {
                __merged[g][f] = __zbsp.measurements[g][f];
              }
            });
          }
        });
        setMeasurements(__merged);
      }
    } catch (e) {}
    setUnit("in");
    setScreen("measures");
  };

  const resetToStandard = () => { if (baseMeasurements) setMeasurements(JSON.parse(JSON.stringify(baseMeasurements))); };
  const adjust = (path, dir) => { setMeasurements((prev) => { if (!prev) return prev; const m = JSON.parse(JSON.stringify(prev)); const cur = m[path[0]]?.[path[1]]; if (cur == null) return prev; m[path[0]][path[1]] = adjustMeasure(cur, dir * deltaIn); return m; }); };

  const buildProps = () => { 
    if (!recommended || !measurements) return null; 
    const displayCode = `${recommended.displaySize}${recommended.lengthKey[0]}`;
    
    if (isShirt && measurements.shirt) {
      // Build details string for accordion
      const details = [
        `Fit: ${fitPref || "—"}`,
        `Build: ${bodyShape || "—"}`,
        `Collar: ${formatMeasure(measurements.shirt.collar, unit)} ${unit}`,
        `Chest: ${formatMeasure(measurements.shirt.chest, unit)} ${unit}`,
        `Waist: ${formatMeasure(measurements.shirt.waist, unit)} ${unit}`,
        `Hip: ${formatMeasure(measurements.shirt.hip, unit)} ${unit}`,
        `Length: ${formatMeasure(measurements.shirt.length, unit)} ${unit}`,
        `Sleeve R: ${formatMeasure(measurements.shirt.sleeveRight, unit)} ${unit}`,
        `Sleeve L: ${formatMeasure(measurements.shirt.sleeveLeft, unit)} ${unit}`,
        `Shoulder: ${formatMeasure(measurements.shirt.shoulder, unit)} ${unit}`
      ].join(" · ");
      
      return {
        "ZB Mode": "Made to Measure",
        "Base Size": displayCode,
        "_zb_details": details,
        // Hidden profile data for ZanePassport (prefixed with _)
        "_profileHeightCm": String(Math.round(heightCm)),
        "_profileWeightKg": String(Math.round(weightKg)),
        "_profileShoe": String(shoe),
        "_profileAge": String(age),
        "_profileBodyShape": bodyShape || "",
        "_profileFitPref": fitPref || "",
        // Individual measurements for production (hidden with _ prefix)
        "_collar": `${formatMeasure(measurements.shirt.collar, unit)} ${unit}`,
        "_chest": `${formatMeasure(measurements.shirt.chest, unit)} ${unit}`,
        "_waist": `${formatMeasure(measurements.shirt.waist, unit)} ${unit}`,
        "_hip": `${formatMeasure(measurements.shirt.hip, unit)} ${unit}`,
        "_length": `${formatMeasure(measurements.shirt.length, unit)} ${unit}`,
        "_sleeveR": `${formatMeasure(measurements.shirt.sleeveRight, unit)} ${unit}`,
        "_sleeveL": `${formatMeasure(measurements.shirt.sleeveLeft, unit)} ${unit}`,
        "_cuffR": `${formatMeasure(measurements.shirt.cuffRight, unit)} ${unit}`,
        "_cuffL": `${formatMeasure(measurements.shirt.cuffLeft, unit)} ${unit}`,
        "_bicep": `${formatMeasure(measurements.shirt.bicep, unit)} ${unit}`,
        "_forearm": `${formatMeasure(measurements.shirt.forearm, unit)} ${unit}`,
        "_shoulder": `${formatMeasure(measurements.shirt.shoulder, unit)} ${unit}`,
        "_unit": unit.toUpperCase()
      };
    }
    
    // Build details string for jacket/suit accordion
    const jacketDetails = [
      `Fit: ${fitPref || "—"}`,
      `Build: ${bodyShape || "—"}`,
      `Chest: ${formatMeasure(measurements.jacket?.chest, unit)} ${unit}`,
      `Waist: ${formatMeasure(measurements.jacket?.waist, unit)} ${unit}`,
      `Shoulder: ${formatMeasure(measurements.jacket?.shoulder, unit)} ${unit}`,
      `Sleeve: ${formatMeasure(measurements.jacket?.sleeve, unit)} ${unit}`,
      `Length: ${formatMeasure(measurements.jacket?.length, unit)} ${unit}`
    ].join(" · ");
    
    const props = {
      "ZB Mode": "Made to Measure",
      "Base Size": displayCode,
      "_zb_details": jacketDetails,
      // Hidden profile data for ZanePassport (prefixed with _)
      "_profileHeightCm": String(Math.round(heightCm)),
      "_profileWeightKg": String(Math.round(weightKg)),
      "_profileShoe": String(shoe),
      "_profileAge": String(age),
      "_profileBodyShape": bodyShape || "",
      "_profileFitPref": fitPref || "",
      // Individual measurements for production (hidden with _ prefix)
      "_fitPref": fitPref || "—",
      "_bodyShape": bodyShape || "—",
      "_height": String(Math.round(heightCm)),
      "_weight": String(Math.round(weightKg)),
      "_shoe": String(shoe),
      "_age": String(age),
      "_unit": unit.toUpperCase(),
      "_jacketChest": `${formatMeasure(measurements.jacket?.chest, unit)} ${unit}`,
      "_jacketWaist": `${formatMeasure(measurements.jacket?.waist, unit)} ${unit}`,
      "_jacketLength": `${formatMeasure(measurements.jacket?.length, unit)} ${unit}`,
      "_jacketSleeve": `${formatMeasure(measurements.jacket?.sleeve, unit)} ${unit}`,
      "_jacketHip": `${formatMeasure(measurements.jacket?.hip, unit)} ${unit}`,
      "_jacketShoulder": `${formatMeasure(measurements.jacket?.shoulder, unit)} ${unit}`,
      "_jacketBack": `${formatMeasure(measurements.jacket?.back, unit)} ${unit}`,
      "_jacketVent": `${formatMeasure(measurements.jacket?.vent, unit)} ${unit}`,
      "_jacketFrontLength": `${formatMeasure(measurements.jacket?.frontLength, unit)} ${unit}`,
      "_jacketBicep": `${formatMeasure(measurements.jacket?.bicep, unit)} ${unit}`,
      "_jacketSleeveOpening": `${formatMeasure(measurements.jacket?.sleeveOpening, unit)} ${unit}`
    };
    
    if (isSuit && measurements.trouser) {
      props["_trouserWaist"] = `${formatMeasure(measurements.trouser.waist, unit)} ${unit}`;
      props["_trouserHip"] = `${formatMeasure(measurements.trouser.hip, unit)} ${unit}`;
      props["_trouserThigh"] = `${formatMeasure(measurements.trouser.thigh, unit)} ${unit}`;
      props["_trouserKnee"] = `${formatMeasure(measurements.trouser.knee, unit)} ${unit}`;
      props["_trouserHem"] = `${formatMeasure(measurements.trouser.hem, unit)} ${unit}`;
      props["_trouserInseam"] = `${formatMeasure(measurements.trouser.inseam, unit)} ${unit}`;
      props["_trouserRise"] = `${formatMeasure(measurements.trouser.rise, unit)} ${unit}`;
      props["_trouserBackRise"] = `${formatMeasure(measurements.trouser.backRise, unit)} ${unit}`;
    }
    // ZB-PROFILE: one consolidated JSON of the saved profile, so the account Fit Profile page can
    // read the customer's measurements straight from their order history (any device, when signed in),
    // and so a future Flow/app can sync it to the customer record in a single field copy.
    try {
      props["_zb_fit_json"] = JSON.stringify({ v: 1, mode: "mtm", unit: unit, inputs: { heightCm: heightCm, weightKg: weightKg, shoe: shoe, age: age, bodyShape: bodyShape, fitPref: fitPref }, measurements: measurements });
    } catch (e) {}
    return props;
  };

  const addToCart = async () => { try { setCartError(""); if (!recommended || !measurements) return;
    // ZB-PROFILE: persist the made-to-measure measurements (and the inputs that produced them)
    // so they carry to the account and the customer's next order. In CustomSizesTab scope.
    try { if (window.ZBProfileStore) window.ZBProfileStore.save({ _source: "mtm", mode: "mtm", unit: unit, inputs: { heightCm: heightCm, weightKg: weightKg, shoe: shoe, age: age, bodyShape: bodyShape, fitPref: fitPref }, measurements: measurements }); } catch (e) {}
    await onAddCustomToCart({ recommended, properties: buildProps() }); } catch { setCartError("Failed to add to cart. Please try again."); } };
  const WIZARD_TABS = ["Profile", "Build", "Fit Preference", "Base Size"];
  const displayCode = recommended ? `${recommended.displaySize}${recommended.lengthKey[0]}` : "—";

  const renderMeasurementRows = () => {
    if (isShirt && measurements?.shirt) {
      return html`
        <div style=${ { fontSize: "14px", fontWeight: 900, marginBottom: "8px", opacity: 0.6 } }>SHIRT</div>
        <${MeasureRow} label="Collar Around" valueIn=${measurements.shirt.collar} unit=${unit} onMinus=${() => adjust(["shirt", "collar"], -1)} onPlus=${() => adjust(["shirt", "collar"], 1)} diagramKey="shirtCollar" onInfo=${setInfoKey} />
        <${MeasureRow} label="Chest Width" valueIn=${measurements.shirt.chest} unit=${unit} onMinus=${() => adjust(["shirt", "chest"], -1)} onPlus=${() => adjust(["shirt", "chest"], 1)} diagramKey="shirtChest" onInfo=${setInfoKey} />
        <${MeasureRow} label="Waist Width" valueIn=${measurements.shirt.waist} unit=${unit} onMinus=${() => adjust(["shirt", "waist"], -1)} onPlus=${() => adjust(["shirt", "waist"], 1)} diagramKey="shirtWaist" onInfo=${setInfoKey} />
        <${MeasureRow} label="Hip Width" valueIn=${measurements.shirt.hip} unit=${unit} onMinus=${() => adjust(["shirt", "hip"], -1)} onPlus=${() => adjust(["shirt", "hip"], 1)} diagramKey="shirtHip" onInfo=${setInfoKey} />
        <${MeasureRow} label="Shirt Length" valueIn=${measurements.shirt.length} unit=${unit} onMinus=${() => adjust(["shirt", "length"], -1)} onPlus=${() => adjust(["shirt", "length"], 1)} diagramKey="shirtLength" onInfo=${setInfoKey} />
        <${MeasureRow} label="Sleeve Length Right" valueIn=${measurements.shirt.sleeveRight} unit=${unit} onMinus=${() => adjust(["shirt", "sleeveRight"], -1)} onPlus=${() => adjust(["shirt", "sleeveRight"], 1)} diagramKey="shirtSleeve" onInfo=${setInfoKey} />
        <${MeasureRow} label="Sleeve Length Left" valueIn=${measurements.shirt.sleeveLeft} unit=${unit} onMinus=${() => adjust(["shirt", "sleeveLeft"], -1)} onPlus=${() => adjust(["shirt", "sleeveLeft"], 1)} diagramKey="shirtSleeve" onInfo=${setInfoKey} />
        <${MeasureRow} label="Cuff Width Right" valueIn=${measurements.shirt.cuffRight} unit=${unit} onMinus=${() => adjust(["shirt", "cuffRight"], -1)} onPlus=${() => adjust(["shirt", "cuffRight"], 1)} diagramKey="shirtCuff" onInfo=${setInfoKey} />
        <${MeasureRow} label="Cuff Width Left" valueIn=${measurements.shirt.cuffLeft} unit=${unit} onMinus=${() => adjust(["shirt", "cuffLeft"], -1)} onPlus=${() => adjust(["shirt", "cuffLeft"], 1)} diagramKey="shirtCuff" onInfo=${setInfoKey} />
        <${MeasureRow} label="Bicep Around" valueIn=${measurements.shirt.bicep} unit=${unit} onMinus=${() => adjust(["shirt", "bicep"], -1)} onPlus=${() => adjust(["shirt", "bicep"], 1)} diagramKey="shirtBicep" onInfo=${setInfoKey} />
        <${MeasureRow} label="Forearm Around" valueIn=${measurements.shirt.forearm} unit=${unit} onMinus=${() => adjust(["shirt", "forearm"], -1)} onPlus=${() => adjust(["shirt", "forearm"], 1)} diagramKey="shirtForearm" onInfo=${setInfoKey} />
        <${MeasureRow} label="Shoulder Width" valueIn=${measurements.shirt.shoulder} unit=${unit} onMinus=${() => adjust(["shirt", "shoulder"], -1)} onPlus=${() => adjust(["shirt", "shoulder"], 1)} diagramKey="shirtShoulder" onInfo=${setInfoKey} />
      `;
    }
    
    if (measurements?.jacket) {
      const itemLabel = isPeacoat ? 'PEACOAT' : isOvercoat ? 'OVERCOAT' : 'JACKET';
      return html`
        <div style=${ { fontSize: "14px", fontWeight: 900, marginBottom: "8px", opacity: 0.6 } }>${itemLabel}</div>
        <${MeasureRow} label="Chest Width" valueIn=${measurements.jacket.chest} unit=${unit} onMinus=${() => adjust(["jacket", "chest"], -1)} onPlus=${() => adjust(["jacket", "chest"], 1)} diagramKey="jacketChest" onInfo=${setInfoKey} />
        <${MeasureRow} label="Waist Width" valueIn=${measurements.jacket.waist} unit=${unit} onMinus=${() => adjust(["jacket", "waist"], -1)} onPlus=${() => adjust(["jacket", "waist"], 1)} diagramKey="jacketWaist" onInfo=${setInfoKey} />
        <${MeasureRow} label="Jacket Length" valueIn=${measurements.jacket.length} unit=${unit} onMinus=${() => adjust(["jacket", "length"], -1)} onPlus=${() => adjust(["jacket", "length"], 1)} diagramKey="jacketLength" onInfo=${setInfoKey} />
        <${MeasureRow} label="Sleeve Length" valueIn=${measurements.jacket.sleeve} unit=${unit} onMinus=${() => adjust(["jacket", "sleeve"], -1)} onPlus=${() => adjust(["jacket", "sleeve"], 1)} diagramKey="jacketSleeve" onInfo=${setInfoKey} />
        <${MeasureRow} label="Hip Width" valueIn=${measurements.jacket.hip} unit=${unit} onMinus=${() => adjust(["jacket", "hip"], -1)} onPlus=${() => adjust(["jacket", "hip"], 1)} diagramKey="jacketHip" onInfo=${setInfoKey} />
        <${MeasureRow} label="Shoulder Width" valueIn=${measurements.jacket.shoulder} unit=${unit} onMinus=${() => adjust(["jacket", "shoulder"], -1)} onPlus=${() => adjust(["jacket", "shoulder"], 1)} diagramKey="jacketShoulder" onInfo=${setInfoKey} />
        <${MeasureRow} label="Back Width" valueIn=${measurements.jacket.back} unit=${unit} onMinus=${() => adjust(["jacket", "back"], -1)} onPlus=${() => adjust(["jacket", "back"], 1)} diagramKey="jacketBack" onInfo=${setInfoKey} />
        <${MeasureRow} label="Vent Length" valueIn=${measurements.jacket.vent} unit=${unit} onMinus=${() => adjust(["jacket", "vent"], -1)} onPlus=${() => adjust(["jacket", "vent"], 1)} diagramKey="jacketVent" onInfo=${setInfoKey} />
        <${MeasureRow} label="Front Length" valueIn=${measurements.jacket.frontLength} unit=${unit} onMinus=${() => adjust(["jacket", "frontLength"], -1)} onPlus=${() => adjust(["jacket", "frontLength"], 1)} diagramKey="jacketFrontLength" onInfo=${setInfoKey} />
        <${MeasureRow} label="Bicep Around" valueIn=${measurements.jacket.bicep} unit=${unit} onMinus=${() => adjust(["jacket", "bicep"], -1)} onPlus=${() => adjust(["jacket", "bicep"], 1)} diagramKey="jacketBicep" onInfo=${setInfoKey} />
        <${MeasureRow} label="Sleeve Opening Width" valueIn=${measurements.jacket.sleeveOpening} unit=${unit} onMinus=${() => adjust(["jacket", "sleeveOpening"], -1)} onPlus=${() => adjust(["jacket", "sleeveOpening"], 1)} diagramKey="jacketSleeveOpening" onInfo=${setInfoKey} />
        ${isSuit && measurements.trouser && html`
          <div style=${ { fontSize: "14px", fontWeight: 900, marginTop: "24px", marginBottom: "8px", opacity: 0.6 } }>TROUSER</div>
          <${MeasureRow} label="Waist Width" valueIn=${measurements.trouser.waist} unit=${unit} onMinus=${() => adjust(["trouser", "waist"], -1)} onPlus=${() => adjust(["trouser", "waist"], 1)} autoUpdated=${autoUpdatedFields.waist} diagramKey="trouserWaist" onInfo=${setInfoKey} />
          <${MeasureRow} label="Hip/Seat Width" valueIn=${measurements.trouser.hip} unit=${unit} onMinus=${() => adjust(["trouser", "hip"], -1)} onPlus=${() => adjust(["trouser", "hip"], 1)} autoUpdated=${autoUpdatedFields.hip} diagramKey="trouserHip" onInfo=${setInfoKey} />
          <${MeasureRow} label="Thigh Width" valueIn=${measurements.trouser.thigh} unit=${unit} onMinus=${() => adjust(["trouser", "thigh"], -1)} onPlus=${() => adjust(["trouser", "thigh"], 1)} autoUpdated=${autoUpdatedFields.thigh} diagramKey="trouserThigh" onInfo=${setInfoKey} />
          <${MeasureRow} label="Knee Width" valueIn=${measurements.trouser.knee} unit=${unit} onMinus=${() => adjust(["trouser", "knee"], -1)} onPlus=${() => adjust(["trouser", "knee"], 1)} diagramKey="trouserKnee" onInfo=${setInfoKey} />
          <${MeasureRow} label="Hem Width" valueIn=${measurements.trouser.hem} unit=${unit} onMinus=${() => adjust(["trouser", "hem"], -1)} onPlus=${() => adjust(["trouser", "hem"], 1)} diagramKey="trouserHem" onInfo=${setInfoKey} />
          <${MeasureRow} label="Inside-leg Length" valueIn=${measurements.trouser.inseam} unit=${unit} onMinus=${() => adjust(["trouser", "inseam"], -1)} onPlus=${() => adjust(["trouser", "inseam"], 1)} diagramKey="trouserInseam" onInfo=${setInfoKey} />
          <${MeasureRow} label="Front Rise" valueIn=${measurements.trouser.rise} unit=${unit} onMinus=${() => adjust(["trouser", "rise"], -1)} onPlus=${() => adjust(["trouser", "rise"], 1)} diagramKey="trouserRise" onInfo=${setInfoKey} />
          <${MeasureRow} label="Back Rise" valueIn=${measurements.trouser.backRise} unit=${unit} onMinus=${() => adjust(["trouser", "backRise"], -1)} onPlus=${() => adjust(["trouser", "backRise"], 1)} diagramKey="trouserBackRise" onInfo=${setInfoKey} />
        `}
      `;
    }
    return null;
  };

  // Helper: Robust check for Shopify Login Status
  const getLoginStatus = () => {
    // Check 1: Shopify Analytics (Standard on all stores)
    if (window.ShopifyAnalytics?.meta?.page?.customerId) return true;
    
    // Check 2: Global Theme Variable (Common legacy standard)
    if (window.__st?.cid) return true;
    
    // Check 3: DOM Check for Logout link (Fallback)
    if (document.querySelector('a[href*="/account/logout"]')) return true;

    return false;
  };

  const userIsAuth = getLoginStatus();

  return html`
    <div style=${ { display: "flex", flexDirection: "column", height: "100%" } }>
      ${screen === "intro" && html`
        <div style=${ { flex: 1, padding: "24px 22px", overflow: "auto", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" } }>
          <div style=${{ marginBottom: "24px" }}>
            <div style=${{ fontSize: "28px", fontWeight: 700, marginBottom: "8px", fontFamily: "var(--zb-font-heading)" }}>Made-to-Measure</div>
            <div style=${{ fontSize: "14px", lineHeight: 1.5, opacity: 0.6, maxWidth: "300px" }}>
              A fit crafted to your exact measurements.
            </div>
          </div>

          <button type="button" className="zane-fit-cta" onClick=${goCreate} style=${{ background: "#000", color: "#fff", width: "100%", maxWidth: "280px" }}>
            Create My Fit
          </button>
          
          ${!userIsAuth ? html`
            <div style=${ { marginTop: "20px", fontSize: "13px", color: "rgba(0,0,0,0.5)" } }>
              Already have saved sizes?${" "}
              <button type="button" onClick=${() => window.dispatchEvent(new CustomEvent("zane:open-login"))} style=${{ border: 0, background: "transparent", textDecoration: "underline", fontWeight: 700, cursor: "pointer", padding: 0 }}>Log in</button>
            </div>
          ` : html`
            <div style=${ { marginTop: "20px", display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#15803d" } }>
              <span>✓</span>
              <span>Account synced</span>
            </div>
          `}
        </div>
      `}
      ${screen === "wizard" && html`
        <${StepTabs} steps=${WIZARD_TABS} active=${wizStep} />
        <div style=${ { flex: 1, overflow: "auto", padding: "20px 22px" } }>
          ${wizStep === 0 && html`
            <div style=${ { fontSize: "28px", fontWeight: 700, marginBottom: "16px", fontFamily: "var(--zb-font-heading)" } }>Profile</div>
            
            <div style=${{ borderRadius: "14px", background: "rgba(0,0,0,0.03)", padding: "12px 14px", marginBottom: "10px" }}>
              <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center" } }>
                <div style=${ { display: "flex", alignItems: "center", gap: "8px" } }>
                  <span style=${ { fontSize: "15px", fontWeight: 600 } }>Height</span>
                  <${InlineInfo} title="Height" text="Ensures correct sleeve and jacket length." />
                </div>
                <span style=${ { fontSize: "15px", fontWeight: 600 } }>${heightLabel}</span>
              </div>
              <div style=${ { marginTop: "4px", display: "flex", justifyContent: "flex-end" } }>
                <${SegToggle} left=${ { label: "FT", value: "ft" } } right=${ { label: "CM", value: "cm" } } value=${heightMode} onChange=${setHeightMode} />
              </div>
              <input type="range" min=${140} max=${220} value=${heightCm} onChange=${(e) => setHeightCm(Number(e.target.value))} style=${ { width: "100%", marginTop: "8px" } } />
            </div>

            <div style=${{ borderRadius: "14px", background: "rgba(0,0,0,0.03)", padding: "12px 14px", marginBottom: "10px" }}>
              <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center" } }>
                <div style=${ { display: "flex", alignItems: "center", gap: "8px" } }>
                  <span style=${ { fontSize: "15px", fontWeight: 600 } }>Weight</span>
                  <${InlineInfo} title="Weight" text="Calibrates chest and shoulder width." />
                </div>
                <span style=${ { fontSize: "15px", fontWeight: 600 } }>${weightLabel}</span>
              </div>
              <div style=${ { marginTop: "4px", display: "flex", justifyContent: "flex-end" } }>
                <${SegToggle} left=${ { label: "KG", value: "kg" } } right=${ { label: "LBS", value: "lbs" } } value=${weightMode} onChange=${setWeightMode} />
              </div>
              <input type="range" min=${weightMode === "kg" ? 45 : 100} max=${weightMode === "kg" ? 140 : 320} value=${weightSliderValue} onChange=${(e) => setWeightFromSlider(e.target.value)} style=${ { width: "100%", marginTop: "8px" } } />
            </div>

            <div style=${{ borderRadius: "14px", background: "rgba(0,0,0,0.03)", padding: "12px 14px", marginBottom: "10px" }}>
              <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center" } }>
                <div style=${ { display: "flex", alignItems: "center", gap: "8px" } }>
                  <span style=${ { fontSize: "15px", fontWeight: 600 } }>Age</span>
                  <${InlineInfo} title="Age" text="Fine-tunes silhouette for natural posture." />
                </div>
                <span style=${ { fontSize: "15px", fontWeight: 600 } }>${age} years</span>
              </div>
              <input type="range" min=${18} max=${90} value=${age} onChange=${(e) => setAge(Number(e.target.value))} style=${ { width: "100%", marginTop: "12px" } } />
            </div>

            <div style=${{ borderRadius: "14px", background: "rgba(0,0,0,0.03)", padding: "12px 14px", marginBottom: "0" }}>
              <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center" } }>
                <div style=${ { display: "flex", alignItems: "center", gap: "8px" } }>
                  <span style=${ { fontSize: "15px", fontWeight: 600 } }>Shoe Size</span>
                  <${InlineInfo} title="Shoe" text="Predicts frame size." />
                </div>
                <span style=${ { fontSize: "15px", fontWeight: 600 } }>${shoe} US</span>
              </div>
              <input type="range" min=${6} max=${14} step=${0.5} value=${shoe} onChange=${(e) => setShoe(Number(e.target.value))} style=${ { width: "100%", marginTop: "12px" } } />
            </div>
          `}
          ${wizStep === 1 && html`
            <div style=${ { fontSize: "28px", fontWeight: 700, marginBottom: "8px", fontFamily: "var(--zb-font-heading)" } }>Build</div>
            <div style=${ { opacity: 0.6, marginBottom: "16px", fontSize: "14px" } }>Select the closest match.</div>
            <div style=${ { display: "flex", flexDirection: "column", gap: "10px" } }>
              ${BODY_SHAPES.map((o) => html`<button key=${o.key} type="button" className=${`zane-fit-btn ${bodyShape === o.key ? "is-active" : ""}`} onClick=${() => setBodyShape(o.key)} style=${ { display: "flex", alignItems: "center", gap: "14px", padding: "14px 16px", width: "100%", textAlign: "left" } }><div style=${ { width: "44px", height: "44px", borderRadius: "10px", border: "1px solid rgba(0,0,0,.08)", background: "#fff", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 } }>${o.icon}</div><div style=${ { flex: 1 } }><div style=${ { fontWeight: 700, fontSize: "13px", marginBottom: "2px" } }>${o.label}</div><div style=${ { fontSize: "12px", opacity: 0.6, lineHeight: "1.3" } }>${o.desc}</div></div></button>`)}
            </div>
            ${error && html`<div style=${ { marginTop: "12px", color: "#c00", fontWeight: 600 } }>${error}</div>`}
          `}
          ${wizStep === 2 && html`
            <div style=${ { fontSize: "28px", fontWeight: 700, marginBottom: "8px", fontFamily: "var(--zb-font-heading)" } }>Fit Preference</div>
            <div style=${ { opacity: 0.6, marginBottom: "16px", fontSize: "14px" } }>Select your silhouette.</div>
            <div style=${ { display: "flex", flexDirection: "column", gap: "10px" } }>
              ${FIT_PREFS.map((o) => html`<button key=${o.key} type="button" className=${`zane-fit-btn ${fitPref === o.key ? "is-active" : ""}`} onClick=${() => setFitPref(o.key)} style=${ { display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", padding: "18px 16px", width: "100%", textAlign: "center" } }><div style=${ { fontWeight: 700, fontSize: "13px" } }>${o.label}</div><div style=${ { fontSize: "12px", opacity: 0.6, lineHeight: "1.3" } }>${o.desc}</div></button>`)}
            </div>
            ${error && html`<div style=${ { marginTop: "12px", color: "#c00", fontWeight: 600 } }>${error}</div>`}
          `}
          ${wizStep === 3 && html`
            <div style=${ { fontSize: "28px", fontWeight: 700, marginBottom: "8px", fontFamily: "var(--zb-font-heading)" } }>Base Size</div>
            <div style=${ { opacity: 0.6, marginBottom: "16px", fontSize: "14px" } }>Starting from your standard size.</div>
            
            <div style=${ { border: "1px solid rgba(0,0,0,.08)", borderRadius: "10px", padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" } }>
              <span style=${ { fontSize: "12px", fontWeight: 600, color: "rgba(0,0,0,0.5)" } }>
                ${isShirt ? "Collar Size" : "Standard Size"}
              </span>
              <span style=${ { fontSize: "18px", fontWeight: 700, fontFamily: "var(--zb-font-heading)" } }>${displayCode}</span>
            </div>

            ${(autoUpdatedFields.waist || autoUpdatedFields.hip || autoUpdatedFields.thigh) && html`
              <div style=${{ marginTop: "14px", padding: "12px 14px", background: "#f0fdf4", borderRadius: "8px", border: "1px solid rgba(34,197,94,0.2)", textAlign: "left" }}>
                <div style=${{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style=${{ flexShrink: 0, marginTop: "2px" }}><polyline points="20 6 9 17 4 12"></polyline></svg>
                  <p style=${{ fontSize: "12px", lineHeight: "1.45", color: "#166534", margin: 0 }}>
                    Measurements optimized for your profile. Adjust further if needed.
                  </p>
                </div>
              </div>
            `}

            ${!autoUpdatedFields.waist && recommended?.outOfRange && html`<div style=${{ marginTop: "12px", fontSize: "12px", opacity: 0.6 }}>Outside standard range. Seeded from estimated measurements.</div>`}
          `}
        </div>
        <div className="zane-fit-footer-glass" style=${ { padding: "14px 22px" } }>
          <div style=${ { gridTemplateColumns: wizStep === 0 ? "1fr" : "1fr 2.5fr", display: "grid", gap: "12px" } }>
            ${wizStep > 0 && html`<button type="button" className="zane-fit-back" onClick=${back}>Back</button>`}
            ${wizStep < 3 ? html`<button type="button" className="zane-fit-cta" onClick=${next}>NEXT</button>` : html`<button type="button" className="zane-fit-cta" onClick=${finishWizard}>Continue</button>`}
          </div>
        </div>
      `}
      ${screen === "measures" && html`
        <div style=${ { flex: 1, overflow: "auto", padding: "20px 22px" } }>
          <div style=${ { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" } }><div><div style=${ { fontSize: "24px", fontWeight: 700, fontFamily: "var(--zb-font-heading)" } }>Refinements</div><div style=${ { fontSize: "13px", opacity: 0.5, marginTop: "4px" } }>Based on ${displayCode}</div></div><${SegToggle} left=${ { label: "CM", value: "cm" } } right=${ { label: "IN", value: "in" } } value=${unit} onChange=${setUnit} /></div>
          <button type="button" onClick=${resetToStandard} style=${ { border: "1px solid rgba(0,0,0,.1)", background: "#fff", borderRadius: "16px", padding: "8px 14px", fontSize: "11px", fontWeight: 600, marginBottom: "16px", cursor: "pointer" } }>Reset</button>
          ${renderMeasurementRows()}
        </div>
        <div className="zane-fit-footer-glass" style=${ { padding: "14px 22px" } }>
          <p className="zb-ethical-statement" style=${{ marginTop: "0", marginBottom: "12px" }}>
            ZB Fit Intelligence™ uses biometric inputs to recommend size with precision and discretion. No data is stored. No profiles are sold. Fit is treated as a responsibility.
          </p>
          <div style=${ { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" } }><button type="button" className="zane-fit-back" onClick=${back}>Back</button><button type="button" className="zane-fit-cta" onClick=${addToCart}>Add to Bag</button></div>
          ${cartError && html`<div style=${{ marginTop: "10px", background: "#111", color: "#fff", padding: "10px 12px", borderRadius: "8px", fontSize: "12px", lineHeight: "1.35", fontWeight: 600 }}>${cartError}</div>`}
        </div>
      `}

      ${infoKey && ZB_MEASURE_DIAGRAMS[infoKey] && html`
        <div onClick=${() => setInfoKey(null)} style=${{ position: "fixed", inset: 0, zIndex: 2147483647, background: "rgba(10,10,10,0.5)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div onClick=${(e) => e.stopPropagation()} style=${{ background: "#F8F4EA", borderRadius: "14px", maxWidth: "340px", width: "100%", padding: "26px 26px 28px", position: "relative", boxShadow: "0 24px 64px rgba(0,0,0,0.18)" }}>
            <button type="button" onClick=${() => setInfoKey(null)} aria-label="Close" style=${{ position: "absolute", top: "12px", right: "14px", background: "none", border: "none", cursor: "pointer", fontSize: "20px", lineHeight: 1, color: "#726A57", padding: "4px 6px" }}>×</button>
            <div style=${{ display: "flex", justifyContent: "center", marginBottom: "10px" }} dangerouslySetInnerHTML=${{ __html: ZB_MEASURE_DIAGRAMS[infoKey].svg }}></div>
            <div style=${{ fontFamily: "var(--zb-font-heading)", fontSize: "20px", fontWeight: 600, color: "#0A0A0A", textAlign: "center", marginBottom: "8px" }}>${ZB_MEASURE_DIAGRAMS[infoKey].title}</div>
            <p style=${{ fontFamily: "var(--zb-font-body)", fontSize: "13px", lineHeight: 1.65, color: "#4B3621", textAlign: "center", margin: 0 }}>${ZB_MEASURE_DIAGRAMS[infoKey].desc}</p>
          </div>
        </div>
      `}
    </div>
  `;
};

// ============================================
// END OF PART 4 - Continue to Part 5
// ============================================
// ============================================
// ZANE BARLAS FIT ENGINE V2 - PART 5 OF 5
// Tabs, App Component, and Mount Function
// ============================================

const Tabs = ({ activeTab, setActiveTab }) => html`
  <div className="zane-fit-tabs">
    <button type="button" className=${`zane-fit-tab ${activeTab === "standard" ? "is-active" : ""}`} onClick=${() => setActiveTab("standard")}>Standard</button>
    <button type="button" className=${`zane-fit-tab ${activeTab === "custom" ? "is-active" : ""}`} onClick=${() => setActiveTab("custom")}>Made to Measure</button>
  </div>
`;

// ZB-PROFILE: only auto-apply the saved-profile recommendation once per page load
let zbProfileAutoSelected = false;

const App = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("standard");
  const [product, setProduct] = useState(null);
  const [showFindModal, setShowFindModal] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [selected, setSelected] = useState({ jacketSize: null, lengthKey: "Regular" });
  const [customSpecs, setCustomSpecs] = useState(null); // For passing ideal specs to Custom tab

  // Handler for switching to Custom tab with pre-filled specs
  const handleCustomize = (specs) => {
    setCustomSpecs(specs);
    setActiveTab("custom");
  };

  // Open the quick "Find My Size" estimator (tracked)
  const openFindMySize = () => { setShowFindModal(true); zbFitTrack("find_my_size_open", { product: product?.title || null }); };

  // Analytics: drawer opened
  useEffect(() => { if (isOpen) zbFitTrack("drawer_open", { product: product?.title || null }); }, [isOpen]);

  // Check for "reopen drawer" flag on load (after login redirect)
  useEffect(() => {
    const shouldReopen = sessionStorage.getItem("zane_reopen_drawer");
    if (shouldReopen === "true") {
      setIsOpen(true);
      setActiveTab("custom"); // Return them to Made-to-Measure tab
      sessionStorage.removeItem("zane_reopen_drawer");
    }
  }, []);

  // Listen for "Open Login" event
  useEffect(() => {
    const handleOpenLogin = () => setShowLogin(true);
    window.addEventListener("zane:open-login", handleOpenLogin);
    return () => window.removeEventListener("zane:open-login", handleOpenLogin);
  }, []);

  useEffect(() => {
    window.__setZaneSizeDrawerOpen = setIsOpen;
    if (window.__zbPendingOpen) { window.__zbPendingOpen = false; setIsOpen(true); }
    window.openZaneSizeDrawer = () => { if (typeof window.__setZaneSizeDrawerOpen === "function") window.__setZaneSizeDrawerOpen(true); else { window.__zbPendingOpen = true; document.dispatchEvent(new CustomEvent("zane:open-size-drawer")); } };
    const open = () => setIsOpen(true);
    const close = () => setIsOpen(false);
    document.addEventListener("zane:open-size-drawer", open);
    document.addEventListener("zane:close-size-drawer", close);
    return () => { document.removeEventListener("zane:open-size-drawer", open); document.removeEventListener("zane:close-size-drawer", close); };
  }, []);


  useEffect(() => { document.body.style.overflow = isOpen ? "hidden" : ""; }, [isOpen]);
  useEffect(() => { setProduct(getProductFromDom()); }, []);

  const category = getProductCategory(product);
  const isShirt = category === "shirt";

  // ZB-PROFILE: when a completed fit profile is saved (page or wizard), pre-select
  // the recommended standard size on drawer open. Additive; uses the engine's own
  // recommendFromBody, same as the Find My Size apply path.
  useEffect(() => {
    if (!isOpen || zbProfileAutoSelected) return;
    if (selected && selected.jacketSize != null) { zbProfileAutoSelected = true; return; }
    if (!category) return;
    let p = null;
    try { p = JSON.parse(localStorage.getItem("zane_mtm_profile") || "null"); } catch (e) {}
    if (!p || typeof p.heightCm !== "number" || typeof p.weightKg !== "number" || !p.bodyShape || !p.fitPref) return;
    try {
      const rec = recommendFromBody({ heightCm: p.heightCm, weightKg: p.weightKg, shoe: p.shoe, age: p.age }, { bodyShape: p.bodyShape, fitPref: p.fitPref, category });
      if (!rec || rec.outOfRange || rec.jacketSize == null) return;
      zbProfileAutoSelected = true;
      setSelected({ jacketSize: rec.jacketSize, lengthKey: rec.lengthKey });
      zbFitTrack("size_recommended", { source: "fit_profile", size: rec.jacketSize, length: rec.lengthKey, product: (product && product.title) || null });
    } catch (e) {}
  }, [isOpen, product, category]);

  const jacketCode = useMemo(() => { 
    if (!selected?.jacketSize || (!selected?.lengthKey && ["sb_suit", "db_suit", "sb_overcoat", "db_overcoat", "peacoat", "sb_jacket", "db_jacket", "shirt"].includes(category))) return ""; 
    return extractJacketCodeFromSelections({ jacketSize: selected.jacketSize, lengthKey: selected.lengthKey, category }); 
  }, [selected, category]);
  
  const trouserCode = useMemo(() => { 
    if (!selected?.jacketSize || !selected?.lengthKey) return ""; 
    const w = (selected.trouserWaist != null) ? selected.trouserWaist : (parseFloat(selected.jacketSize) - 6);
    const suffix = selected.lengthKey === "Short" ? "S" : selected.lengthKey === "Long" ? "L" : "R";
    return `${w}${suffix}`;
  }, [selected]);

  const addStandardToCart = async () => {
    try {
      if (!product) throw new Error("No product JSON found.");
      if (!selected?.jacketSize || (!selected?.lengthKey && ["sb_suit", "db_suit", "sb_overcoat", "db_overcoat", "peacoat", "sb_jacket", "db_jacket", "shirt"].includes(category))) return;
      const resolved = resolveVariant(product, { jacketSize: selected.jacketSize, lengthKey: selected.lengthKey });
      if (!resolved) { alert("Sorry, that size is not available for this piece. Please choose another size or length."); return; }
      const props = { "ZB Mode": "Standard" };
      // The requested size AND length (e.g. "40L"). The line's variant (resolved.id) is the
      // purchasable SKU, which may be the Regular size when a specific length isn't separately
      // stocked (made to order). Label the size by garment so a waistcoat or coat is not "Jacket Size".
      const sizeCode = jacketCode || resolved.label;
      const sizeLabel = isShirt
        ? "Collar Size"
        : category === "waistcoat"
          ? "Waistcoat Size"
          : (category === "sb_overcoat" || category === "db_overcoat" || category === "peacoat")
            ? "Coat Size"
            : "Jacket Size";
      props[sizeLabel] = sizeCode;
      if (!isShirt && isSuitOrTux(product)) {
        props["Trouser Size"] = trouserCode || trouserFromJacket(parseFloat(selected.jacketSize), getLengthKey(selected.lengthKey));
      }
      await addToShopifyCart(resolved.id, 1, props);
      zbFitTrack("add_to_cart", { mode: "standard", size: sizeCode, product: product?.title || null });
      // ZB-PROFILE: persist the chosen standard size so it carries to the account and the next order.
      // (Only references vars in App scope: selected + the built code.)
      try {
        if (window.ZBProfileStore) window.ZBProfileStore.save({
          _source: "standard", mode: "standard",
          standard: { jacketSize: selected.jacketSize, lengthKey: selected.lengthKey || null, sizeLabel: sizeLabel, jacketCode: (isShirt ? null : sizeCode), trouserCode: props["Trouser Size"] || null, collar: (isShirt ? sizeCode : null) }
        });
      } catch (e) {}
      setIsOpen(false);
    } catch (e) { console.error(e); alert("Could not add to cart."); }
  };

  const applyFindYourSize = (rec) => {
    setSelected({ jacketSize: rec.jacketSize, lengthKey: rec.lengthKey });
    setActiveTab("standard");
    zbFitTrack("size_recommended", { size: rec.jacketSize, length: rec.lengthKey, product: product?.title || null });
  };

  const addCustomToCart = async ({ recommended, properties }) => {
    if (!product) throw new Error("No product JSON found.");
    const resolved = resolveVariant(product, { jacketSize: recommended.jacketSize, lengthKey: recommended.lengthKey });
    if (!resolved) { alert("Sorry, that size is not available for this piece. Please choose another size or length."); return; }
    await addToShopifyCart(resolved.id, 1, properties || { "ZB Mode": "Custom Sizes", "Recommended Size": resolved.label });
    zbFitTrack("add_to_cart", { mode: "custom", size: resolved.label, product: product?.title || null });
    // Clear local draft after successful order (it will be saved in order history)
    localStorage.removeItem("zane_mtm_profile");
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return html`
    <div className="zane-fit-overlay is-open">
      <div className="zane-fit-backdrop" onClick=${() => setIsOpen(false)}></div>
      <div className="zane-fit-panel">
        <header className="zane-fit-header">
          <h2 className="zane-fit-title">Select Size & Fit</h2>
          <div style=${{ display: "flex", alignItems: "center", gap: "16px" }}>
            <span className="zb-hallmark">ZB FIT INTELLIGENCE™</span>
            <button type="button" className="zane-fit-close" onClick=${() => setIsOpen(false)} aria-label="Close">✕</button>
          </div>
        </header>
        <${Tabs} activeTab=${activeTab} setActiveTab=${setActiveTab} />
        <div className="zane-fit-body" style=${{ display: "flex", flexDirection: "column" }}>
          ${activeTab === "standard" && html`
            <div style=${ { flex: 1, overflow: "auto" } }>
              <${StandardTab} product=${product} selected=${{ ...selected, jacketCode, trouserCode }} setSelected=${setSelected} openSizeGuide=${() => setShowSizeGuide(true)} onClose=${() => setIsOpen(false)} onFindMySize=${openFindMySize} />
            </div>
            <div className="zane-fit-footer-glass" style=${ { padding: "14px 22px" } }>
              <div style=${ { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" } }>
                <button type="button" className="zane-fit-back" onClick=${openFindMySize}>Find My Size</button>
                <button type="button" className="zane-fit-cta" disabled=${!(selected?.jacketSize && selected?.lengthKey)} onClick=${addStandardToCart}>Add to Bag</button>
              </div>
            </div>
          `}
          ${activeTab === "custom" && html`<${CustomSizesTab} product=${product} onAddCustomToCart=${addCustomToCart} customSpecs=${customSpecs} />`}
        </div>
        <${FindYourSizeModal} open=${showFindModal} onClose=${() => setShowFindModal(false)} onApply=${applyFindYourSize} onCustomize=${handleCustomize} product=${product} />
        <${SizeGuideModal} open=${showSizeGuide} product=${product} onClose=${() => setShowSizeGuide(false)} />
        <${LoginModal} open=${showLogin} onClose=${() => setShowLogin(false)} />
      </div>
    </div>
  `;
};

// ============================================
// MOUNT FUNCTION
// ============================================
(function mount() {
  let rootElement = document.getElementById("zb-fit-root");
  if (!rootElement) { 
    rootElement = document.createElement("div"); 
    rootElement.id = "zb-fit-root"; 
    document.body.appendChild(rootElement); 
  }
  createRoot(rootElement).render(React.createElement(App));
  console.log("[ZB FIT V2] mounted with full measurement support");
})();

// ============================================
// ZB FIT PROFILE STORE (additive)
// Persists the customer's chosen sizing (standard size or made-to-measure
// measurements) to a durable local profile so it carries to their next order,
// and so the "Your Fit Profile" page can show and edit it. This stores values
// only. It does NOT change any sizing calculation.
// ============================================
(function () {
  var KEY = "zane_fit_profile";
  function nowISO() { try { return new Date().toISOString(); } catch (e) { return ""; } }
  window.ZBProfileStore = {
    read: function () { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch (e) { return null; } },
    write: function (p) {
      try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) {}
      try { document.dispatchEvent(new CustomEvent("zb:fit-profile-updated", { detail: p })); } catch (e) {}
    },
    // Merge a partial update and append a change-log entry for each changed measurement field.
    save: function (partial) {
      var prev = this.read() || { measurements: {}, log: [] };
      var next = Object.assign({}, prev, partial);
      next.measurements = Object.assign({}, prev.measurements || {}, partial.measurements || {});
      next.updatedAt = nowISO();
      var log = (prev.log || []).slice();
      if (partial.measurements) {
        Object.keys(partial.measurements).forEach(function (grp) {
          var nm = partial.measurements[grp] || {}, om = (prev.measurements && prev.measurements[grp]) || {};
          Object.keys(nm).forEach(function (f) {
            if (typeof nm[f] === "number" && nm[f] !== om[f]) {
              log.push({ at: next.updatedAt, field: grp + "." + f, from: (typeof om[f] === "number" ? om[f] : null), to: nm[f], source: partial._source || "engine" });
            }
          });
        });
      }
      next.log = log.slice(-50);
      delete next._source;
      this.write(next);
      return next;
    }
  };
})();

// ============================================
// END OF PART 5 - Complete Implementation
// ============================================

